/*
@license
  Be Yours by roartheme (https://roartheme.co)
  Access unminified JS in assets/global.js

  Use this event listener to run your own JS outside of this file.
  Documentation - https://roartheme.co/blogs/beyours/javascript-events-for-developers

  document.addEventListener('page:loaded', function() {
    // Page has loaded and theme assets are ready
  });
*/
window.theme = window.theme || {};

theme.config = {
  mqlSmall: false,
  mediaQuerySmall: 'screen and (max-width: 749px)',
  isTouch: ('ontouchstart' in window) || window.DocumentTouch && window.document instanceof DocumentTouch || window.navigator.maxTouchPoints || window.navigator.msMaxTouchPoints ? true : false,
  rtl: document.documentElement.getAttribute('dir') === 'rtl' ? true : false
};

if (console && console.log) {
  console.log(theme.settings.themeName + ' theme (' + theme.settings.themeVersion + ') by RoarTheme | Learn more at https://roartheme.co');
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

theme.Currency = (function() {
  const moneyFormat = '${{amount}}'; // eslint-disable-line camelcase

  function formatMoney(cents, format) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }
    let value = '';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format || moneyFormat;

    function formatWithDelimiters(number, precision, thousands, decimal) {
      thousands = thousands || ',';
      decimal = decimal || '.';

      if (isNaN(number) || number === null) {
        return 0;
      }

      number = (number / 100.0).toFixed(precision);

      const parts = number.split('.');
      const dollarsAmount = parts[0].replace(
        /(\d)(?=(\d\d\d)+(?!\d))/g,
        '$1' + thousands
      );
      const centsAmount = parts[1] ? decimal + parts[1] : '';

      return dollarsAmount + centsAmount;
    }

    switch (formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'");
        break;
    }

    return formatString.replace(placeholderRegex, value);
  }

  function getBaseUnit(variant) {
    if (!variant) {
      return;
    }

    if (!variant.unit_price_measurement || !variant.unit_price_measurement.reference_value) {
      return;
    }

    return variant.unit_price_measurement.reference_value === 1
      ? variant.unit_price_measurement.reference_unit
      : variant.unit_price_measurement.reference_value +
          variant.unit_price_measurement.reference_unit;
  }

  return {
    formatMoney: formatMoney,
    getBaseUnit: getBaseUnit
  };
})();

// Init section function when it's visible, then disable observer
theme.initWhenVisible = function(options) {
  const threshold = options.threshold ? options.threshold : 0;

  const observer = new IntersectionObserver((entries, observer) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        if (typeof options.callback === 'function') {
          options.callback();
          observer.unobserve(entry.target);
        }
      }
    });
  }, {rootMargin: `0px 0px ${threshold}px 0px`});

  observer.observe(options.element);
};

(function() {
  // Trigger events when going between breakpoints
  const mql = window.matchMedia(theme.config.mediaQuerySmall);
  theme.config.mqlSmall = mql.matches;
  mql.onchange = (e) => {
    if (e.matches) {
      theme.config.mqlSmall = true;
      document.dispatchEvent(new CustomEvent('matchSmall'));
    }
    else {
      theme.config.mqlSmall = false;
      document.dispatchEvent(new CustomEvent('unmatchSmall'));
    }
  }

  // Page transition
  document.addEventListener('click', (event) => {
    const anchor = event.target.closest('a');
    if (anchor?.href && /^(mailto:|tel:)/.test(anchor.href)) {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
    } else {
      window.addEventListener('beforeunload', beforeUnloadHandler);
    }
  });
  const beforeUnloadHandler = (event) => {
    document.body.classList.add('unloading');
  }
  window.addEventListener('DOMContentLoaded', () => {
    document.body.classList.add('loaded');

    document.dispatchEvent(new CustomEvent('page:loaded'));
  });
  window.addEventListener('pageshow', (event) => {
    // Removes unload class when returning to page via history
    if (event.persisted) {
      document.body.classList.remove('unloading');
    }
  });

  // Viewport height
  function viewportHeight() {
    if (window.matchMedia('(max-width: 990px)')) {
      document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    }

    setHeaderBottomPosition();
  }
  window.addEventListener('resize', viewportHeight);
  window.addEventListener('DOMContentLoaded', viewportHeight);

  if (document.body.getAttribute('data-page-rendering') !== null) {
    // Improve initial load time by skipping the rendering of offscreen content
    const observer = new IntersectionObserver(
      (entries, _observer) => {
        entries.forEach((entry) => {
          const el = entry.target;
          // Not currently in intersection area.
          if (entry.intersectionRatio == 0) {
            return;
          }
          // Trigger rendering for elements within scroll area that haven't already been marked.
          if (!el.markedVisible) {
            el.attributeStyleMap && el.attributeStyleMap.set('content-visibility', 'visible');
            el.markedVisible = true;
          }
        });
      },
      // Set a rendering 50px above and 100px below the main scroll area.
      { rootMargin: '50px 0px 100px 0px' }
    );
    const sections = document.querySelectorAll('.shopify-section + .shopify-section');
    sections.forEach((section) => { observer.observe(section); });
  }

  /*
  // Delay JavaScript until user interaction
  const loadScriptsTimer = setTimeout(loadScripts, 5000);
  const userInteractionEvents = ["mouseover", "mousemove", "keydown", "touchstart", "touchend", "touchmove", "wheel"];
  userInteractionEvents.forEach(function(event) {
    window.addEventListener(event, triggerScriptLoader, {
      passive: !0
    });
  });
  function triggerScriptLoader() {
    loadScripts();
    clearTimeout(loadScriptsTimer);
    userInteractionEvents.forEach(function(event) {
      window.removeEventListener(event, triggerScriptLoader, {
        passive: !0
      });
    });
  }
  function loadScripts() {
    document.querySelectorAll("script[data-type='lazy']").forEach(function(script) {
      script.setAttribute("src", script.getAttribute("data-src"));
    });
  }
  */
})();

// Prevent vertical scroll while using flickity sliders
(function() {
  var e = !1;
  var t;

  document.body.addEventListener('touchstart', function(i) {
    if (!i.target.closest('.flickity-slider')) {
      return e = !1;
    }
    e = !0;
    t = {
      x: i.touches[0].pageX,
      y: i.touches[0].pageY
    }
  })

  document.body.addEventListener('touchmove', function(i) {
    if (e && i.cancelable) {
      var n = {
        x: i.touches[0].pageX - t.x,
        y: i.touches[0].pageY - t.y
      };
      if (Math.abs(n.x) > Math.abs(n.y) && Math.abs(n.x) > Flickity.defaults.dragThreshold) {
        i.preventDefault();
      }
    }
  }, { passive: !1 })
})();

function isStorageSupported (type) {
  // Return false if we are in an iframe without access to sessionStorage
  if (window.self !== window.top) {
    return false;
  }

  const testKey = 'beyours:test';
  let storage;
  if (type === 'session') {
    storage = window.sessionStorage;
  }
  if (type === 'local') {
    storage = window.localStorage;
  }

  try {
    storage.setItem(testKey, '1');
    storage.removeItem(testKey);
    return true;
  }
  catch (error) {
    // Do nothing, this may happen in Safari in incognito mode
    return false;
  }
}

function checkScrollbar() {
  const rect = document.body.getBoundingClientRect();
  return Math.round(rect.left + rect.right) < window.innerWidth;
}

function setScrollbarWidth() {
  if (checkScrollbar()) {
    const element = document.createElement('span');
    element.className = 'modal-scrollbar-measure', document.body.appendChild(element);
  
    const width = element.getBoundingClientRect().width - element.clientWidth;
    document.body.removeChild(element);
    document.documentElement.style.setProperty('--scrollbar-width', `${width}px`);
  }
}

function setHeaderBottomPosition() {
  const header = document.querySelector('.shopify-section-header');
  if (header) {
    document.documentElement.style.setProperty('--header-bottom-position', `${parseInt(header.getBoundingClientRect().bottom)}px`);
  }
}

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function() {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function(event) {
    const charCode = event.code ? event.code.toUpperCase() : event.key.toUpperCase();
    if (charCode !== 'TAB') return; // If not TAB key
    
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  if (elementToFocus) elementToFocus.focus();

  if (elementToFocus.tagName === 'INPUT' && ['search', 'text', 'email', 'url'].includes(elementToFocus.type) && elementToFocus.value) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(":focus-visible");
} catch {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = ['ARROWUP', 'ARROWDOWN', 'ARROWLEFT', 'ARROWRIGHT', 'TAB', 'ENTER', 'SPACE', 'ESCAPE', 'HOME', 'END', 'PAGEUP', 'PAGEDOWN']
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if(navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener('focus', () => {
    if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

    if (mouseClick) return;

    currentFocusedElement = document.activeElement;
    currentFocusedElement.classList.add('focused');

  }, true);
}

function pauseAllMedia() {
  document.querySelectorAll('iframe.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('iframe.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video-section[data-type="youtube"]:not([data-video-hero]) iframe').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('video-section[data-type="vimeo"]:not([data-video-hero]) iframe').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => {
    if (!video.closest('[data-video-hero]')) video.pause();
  });
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  const charCode = event.code ? event.code.toUpperCase() : event.key.toUpperCase();
  if (charCode !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

function filterShopifyEvent(event, domElement, callback) {
  let executeCallback = false;
  if (event.type.includes('shopify:section')) {
    if (domElement.hasAttribute('data-section-id') && domElement.getAttribute('data-section-id') === event.detail.sectionId) {
      executeCallback = true;
    }
  }
  else if (event.type.includes('shopify:block') && event.target === domElement) {
    executeCallback = true;
  }
  if (executeCallback) {
    callback(event);
  }
}

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function fetchConfig(type = 'json', method = 'POST') {
  return {
    method: method,
    headers: { 'Content-Type': 'application/json', 'Accept': `application/${type}` }
  };
}

const serializeForm = form => {
  const obj = {};
  const formData = new FormData(form);

  for (const key of formData.keys()) {
    const regex = /(?:^(properties\[))(.*?)(?:\]$)/;

    if (regex.test(key)) { 
      obj.properties = obj.properties || {};
      obj.properties[regex.exec(key)[2]] = formData.get(key);
    } else {
      obj[key] = formData.get(key);
    }
  }

  return JSON.stringify(obj);
};

/*
 * Shopify Common JS
 *
 */
if ((typeof window.Shopify) == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function(fn, scope) {
  return function() {
    return fn.apply(scope, arguments);
  }
};

Shopify.setSelectorByValue = function(selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function(target, eventName, callback) {
  target.addEventListener ? target.addEventListener(eventName, callback, false) : target.attachEvent('on'+eventName, callback);
};

Shopify.postLink = function(path, options) {
  options = options || {};
  const method = options['method'] || 'post';
  const params = options['parameters'] || {};

  const form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for (const key in params) {
    const hiddenField = document.createElement("input");
    hiddenField.setAttribute("type", "hidden");
    hiddenField.setAttribute("name", key);
    hiddenField.setAttribute("value", params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.postLink2 = function(path, options) {
  options = options || {};
  const method = options['method'] || 'post';
  const params = options['parameters'] || {};

  const form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for (const key in params) {
    for (const index in params[key]) {
      for (const key2 in params[key][index]) {
        const hiddenField = document.createElement("input");
        hiddenField.setAttribute("type", "hidden");
        hiddenField.setAttribute("name", `${key}[${index}][${key2}]`);
        hiddenField.setAttribute("value", params[key][index][key2]);
        form.appendChild(hiddenField);
      }
    }
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function(country_domid, province_domid, options) {
  this.countryEl         = document.getElementById(country_domid);
  this.provinceEl        = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler,this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function() {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function() {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function(e) {
    var opt       = this.countryEl.options[this.countryEl.selectedIndex];
    var raw       = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = "";
    }
  },

  clearOptions: function(selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function(selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  }
};

class QuantityInput extends HTMLElement {
  constructor() {
    super();

    this.init();
  }

  init() {
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach(
      (button) => button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus?.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus?.classList.toggle('disabled', value >= max);
    }
  }
}
customElements.define('quantity-input', QuantityInput);

class DrawerCloseButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', () => this.dispatchEvent(new CustomEvent('drawer:force-close', { bubbles: true, cancelable: true, composed: true })));
  }
}
customElements.define('drawer-close-button', DrawerCloseButton);

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.classes = {
      open: 'menu-drawer--open',
      opening: 'menu-drawer--opening',
      closing: 'menu-drawer--closing'
    };

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  setClasses(classes) {
    this.classes = classes;
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach(summary => summary.addEventListener('click', this.onSummaryClick.bind(this)));
    this.querySelectorAll('button[data-close]').forEach(button => button.addEventListener('click', this.onCloseButtonClick.bind(this)));

    this.addEventListener('drawer:force-close', (event) => {
      event.stopPropagation();
      this.closeMenuDrawer(event);
    });
  }

  onKeyUp(event) {
    const charCode = event.code ? event.code.toUpperCase() : event.key.toUpperCase();
  if (charCode !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary')) : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        !reducedMotion || reducedMotion.matches ? addTrapFocus() : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement = false) {
    setScrollbarWidth();
    setHeaderBottomPosition();

    if (summaryElement) {
      summaryElement.setAttribute('aria-expanded', true);
      trapFocus(this.mainDetailsToggle, summaryElement);
    }

    setTimeout(() => {
      this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
      document.body.addEventListener('click', this.onBodyClickEvent);
    });
    document.body.classList.add(this.classes.opening);

    this.openAnimation();
    setTimeout(() => {
      this.mainDetailsToggle.setAttribute('open', '');
      setTimeout(() => {
        this.mainDetailsToggle.classList.add('menu-opening');
      }, 10);
    });
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event !== undefined) {
      this.mainDetailsToggle.querySelectorAll('details').forEach(details =>  {
        details.removeAttribute('open');
        details.classList.remove('menu-opening');
      });
      this.mainDetailsToggle.classList.remove('menu-opening');
      this.mainDetailsToggle.classList.add('menu-closing');
      removeTrapFocus(elementToFocus);
      
      document.body.removeEventListener('click', this.onBodyClickEvent);
      document.body.classList.remove(this.classes.open);
      document.body.classList.add(this.classes.closing);
      
      this.closeAnimation(this.mainDetailsToggle);
      this.dispatchEvent(new CustomEvent('close'));
      document.dispatchEvent(new CustomEvent('menudrawer:close'));
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement)) this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const target = event.currentTarget;
    if (target.classList.contains('noclose')) return;
    
    const detailsElement = target.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus();
    this.closeAnimation(detailsElement);
  }

  openAnimation() {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      }
      else {
        document.body.classList.remove(this.classes.opening);
        document.body.classList.add(this.classes.open);
      }
    }

    window.requestAnimationFrame(handleAnimation);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      }
      else {
        if (detailsElement === this.mainDetailsToggle) {
          document.body.classList.remove(this.classes.closing);
          detailsElement.classList.remove('menu-closing');
        }
        detailsElement.removeAttribute('open');

        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    }

    window.requestAnimationFrame(handleAnimation);
  }

  onBodyClick(event) {
    if (!this.contains(event.target)) this.closeMenuDrawer(event);
  }
}
customElements.define('menu-drawer', MenuDrawer);

class CartDrawer extends MenuDrawer {
  constructor() {
    super();

    this.setClasses({
      open: 'mini-cart--open',
      opening: 'mini-cart--opening',
      closing: 'mini-cart--closing'
    });

    this.onCartRefreshListener = this.onCartRefresh.bind(this);
  }

  connectedCallback() {
    document.addEventListener('cart:refresh', this.onCartRefreshListener);
  }
  
  async onCartRefresh(event) {
    const miniCartElement = document.getElementById('mini-cart');
    if (!miniCartElement) return;

    const miniCartSectionId = 'shopify-section-mini-cart';
    const headerSectionId = this.getAttribute('data-header-section-id');
    const cartIconBubbleId = 'cart-icon-bubble';
    const rootUrl = theme.routes.root_url;

    try {
      // Fetch mini cart and header sections in parallel
      const [miniCartResponse, headerResponse] = await Promise.all([
        fetch(`${rootUrl}?section_id=mini-cart`),
        fetch(`${rootUrl}?section_id=${headerSectionId}`)
      ]);

      if (!miniCartResponse.ok || !headerResponse.ok) {
        throw new Error('Failed to fetch cart sections');
      }

      const [miniCartText, headerText] = await Promise.all([
        miniCartResponse.text(),
        headerResponse.text()
      ]);

      const parser = new DOMParser();
      const miniCartParsedHTML = parser.parseFromString(miniCartText, 'text/html');
      const headerParsedHTML = parser.parseFromString(headerText, 'text/html');

      const miniCartSection = miniCartParsedHTML.getElementById(miniCartSectionId);
      const cartIconBubble = headerParsedHTML.getElementById(cartIconBubbleId);

      if (miniCartSection) {
        miniCartElement.innerHTML = miniCartSection.innerHTML;
      }

      const cartIconBubbleElement = document.getElementById(cartIconBubbleId);
      if (cartIconBubbleElement && cartIconBubble) {
        cartIconBubbleElement.innerHTML = cartIconBubble.innerHTML;
      }

      if (event?.detail?.open === true) {
        this.openMenuDrawer();
      }
    } catch (error) {
      console.error('Error refreshing cart:', error);
    }
  }
}
customElements.define('cart-drawer', CartDrawer);

class FacetDrawer extends MenuDrawer {
  constructor() {
    super();

    if (this.dataset.desktop == 'true') {
      this.setClasses({
        open: 'facet-horizontal--open',
        opening: 'facet-horizontal--opening',
        closing: 'facet-horizontal--closing'
      });
    }
    else {
      this.setClasses({
        open: 'facet-drawer--open',
        opening: 'facet-drawer--opening',
        closing: 'facet-drawer--closing'
      });
    }
  }
}
customElements.define('facet-drawer', FacetDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();

    this.setClasses({
      open: 'menu-mobile--open',
      opening: 'menu-mobile--opening',
      closing: 'menu-mobile--closing'
    });

    this.setupScrollHint();
  }

  // Shows a fade at the bottom of the menu list while more items sit below
  // the fold (the nav is the scroller — promo/footer stay pinned under it),
  // and hides it once the list is scrolled to its end.
  setupScrollHint() {
    this.drawerElement = this.querySelector('.menu-drawer');
    this.scrollContainer = this.querySelector('.menu-drawer__navigation');
    if (!this.drawerElement || !this.scrollContainer) return;

    let ticking = false;
    this.updateScrollHint = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const el = this.scrollContainer;
        const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
        this.drawerElement.classList.toggle('menu-drawer--can-scroll', remaining > 24);
        ticking = false;
      });
    };

    this.scrollContainer.addEventListener('scroll', this.updateScrollHint, { passive: true });
    // Content height settles after the open animation and image loads;
    // observe the children too — the container itself is height-locked
    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(this.updateScrollHint);
      observer.observe(this.scrollContainer);
      Array.from(this.scrollContainer.children).forEach((child) => observer.observe(child));
    }
  }

  openMenuDrawer(summaryElement = false) {
    super.openMenuDrawer(summaryElement);
    if (this.updateScrollHint) setTimeout(this.updateScrollHint, 100);
  }
}
customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    
    this.querySelector('[id^="ModalClose-"]').addEventListener(
      'click',
      this.hide.bind(this)
    );
    this.addEventListener('keyup', (event) => {
      const charCode = event.code ? event.code.toUpperCase() : event.key.toUpperCase();
      if (charCode === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target.nodeName === 'MODAL-DIALOG') this.hide();
      });
    }
  }

  show(opener) {
    setScrollbarWidth();
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    this.button = this.querySelector('button');
    if (!this.button) return;

    this.button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(this.button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    
    this.poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!this.poster) return;

    this.poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      if (content.querySelector('video-section')) {
        const deferredElement = this.appendChild(content);
        if (focus) deferredElement.focus();
        if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
          // force autoplay for safari
          deferredElement.play();
        }
      } else {
        this.setAttribute('loaded', true);
        const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
        if (focus) deferredElement.focus();
        if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
          // force autoplay for safari
          deferredElement.play();
        }
      }
    }
  }
}
customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();

    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');

    // A section can place its controls outside the slider's own element, in the
    // heading row. data-controls names the container that holds them, so a
    // section carrying several sliders never binds one to another's buttons.
    this.controls = (this.dataset.controls && document.getElementById(this.dataset.controls)) || this;
    this.prevButton = this.controls.querySelector('button[name="previous"]');
    this.nextButton = this.controls.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    theme.initWhenVisible({
      element: this,
      callback: this.init.bind(this),
      threshold: 200
    });
  }

  init() {
    const resizeObserver = new ResizeObserver(() => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(element => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(this.slider.clientWidth / this.sliderItemOffset);
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(new CustomEvent('slideChanged', { detail: {
        currentPage: this.currentPage,
        currentElement: this.sliderItemsToShow[this.currentPage - 1]
      }}));
    }

    // The hiding rule reads from an ancestor of the buttons, so the class has to
    // land wherever they actually live.
    const fitsOnOnePage = this.currentPage === 1 && this.currentPage === this.totalPages;
    this.classList.toggle('slider--no-buttons', fitsOnOnePage);
    if (this.controls !== this) {
      this.controls.classList.toggle('slider--no-buttons', fitsOnOnePage);
    }

    if (this.enableSliderLooping) return;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (isFirstSlide || this.isSlideVisible(this.sliderItemsToShow[0])) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (isLastSlide || this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return (element.offsetLeft + element.clientWidth) <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();

    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition = event.currentTarget.name === 'next' ? this.slider.scrollLeft + (step * this.sliderItemOffset) : this.slider.scrollLeft - (step * this.sliderItemOffset);
    //this.slideScrollPosition = theme.config.rtl ? this.slideScrollPosition * -1 : this.slideScrollPosition;
    this.slider.scrollTo({
      left: this.slideScrollPosition
    });
  }
}
customElements.define('slider-component', SliderComponent);

class ThumbnailSlider extends SliderComponent {
  constructor() {
    super();
  }

  initPages() {
    if (!this.hasAttribute('data-static-thumbnails')) return super.initPages();
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(element => element.clientWidth > 0);
    this.currentPage = 1;
    this.totalPages = 1;
    this.slidesPerPage = this.sliderItemsToShow.length;
    this.classList.add('slider--no-buttons');
    this.prevButton?.setAttribute('disabled', 'disabled');
    this.nextButton?.setAttribute('disabled', 'disabled');
  }

  update() {
    if (!this.hasAttribute('data-static-thumbnails')) return super.update();
  }

  isSlideVisible(element, offset = 0) {
    if (this.hasAttribute('data-static-thumbnails')) return true;
    return super.isSlideVisible(element, offset);
  }

  init() {
    super.init();

    const variantItems = this.querySelectorAll('.thumbnail-list_item--variant:not(.is-active)');
    this.slider.dataset.mediaCount = this.sliderItems.length - variantItems.length;

    if(this.querySelector('[data-gang-option]')) {
      const inactiveGangItems = this.querySelectorAll('[data-gang-option]:not(.gang__active)');
      this.slider.dataset.mediaCount = this.sliderItems.length - inactiveGangItems.length;
    }

    if (this.slider.dataset.mediaCount < 2) this.classList.add('hidden');
  }
}
customElements.define('thumbnail-slider', ThumbnailSlider);

class QuoteSlider extends SliderComponent {
  constructor() {
    super();
  }

  update() {
    super.update();

    if (this.currentPageElement && this.pageTotalElement) {
      this.sliderItems.forEach((element) => {
        element.removeAttribute('aria-current');
      });

      const currentElement = this.sliderItemsToShow[this.currentPage - 1];
      currentElement.setAttribute('aria-current', true);
    }
  }
}
customElements.define('quote-slider', QuoteSlider);

class ProductGallery extends SliderComponent {
  constructor() {
    super();
  }

  init() {
    super.init();

    const gallerySliderButtons = this.querySelector('.slider-buttons');
    if (gallerySliderButtons && this.querySelector('[data-gang-option]')) {
      if (this.querySelectorAll('.product__media-item.gang__active').length < 2) {
        gallerySliderButtons.classList.add('hidden');
      } else {
        gallerySliderButtons.classList.remove('hidden');
      }
    }

    this.cursor = this.querySelector('.gallery-cursor');
    if (!this.cursor) return;

    const images = this.querySelectorAll('.product__modal-opener--image');
    images.forEach((image) => image.addEventListener('mousemove', this.onMoveHandler.bind(this)));
    images.forEach((image) => image.addEventListener('mouseenter', this.onEnterHandler.bind(this)));
    images.forEach((image) => image.addEventListener('mouseleave', this.onLeaveHandler.bind(this)));
  }

  onMoveHandler(event) {
    this.cursor.style.left = `${event.clientX - 32}px`;
    this.cursor.style.top = `${event.clientY - 32}px`;
  }

  onEnterHandler() {
    this.cursor.classList.add('show');
  }

  onLeaveHandler() {
    this.cursor.classList.remove('show');
  }

  update() {
    super.update();

    if (this.currentPageElement && this.pageTotalElement) {
      const currentElement = this.sliderItemsToShow[this.currentPage - 1];
      if (currentElement) {
        this.slider.style.setProperty('--force-image-ratio-percent', currentElement.querySelector('.media').style.getPropertyValue('--image-ratio-percent'));
      }
    }
  }

  goToFirstSlide() {
    this.slider.scrollTo({
      left: 0
    });
  }
}
customElements.define('product-gallery', ProductGallery);
class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProgressBar extends HTMLElement {
  constructor() {
    super();

    theme.initWhenVisible({
      element: this,
      callback: this.init.bind(this),
      threshold: 0
    });
  }

  init() {
    setTimeout(() => {
      const quantity = parseInt(this.dataset.quantity);
      this.style.setProperty('--progress-bar-width', `${quantity * 100 / 30}%`);
    }, 1e3);
  }
}
customElements.define('progress-bar', ProgressBar);

class FormState extends HTMLElement {
  constructor() {
    super();

    this.formInputs = this.querySelectorAll('input,select,textarea');
    this.form = this.querySelector('form');

    this.formInputs.forEach((input) => {
      input.addEventListener('input', this.onInputChange.bind(this));
      input.addEventListener('blur', this.onInputChange.bind(this));
    });

    if (this.form) this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
  }

  onInputChange(event) {
    this.handleInputCheck(event.target);
  }

  onSubmitHandler(event) {
    let valid = !0;

    this.formInputs.forEach((input) => {
      if (!this.handleInputCheck(input)) {
        valid = !1;
      }
    });

    if (!valid) {
      event.preventDefault();
      return;
    }
  }

  handleInputCheck(input) {
    if (input.classList.contains('required')) {

      if (input.value.length === 0 || input.value === input.dataset.empty) {
        input.classList.remove('valid');
        input.classList.add('invalid');
        
        return !1;
      }
      else {
        input.classList.remove('invalid');
        input.classList.add('valid');

        return !0;
      }
    }

    return !0;
  }
}
customElements.define('form-state', FormState);

class ListMenuItem extends HTMLElement {
  constructor() {
    super();

    this.listMenuHover = this.closest('menu-drawer').querySelector('list-menu-hover');

    this.addEventListener('mouseenter', () => {
      this.listMenuHover.open({
        title: this.dataset.title,
        url: this.dataset.url,
        image: this.dataset.image,
        description: this.dataset.description
      });
    });
  }
}
customElements.define('list-menu-item', ListMenuItem);

class ListMenuHover extends HTMLElement {
  constructor() {
    super();
  }

  open(obj) {
    let html = '';
    const opened = obj.image.length > 0 || obj.description.length > 0;

    if (opened) {
      html += `<a href="${obj.url}" class="hover-collection${obj.image.length > 0 ? '' : ' hover-collection--no-image'}">`;
        html += '<figure class="hover-collection__image"' + (obj.image.length > 0 ? `style="background-image:url(${obj.image})"` : '') + '></figure>';
        html += '<div class="hover-collection__content">'
          html += `<span class="hover-collection__title h4">${obj.title}</span>`;
          html += obj.description.length > 0 ? `<div class="hover-collection__description">${obj.description}</div>` : '';
        html += '</div>';
      html += '</a>';
    }

    this.innerHTML = html;
  }

  close() {
    this.innerHTML = '';
  }
}
customElements.define('list-menu-hover', ListMenuHover);

class TransparentHeader extends HTMLElement {
  constructor() {
    super();

    const header = document.querySelector('.shopify-section-header');

    header.classList.add('transparent-header');
    if (this.dataset.showSeparator == 'true') {
      header.classList.add('transparent-separator');
    }
  }
}
customElements.define('transparent-header', TransparentHeader);

class AddToCart extends HTMLElement {
  constructor() {
    super();

    this.miniCart = document.querySelector('mini-cart');
    this.addEventListener('click', this.onClickHandler.bind(this));
  }

  onClickHandler() {
    const variantId = this.dataset.variantId;

    if (variantId) {
      if (document.body.classList.contains('template-cart') || !theme.shopSettings.cartDrawer) {
        Shopify.postLink(theme.routes.cart_add_url, {
          parameters: {
            id: variantId,
            quantity: 1
          },
        });
        return;
      }

      this.setAttribute('disabled', true);
      this.classList.add('loading');
      const sections = this.miniCart ? this.miniCart.getSectionsToRender().map((section) => section.id) : [];

      const body = JSON.stringify({
        id: variantId,
        quantity: 1,
        sections: sections,
        sections_url: window.location.pathname
      });

      fetch(`${theme.routes.cart_add_url}`, { ...fetchConfig('javascript'), body })
        .then((response) => response.json())
        .then((parsedState) => {
          if (parsedState.status === 422) {
             document.dispatchEvent(new CustomEvent('ajaxProduct:error', {
                detail: {
                  errorMessage: parsedState.description
                }
              }));
           }
           else {
            this.miniCart && this.miniCart.renderContents(parsedState);

             document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
              detail: {
                product: parsedState
              }
            }));
          }
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this.classList.remove('loading');
          this.removeAttribute('disabled');
        });
    }
  }
}
customElements.define('add-to-cart', AddToCart);

class PriceMoney extends HTMLElement {
  constructor() {
    super();

    if (this.shouldInit()) {
      this.init();
    }
  }

  shouldInit() {
    if (document.body.dataset.priceSuperscript === undefined) {
      return false;
    }  

    const moneyFormat = theme.shopSettings.moneyFormat.toLowerCase();
    if (moneyFormat.indexOf('class=') !== -1) {
      return false;
    }
    
    return true;
  }

  init() {
    const currencies_using_comma_decimals = 'ANG,ARS,BRL,BYN,BYR,CLF,CLP,COP,CRC,CZK,DKK,EUR,HRK,HUF,IDR,ISK,MZN,NOK,PLN,RON,RUB,SEK,TRY,UYU,VES,VND'.split(',');
    const bdi = this.querySelector('bdi');
    if (!bdi) return;

    // The text already carries the shop's own currency format, so this
    // only decorates it in place; splitting and reassembling the parts
    // would pin the currency code to the end and override formats that
    // put it elsewhere, such as "USD ${{amount}}".
    const iso = theme.shopSettings.isoCode;
    const text = bdi.textContent.trim();
    const separator = currencies_using_comma_decimals.includes(iso) ? ',' : '.';
    const decimalRegex = new RegExp((separator === ',' ? ',' : '\\.') + '\\d+(?=\\D|$)');

    // The ISO code inside a run stays plain text; only the symbol takes
    // the smaller prefix face.
    const wrapSymbol = (run) => {
      const symbol = run.replace(iso, '').trim();
      if (symbol === '') return run;
      return run.replace(symbol, `<span class="price__prefix">${symbol}</span>`);
    };

    let html = text.replace(decimalRegex, (decimal) => `<sup class="price__suffix">${decimal}</sup>`);

    const lead = text.match(/^\D+?(?=\d)/);
    if (lead) {
      const hasLeadSymbol = lead[0].replace(iso, '').trim() !== '';
      html = (hasLeadSymbol ? wrapSymbol(lead[0].replace(/ $/, '')) : lead[0]) + html.slice(lead[0].length);
    }

    const tail = text.match(/\d(\D+)$/);
    if (tail && html.endsWith(tail[1])) {
      // A code-only tail keeps its own spacing; a symbol drops the gap
      // because the prefix face brings its own margins.
      const hasSymbol = tail[1].replace(iso, '').trim() !== '';
      html = html.slice(0, -tail[1].length) + (hasSymbol ? wrapSymbol(tail[1].replace(/^ /, '')) : tail[1]);
    }

    bdi.innerHTML = html;
  }
}
customElements.define('price-money', PriceMoney);

// Background videos: iOS Low Power Mode blocks even muted autoplay. When play()
// is rejected, retry once on the first user gesture (which the autoplay policy
// allows). No UI — the video's poster stays visible until it resumes.
function playBackgroundVideoOnFirstGesture(video) {
  if (!video || video.dataset.gestureRetry === '1') return;
  video.dataset.gestureRetry = '1';
  const events = ['touchstart', 'pointerdown', 'click'];
  const resume = () => {
    events.forEach((evt) => document.removeEventListener(evt, resume));
    video.play().catch(() => {});
  };
  events.forEach((evt) => document.addEventListener(evt, resume, { passive: true }));
}

class SlideshowComponent extends HTMLElement {
  constructor() {
    super();

    this.elements = {
      small: this.querySelector('.slideshow__left'),
      content: this.querySelector('.slideshow__content'),
      large: this.querySelector('.slideshow__right')
    }
    this.settings = {
      autorotate: this.dataset.autorotate == 'true',
      autorotateSpeed: parseInt(this.dataset.autorotateSpeed),
      slidesSmall: this.elements.small.querySelectorAll('.slideshow__image'),
      slidesLarge: this.elements.large.querySelectorAll('.slideshow__image')
    };

    theme.initWhenVisible({
      element: this,
      callback: this.init.bind(this),
      threshold: 600
    });
    this.matchMedia();
  }

  init() {
    setTimeout(() => {
      this.flickitySmall = new Flickity(this.elements.small, {
        accessibility: false,
        rightToLeft: theme.config.rtl,
        prevNextButtons: false,
        pageDots: false,
        wrapAround: true,
        draggable: false,
        setGallerySize: false,
        autoPlay: this.settings.autorotate ? this.settings.autorotateSpeed : false,
        dragThreshold: 20
      });

      this.flickityContent = new Flickity(this.elements.content, {
        accessibility: false,
        rightToLeft: theme.config.rtl,
        prevNextButtons: true,
        pageDots: true,
        wrapAround: true,
        draggable: theme.config.mqlSmall ? true : false,
        asNavFor: this.elements.small,
        adaptiveHeight: true,
        arrowShape: 'M29.043 19.756l-28.125 28.125c-0.605 0.606-0.918 1.406-0.918 2.031s0.305 1.599 0.916 2.209l28.125 28.125c1.221 1.221 3.199 1.221 4.418 0s1.221-3.199 0-4.418l-22.793-22.793h86.209c1.727 0 3.125-1.398 3.125-2.949 0-1.727-1.398-3.125-3.125-3.125h-86.211l22.793-22.793c1.221-1.221 1.221-3.199 0-4.418s-3.203-1.217-4.414 0.006z',
        on: {
          ready: () => {
            const prevNextButtons = this.querySelectorAll('.flickity-button');
            if (prevNextButtons) {
              prevNextButtons.forEach((button) => {
                button.setAttribute('tabindex', '-1');
              });
            }
          }
        },
        dragThreshold: 20
      });

      this.flickityLarge = new Flickity(this.elements.large, {
        accessibility: false,
        rightToLeft: theme.config.rtl,
        prevNextButtons: false,
        pageDots: false,
        wrapAround: true,
        draggable: true,
        setGallerySize: false,
        asNavFor: this.elements.content,
        dragThreshold: 20
      });

      this.bindEvents();
    });
  }

  bindEvents() {
    this.flickitySmall.on('scroll', () => {
      this.flickitySmall.slides.forEach((slide, i) => {
        const flickity = this.flickitySmall;
        const image = this.settings.slidesSmall[i];
        let x = 0;

        if (image) {
          if( 0 === i ) {
            x = Math.abs( flickity.x ) > flickity.slidesWidth ? ( flickity.slidesWidth + flickity.x + flickity.slides[flickity.slides.length - 1 ].outerWidth + slide.target ) : ( slide.target + flickity.x );
          }
          else if( i === flickity.slides.length - 1 ) {
            x = Math.abs( flickity.x ) + flickity.slides[i].outerWidth < flickity.slidesWidth ? ( slide.target - flickity.slidesWidth + flickity.x - flickity.slides[i].outerWidth ) : ( slide.target + flickity.x );
          }
          else {
            x = slide.target + flickity.x;
          }
          
          if (!theme.config.isTouch && !theme.config.rtl) {
            image.style.transform = 'translateX(' + x * ( -1 / 2 ) + 'px)';
          }
        }
      });
    });

    this.flickityLarge.on('scroll', () => {
      this.flickityLarge.slides.forEach((slide, i) => {
        const flickity = this.flickityLarge;
        const image = this.settings.slidesLarge[i];
        let x = 0;

        if (image) {
          if( 0 === i ) {
            x = Math.abs( flickity.x ) > flickity.slidesWidth ? ( flickity.slidesWidth + flickity.x + flickity.slides[flickity.slides.length - 1 ].outerWidth + slide.target ) : ( slide.target + flickity.x );
          }
          else if( i === flickity.slides.length - 1 ) {
            x = Math.abs( flickity.x ) + flickity.slides[i].outerWidth < flickity.slidesWidth ? ( slide.target - flickity.slidesWidth + flickity.x - flickity.slides[i].outerWidth ) : ( slide.target + flickity.x );
          }
          else {
            x = slide.target + flickity.x;
          }

          if (!theme.config.isTouch && !theme.config.rtl) {
            image.style.transform = 'translateX(' + x * ( -1 / 2 ) + 'px)';
          }
        }
      });
    });

    this.elements.content.querySelector('.flickity-button.next').addEventListener('click', () => {
      this.flickitySmall && this.flickitySmall.next();
      this.stopPlayer();
    });

    this.elements.content.querySelector('.flickity-button.previous').addEventListener('click', () => {
      this.flickitySmall && this.flickitySmall.previous();
      this.stopPlayer();
    });

    this.elements.content.querySelectorAll('.flickity-page-dot').forEach((button) => {
      button.addEventListener('click', () => {
        this.stopPlayer();
      });
    });

    let currentIndex = null,
      swipeDirection = null;
  
    // Detect swipe direction
    this.flickityLarge.on('dragMove',  (_event, _pointer, moveVector) => {
      currentIndex = this.flickityLarge.selectedIndex;
      swipeDirection = this.getSwipeDirection(moveVector);

      this.stopPlayer();
    });
    
    // Do stuff based on a successful swipe and it's direction
    this.flickityLarge.on('dragEnd', () => {
      if (this.flickityLarge.selectedIndex !== currentIndex) {
        
        if (swipeDirection === 'left') {
          this.flickitySmall.next();
        }
        else {
          this.flickitySmall.previous();
        }
      }
    });

    // Accessibility fix: ensure focusable elements in aria-hidden slides and boxes are not focusable
    const updateFocusableInHiddenSlides = () => {
      // Hide focusable elements in hidden slides and boxes
      this.querySelectorAll('.slideshow__slide[aria-hidden="true"], .slideshow__box[aria-hidden="true"]').forEach(container => {
        container.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach(el => {
          if (!el.hasAttribute('data-orig-tabindex') && el.hasAttribute('tabindex')) {
            el.setAttribute('data-orig-tabindex', el.getAttribute('tabindex'));
          }
          el.setAttribute('tabindex', '-1');
          if (['BUTTON', 'INPUT', 'SELECT', 'TEXTAREA'].includes(el.tagName)) {
            el.setAttribute('disabled', 'disabled');
          }
        });
      });
      // Restore focusable elements in visible slides and boxes
      this.querySelectorAll('.slideshow__slide:not([aria-hidden="true"]), .slideshow__box:not([aria-hidden="true"])').forEach(container => {
        container.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach(el => {
          if (el.hasAttribute('data-orig-tabindex')) {
            el.setAttribute('tabindex', el.getAttribute('data-orig-tabindex'));
            el.removeAttribute('data-orig-tabindex');
          } else {
            el.removeAttribute('tabindex');
          }
          if (['BUTTON', 'INPUT', 'SELECT', 'TEXTAREA'].includes(el.tagName)) {
            el.removeAttribute('disabled');
          }
        });
      });
    };
    // Run on Flickity events
    this.flickityContent.on('select', updateFocusableInHiddenSlides);
    this.flickityContent.on('settle', updateFocusableInHiddenSlides);
    // Run on init
    setTimeout(updateFocusableInHiddenSlides, 0);

    // Handle lazy video loading on slide change
    const handleSlideVideos = () => {
      const selectedIndex = this.flickityContent.selectedIndex;
      
      // Pause all videos
      this.elements.large.querySelectorAll('video').forEach(video => {
        video.pause();
      });
      this.elements.large.querySelectorAll('video-section[data-type="youtube"] iframe').forEach(iframe => {
        iframe.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
      });
      this.elements.large.querySelectorAll('video-section[data-type="vimeo"] iframe').forEach(iframe => {
        iframe.contentWindow?.postMessage('{"method":"pause"}', '*');
      });
      
      // Find current slide
      const currentSlide = this.elements.large.querySelector(`.slideshow__slide[data-slide-index="${selectedIndex}"]`);
      if (!currentSlide) return;

      const videoSection = currentSlide.querySelector('video-section[data-type="mp4"]');
      if (videoSection) {
        const video = videoSection.querySelector('video');
        if (video) {
          // Force background-video behavior: disable all native controls
          video.removeAttribute('controls');
          video.setAttribute('playsinline', '');
          video.setAttribute('webkit-playsinline', '');
          video.setAttribute('x5-playsinline', 'true');
          video.setAttribute('controlsList', 'nodownload noremoteplayback');
          video.setAttribute('disablePictureInPicture', 'true');

          if (video.preload === 'none') {
            video.preload = 'auto';
            video.load();
          }
          video.play().catch(() => { playBackgroundVideoOnFirstGesture(video); });
        }
        return;
      }

      const youtubeSection = currentSlide.querySelector('video-section[data-type="youtube"]');
      if (youtubeSection) {
        if (youtubeSection.dataset.initMode === 'manual' && !youtubeSection.player) {
          youtubeSection.removeAttribute('data-init-mode');
          youtubeSection.init();
        } else {
          const iframe = youtubeSection.querySelector('iframe');
          if (iframe) {
            iframe.contentWindow?.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
          } else if (youtubeSection.player && typeof youtubeSection.player.playVideo === 'function') {
            youtubeSection.player.playVideo();
          }
        }
        return;
      }

      const vimeoSection = currentSlide.querySelector('video-section[data-type="vimeo"]');
      if (vimeoSection) {
        if (vimeoSection.dataset.initMode === 'manual' && !vimeoSection.player) {
          vimeoSection.removeAttribute('data-init-mode');
          vimeoSection.init();
        } else {
          const iframe = vimeoSection.querySelector('iframe');
          if (iframe) {
            iframe.contentWindow?.postMessage('{"method":"play"}', '*');
          } else if (vimeoSection.player && typeof vimeoSection.player.play === 'function') {
            vimeoSection.player.play();
          }
        }
      }
    };
    
    this.flickityContent.on('select', handleSlideVideos);
    // Init call for first slide
    setTimeout(handleSlideVideos, 100);
  }

  stopPlayer() {
    if (this.settings.autorotate) {
      this.flickitySmall && this.flickitySmall.stopPlayer();
    }
  }

  getSwipeDirection(moveVector) {
    return moveVector.x > 0 ? 'right' : 'left';
  }

  matchMedia() {
    document.addEventListener('matchSmall', () => {
      this.unload();
      this.init();
    });

    document.addEventListener('unmatchSmall', () => {
      this.unload();
      this.init();
    });
  }

  slideTo(index) {
    this.flickityLarge.select(index);
    this.flickityContent.select(index);
    this.flickitySmall.select(index);
  }

  unload() {
    if (this.flickitySmall && typeof this.flickitySmall.destroy === 'function') {
      this.flickitySmall.destroy();
    }
    if (this.flickityContent && typeof this.flickityContent.destroy === 'function') {
      this.flickityContent.destroy();
    }
    if (this.flickityLarge && typeof this.flickityLarge.destroy === 'function') {
      this.flickityLarge.destroy();
    }
  }
}
customElements.define('slideshow-component', SlideshowComponent);

class TestimonialsComponent extends HTMLElement {
  constructor() {
    super();

    if (this.dataset.slider == 'true') {
      this.settings = {
        autorotate: this.dataset.autorotate == 'true',
        autorotateSpeed: parseInt(this.dataset.autorotateSpeed)
      };
      this.slider = this.querySelector('.testimonial__list');
      this.previews = this.querySelector('.testimonial__previews');

      theme.initWhenVisible({
        element: this,
        callback: this.init.bind(this),
        threshold: 600
      });
      theme.initWhenVisible({
        element: this,
        callback: this.update.bind(this),
        threshold: 0
      });
    }
  }

  init() {
    setTimeout(() => {
      if (this.slider) {
        this.flickity = new Flickity(this.slider, {
          accessibility: false,
          rightToLeft: theme.config.rtl,
          prevNextButtons: true,
          arrowShape: 'M29.043 19.756l-28.125 28.125c-0.605 0.606-0.918 1.406-0.918 2.031s0.305 1.599 0.916 2.209l28.125 28.125c1.221 1.221 3.199 1.221 4.418 0s1.221-3.199 0-4.418l-22.793-22.793h86.209c1.727 0 3.125-1.398 3.125-2.949 0-1.727-1.398-3.125-3.125-3.125h-86.211l22.793-22.793c1.221-1.221 1.221-3.199 0-4.418s-3.203-1.217-4.414 0.006z',
          pageDots: false,
          wrapAround: true,
          draggable: true,
          cellAlign: 'center',
          autoPlay: this.settings.autorotate ? this.settings.autorotateSpeed : false,
          pauseAutoPlayOnHover: true,
          asNavFor: this.previews
        });
      }

      this.flickityNav = new Flickity(this.previews, {
        accessibility: false,
        rightToLeft: theme.config.rtl,
        prevNextButtons: this.flickity ? false : true,
        arrowShape: this.flickity ? '' : 'M29.043 19.756l-28.125 28.125c-0.605 0.606-0.918 1.406-0.918 2.031s0.305 1.599 0.916 2.209l28.125 28.125c1.221 1.221 3.199 1.221 4.418 0s1.221-3.199 0-4.418l-22.793-22.793h86.209c1.727 0 3.125-1.398 3.125-2.949 0-1.727-1.398-3.125-3.125-3.125h-86.211l22.793-22.793c1.221-1.221 1.221-3.199 0-4.418s-3.203-1.217-4.414 0.006z',
        pageDots: false,
        wrapAround: true,
        selectedAttraction: 0.2,
        friction: 0.8,
        adaptiveHeight: true,
      });

      this.flickityNav.previous();
      if (this.settings.autorotate) {
        this.flickity.pausePlayer();
      }

      this.flickity && this.flickity.on('staticClick', (_event, _pointer, _cellElement, cellIndex) => {
        if (typeof cellIndex == 'number') {
          this.flickityNav.select(cellIndex);
        }
      });
      
      // Do stuff based on a successful swipe and it's direction
      this.flickity && this.flickity.on('change', (index) => {
        this.flickityNav.select(index);
      });
    });
  }

  update() {
    setTimeout(() => {
      if (this.flickity && this.flickityNav) {
        this.flickityNav.next();
        if (this.settings.autorotate) {
          this.flickity.unpausePlayer();
        }
      }
    }, 300);
  }

  getSwipeDirection(moveVector) {
    return moveVector.x > 0 ? 'right' : 'left';
  }
}
customElements.define('testimonials-component', TestimonialsComponent);

class UseAnimate extends HTMLElement {
  constructor() {
    super();

    theme.initWhenVisible({
      element: this,
      callback: this.init.bind(this),
      threshold: 0
    });
  }

  init() {
    this.setAttribute('animate', '');
  }
}
customElements.define('use-animate', UseAnimate);

class AnimateSticky extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    // data-follow names the element this bar stands in for: the bar shows
    // exactly while that element is out of view — below the fold at page
    // open included — so the page always offers one visible instance of it.
    // Without data-follow the bar reveals only after scrolling past its own
    // anchor position.
    const followTarget = document.getElementById(this.getAttribute('data-follow'));

    if (followTarget && 'IntersectionObserver' in window) {
      this.followObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            window.requestAnimationFrame(this.reset.bind(this));
          } else {
            window.requestAnimationFrame(this.reveal.bind(this));
          }
        });
      });
      this.followObserver.observe(followTarget);
      return;
    }

    this.onScrollHandler = this.onScroll.bind(this);

    window.addEventListener('scroll', this.onScrollHandler, false);
    this.onScrollHandler();
  }

  disconnectedCallback() {
    if (this.followObserver) this.followObserver.disconnect();
    if (this.onScrollHandler) window.removeEventListener('scroll', this.onScrollHandler);
  }

  onScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    if (scrollTop > this.getOffsetTop(this)) {
      window.requestAnimationFrame(this.reveal.bind(this));
    } else {
      window.requestAnimationFrame(this.reset.bind(this));
    }
  }

  reveal() {
    this.setAttribute('animate', '');
  }

  reset() {
    this.removeAttribute('animate');
  }

  getOffsetTop(element) {
    let offsetTop = 0;
    while(element) {
      offsetTop += element.offsetTop;
      element = element.offsetParent;
    }
    return offsetTop;
  }
}
customElements.define('animate-sticky', AnimateSticky);

class LookbookComponent extends HTMLElement {
  constructor() {
    super();

    theme.initWhenVisible({
      element: this,
      callback: this.init.bind(this),
      threshold: 0
    });
  }

  init() {
    const hotspots = this.querySelectorAll('.look__hotspot');
    hotspots.forEach((hotspot) => {
      hotspot.classList.add('is-active');
    });

    setTimeout(() => {
      hotspots.forEach((hotspot) => {
        hotspot.classList.remove('is-active');
      });
    }, 3e3);
  }
}
customElements.define('lookbook-component', LookbookComponent);

class TabCollage extends HTMLElement {
  constructor() {
    super();

    this.querySelectorAll('.tab-collage__heading').forEach(
      (button) => button.addEventListener('click', this.onButtonClick.bind(this))
    );

    if (this.dataset.accordion === undefined) {
      this.querySelectorAll('.tab-collage__heading').forEach(
        (button) => button.addEventListener('mouseover', this.onButtonClick.bind(this))
      );
    }
  }

  onButtonClick(event) {
    const target = event.target;
    if (target.classList.contains('is-active')) {
      if (this.dataset.accordion !== undefined) {
        target.classList.remove('is-active');
      }
      return;
    }

    this.pauseAllMedia();

    this.querySelectorAll('[data-block-id]').forEach((button) => {
      if (target.dataset.blockId === button.dataset.blockId) {
        button.classList.add('is-active');
      }
      else {
        button.classList.remove('is-active');
      }
    });
  }

  pauseAllMedia() {
    this.querySelectorAll('video-section[data-type="youtube"] iframe').forEach((video) => {
      video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
    });
    this.querySelectorAll('video-section[data-type="vimeo"] iframe').forEach((video) => {
      video.contentWindow.postMessage('{"method":"pause"}', '*');
    });
    this.querySelectorAll('video').forEach((video) => video.pause());
  }
}
customElements.define('tab-collage', TabCollage);

class CountdownTimer extends HTMLElement {
  constructor() {
    super();

    this.parent = this.closest('.product-countdown');
    this.date = this.parseToDate(this.dataset.date).getTime();
    
    if (isNaN(this.date)) {
      this.unload();
      return;
    }

    theme.initWhenVisible({
      element: this,
      callback: this.init.bind(this),
      threshold: 200
    });
  }

  parseToDate(input) {
    if (input.includes("T")) {
      return new Date(input);
    }
    
    const [month, day, year] = input.split('-');
    return new Date(`${year}-${month}-${day}`);
  }

  init() {
    this.timer();
    this.dataset.interval = setInterval(this.timer.bind(this), 1000);
  }

  timer() {
    const now = new Date(),
      countTo = new Date(this.date),
      timeDifference = (countTo - now);

    if (timeDifference < 0) {
      this.unload();
      return;
    }

    const secondsInADay = 60 * 60 * 1000 * 24,
      secondsInAHour = 60 * 60 * 1000;

    const days = Math.floor(timeDifference / (secondsInADay) * 1);
    const hours = Math.floor((timeDifference % (secondsInADay)) / (secondsInAHour) * 1);
    const mins = Math.floor(((timeDifference % (secondsInADay)) % (secondsInAHour)) / (60 * 1000) * 1);
    const secs = Math.floor((((timeDifference % (secondsInADay)) % (secondsInAHour)) % (60 * 1000)) / 1000 * 1);

    if (this.dataset.compact == 'true') {
      const dayHTML = days > 0 ? `<div class="countdown__item"><span>${days}${theme.dateStrings.d}</span></div>` : '';
      const otherHTML = `<div class="countdown__item"><span>${hours > 9 ? hours : '0' + hours}:${mins > 9 ? mins : '0' + mins}:${secs > 9 ? secs : '0' + secs}</span></div>`;
  
      this.innerHTML = dayHTML + otherHTML;
    }
    else {
      const dayHTML = days > 0 ? `<div class="countdown__item"><span>${days}</span> ${days == 1 ? theme.dateStrings.day : theme.dateStrings.days}</div>` : '';
      const hourHTML = `<div class="countdown__item"><span>${hours}</span> ${hours == 1 ? theme.dateStrings.hour : theme.dateStrings.hours}</div>`;
      const minHTML = `<div class="countdown__item"><span>${mins}</span> ${mins == 1 ? theme.dateStrings.minute : theme.dateStrings.minutes}</div>`;
      const secHTML = `<div class="countdown__item"><span>${secs}</span> ${secs == 1 ? theme.dateStrings.second : theme.dateStrings.seconds}</div>`;

      this.innerHTML = dayHTML + hourHTML + minHTML + secHTML;
    }
  }

  unload() {
    if (this.dataset.interval) {
      clearInterval(this.dataset.interval);
    }

    this.classList.add('hidden');
    if (this.parent) {
      this.parent.classList.add('hidden');
    }
  }
}
customElements.define('countdown-timer', CountdownTimer);

class GMap extends HTMLElement {
  constructor() {
    super();

    if (!this.dataset.apiKey || !this.dataset.mapAddress) {
      return;
    }

    theme.initWhenVisible({
      element: this,
      callback: this.prepMapApi.bind(this),
      threshold: 200
    });
  }

  prepMapApi() {
    this.loadScript().then(this.initMap.bind(this));
  }

  loadScript() {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      document.body.appendChild(script);
      script.onload = resolve;
      script.onerror = reject;
      script.async = true;
      script.src = 'https://maps.googleapis.com/maps/api/js?key=' + this.dataset.apiKey;
    });
  }

  initMap() {
    const geocoder = new google.maps.Geocoder();

    geocoder.geocode({ address: this.dataset.mapAddress }, (results, status) => {
      if (status !== google.maps.GeocoderStatus.OK) {
        if (Shopify.designMode) {}
      }
      else {
        const mapOptions = {
          zoom: parseInt(this.dataset.zoom),
          center: results[0].geometry.location,
          draggable: true,
          clickableIcons: false,
          scrollwheel: false,
          disableDoubleClickZoom: true,
          disableDefaultUI: true
        };

        const map = new google.maps.Map(this, mapOptions), center = map.getCenter();

        map.setCenter(center);

        const icon = {
          path: "M32.7374478,5.617 C29.1154478,1.995 24.2994478,0 19.1774478,0 C14.0544478,0 9.23944778,1.995 5.61744778,5.617 C-1.08555222,12.319 -1.91855222,24.929 3.81344778,32.569 L19.1774478,54.757 L34.5184478,32.6 C40.2734478,24.929 39.4404478,12.319 32.7374478,5.617 Z M19.3544478,26 C15.4954478,26 12.3544478,22.859 12.3544478,19 C12.3544478,15.141 15.4954478,12 19.3544478,12 C23.2134478,12 26.3544478,15.141 26.3544478,19 C26.3544478,22.859 23.2134478,26 19.3544478,26 Z",
          fillColor: this.dataset.markerColor,
          fillOpacity: 1,
          anchor: new google.maps.Point(15, 55),
          strokeWeight: 0,
          scale: 0.6
        };

        new google.maps.Marker({
          map: map,
          position: map.getCenter(),
          icon: icon
        });

        const styledMapType = new google.maps.StyledMapType(JSON.parse(this.parentNode.querySelector('[data-gmap-style]').innerHTML));

        map.mapTypes.set('styled_map', styledMapType);
        map.setMapTypeId('styled_map');

        google.maps.event.addDomListener(window, 'resize', function () {
          google.maps.event.trigger(map, 'resize');
          map.setCenter(center);
        });
      }
    });
  }
}
customElements.define('g-map', GMap);

class RelatedButtons extends HTMLElement {
  constructor() {
    super();

    if(!document.querySelector(this.dataset.scrollto)) {
      this.classList.add('hidden');
      return;
    }

    window.addEventListener('scroll', this.checkListener.bind(this));
    this.querySelectorAll('a').forEach(
      (button) => button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  checkListener() {
    const element = document.querySelector(this.dataset.scrollto);
    const show = window.scrollY >= element.offsetTop - element.clientHeight;

    if (show) {
      this.classList.add('is-flipped');
    } else {
      this.classList.remove('is-flipped');
    }
  }

  onButtonClick(event) {
    event.preventDefault();
    const target = event.target;
    const scrollIntoView = document.querySelector(target.getAttribute('href'));

    scrollIntoView.scrollIntoView({behavior: "smooth"});
  }
}
customElements.define('related-buttons', RelatedButtons);

class ProductRecentlyViewed extends HTMLElement {
  constructor() {
    super();
    
    // We save the product ID in local storage to be eventually used for recently viewed section
    if (isStorageSupported('local')) {
      const productId = parseInt(this.dataset.productId);
      const cookieName = 'beyours:recently-viewed';
      const items = JSON.parse(window.localStorage.getItem(cookieName) || '[]');

      // We check if the current product already exists, and if it does not, we add it at the start
      if (!items.includes(productId)) {
        items.unshift(productId);
      }

      // By keeping only the 4 most recent
      window.localStorage.setItem(cookieName, JSON.stringify(items.slice(0, 5)));
    }
  }
}
customElements.define('product-recently-viewed', ProductRecentlyViewed);

class RecentlyViewedProducts extends HTMLElement {
  constructor() {
    super();

    theme.initWhenVisible({
      element: this,
      callback: this.init.bind(this),
      threshold: 600
    });
  }

  init() {
    fetch(this.dataset.url + this.getQueryString())
      .then(response => response.text())
      .then(text => {
        const html = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('.shopify-section');

        if (html === null) return;

        const recommendations = html.querySelector('recently-viewed-products');
        if (recommendations && recommendations.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }
      })
      .catch(e => {
        console.error(e);
      });
  }

  getQueryString() {
    const cookieName = 'beyours:recently-viewed';
    const items = JSON.parse(window.localStorage.getItem(cookieName) || "[]");
    if (this.dataset.productId && items.includes(parseInt(this.dataset.productId))) {
      items.splice(items.indexOf(parseInt(this.dataset.productId)), 1);
    }
    return items.map((item) => "id:" + item).slice(0, 4).join(" OR ");
  }
}
customElements.define('recently-viewed-products', RecentlyViewedProducts);

class VideoSection extends HTMLElement {
  constructor() {
    super();

    this.background = this.dataset.initMode !== 'template';
    this.loop = this.dataset.loop;

    // Skip auto-init for lazy videos (will be initialized on slide change)
    if (this.dataset.initMode === 'manual') {
      return;
    }

    if (this.background) {
      theme.initWhenVisible({
        element: this,
        callback: this.init.bind(this),
        threshold: 600
      });
    }
    else {
      this.init();
    }
  }

  init() {
    this.parentSelector = this.dataset.parent || '.media-wrapper';
    this.parent = this.closest(this.parentSelector);

    switch(this.dataset.type) {
      case 'youtube':
        this.initYoutubeVideo();
        break;

      case 'vimeo':
        this.initVimeoVideo();
        break;

      case 'mp4':
        this.initMp4Video();
        break;
    }
  }

  initYoutubeVideo() {
    this.setAsLoading();
    this.loadScript('youtube').then(this.setupYoutubePlayer.bind(this));
  }

  initVimeoVideo() {
    this.setAsLoading();
    this.loadScript('vimeo').then(this.setupVimeoPlayer.bind(this));
  }

  initMp4Video() {
    const player = this.querySelector('video');

    if (player) {
      // Force background-video behavior: disable all native controls
      player.removeAttribute('controls');
      player.setAttribute('playsinline', '');
      player.setAttribute('webkit-playsinline', '');
      player.setAttribute('x5-playsinline', 'true');
      player.setAttribute('controlsList', 'nodownload noremoteplayback');
      player.setAttribute('disablePictureInPicture', 'true');

      const promise = player.play();

      // Edge does not return a promise (video still plays)
      if (typeof promise !== 'undefined') {
        promise.then(function() {
          // playback normal
        }).catch(function() {
          // Background video: retry on first user gesture instead of showing controls
          playBackgroundVideoOnFirstGesture(player);
        });
      }
    }
  }

  loadScript(videoType) {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      document.body.appendChild(script);
      script.onload = resolve;
      script.onerror = reject;
      script.async = true;
      script.src = videoType === 'youtube' ? '//www.youtube.com/iframe_api' : '//player.vimeo.com/api/player.js';
    });
  }

  setAsLoading() {
    this.parent.setAttribute('loading', true);
  }

  setAsLoaded() {
    this.parent.removeAttribute('loading');
    this.parent.setAttribute('loaded', true);
  }

  setupYoutubePlayer() {
    const videoId = this.dataset.videoId;
    
    const playerInterval = setInterval(() => {
      if (window.YT) {
        window.YT.ready(() => {
          const element = document.createElement('div');
          this.appendChild(element);

          if(!this.player) this.player = new YT.Player(element, {
            videoId: videoId,
            playerVars: {
              showinfo: 0,
              controls: !this.background,
              fs: !this.background,
              rel: 0,
              height: '100%',
              width: '100%',
              iv_load_policy: 3,
              html5: 1,
              loop: this.loop == '1' ? 1 : 0,
              playlist: videoId,
              playsinline: 1,
              modestbranding: 1,
              disablekb: 1
            },
            events: {
              onReady: this.onYoutubeReady.bind(this),
              onStateChange: this.onYoutubeStateChange.bind(this)
            }
          });
          clearInterval(playerInterval);
        });
      }
    }, 50);
  }

  onYoutubeReady(event) {
    this.iframe = this.querySelector('iframe'); // iframe once YT loads
    this.iframe.setAttribute('tabindex', '-1');
    this.background && event.target.mute();
    if (typeof this.player.playVideo === 'function') this.player.playVideo();

    this.setAsLoaded();

    // pause when out of view
    const observer = new IntersectionObserver((entries, _observer) => {
      entries.forEach(entry => {
        entry.isIntersecting ? this.youtubePlay() : this.youtubePause();
      });
    }, {rootMargin: '0px 0px 50px 0px'});

    observer.observe(this.iframe);
  }

  onYoutubeStateChange(event) {
    switch (event.data) {
      case -1: // unstarted
        // Handle low power state on iOS by checking if
        // video is reset to unplayed after attempting to buffer
        if (this.attemptedToPlay) {
          this.setAsLoaded();
          this.closest('.banner')?.classList.add('video-interactable');
        }
        break;
      case 0: // ended, loop it
        this.youtubePlay();
        break;
      case 1: // playing
        this.setAsLoaded();
        break;
      case 3: // buffering
        this.attemptedToPlay = true;
        break;
    }
  }

  youtubePlay() {
    if (this.background && this.player && typeof this.player.playVideo === 'function') {
      this.player.playVideo();
    }
  }

  youtubePause() {
    if (this.background && this.player && typeof this.player.pauseVideo === 'function') {
      this.player.pauseVideo();
    }
  }

  setupVimeoPlayer() {
    const videoId = this.dataset.videoId;

    const playerInterval = setInterval(() => {
      if (window.Vimeo) {
        this.player = new Vimeo.Player(this, {
          id: videoId,
          autoplay: true,
          autopause: false,
          background: this.background,
          controls: !this.background,
          loop: this.loop == '1' ? true : false,
          playlist: videoId,
          height: '100%',
          width: '100%'
        });
        this.player.ready().then(this.onVimeoReady.bind(this));

        clearInterval(playerInterval);
      }
    }, 50);
  }

  onVimeoReady() {
    this.iframe = this.querySelector('iframe');
    this.iframe.setAttribute('tabindex', '-1');

    this.background && this.player.setMuted(true);

    this.setAsLoaded();

    // pause when out of view
    const observer = new IntersectionObserver((entries, _observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.vimeoPlay();
        } else {
          this.vimeoPause();
        }
      });
    }, {rootMargin: '0px 0px 50px 0px'});

    observer.observe(this.iframe);
  }

  vimeoPlay() {
    if (this.background && this.player && typeof this.player.play === 'function') {
      this.player.play();
    }
  }

  vimeoPause() {
    if (this.background && this.player && typeof this.player.pause === 'function') {
      this.player.pause();
    }
  }
}
customElements.define('video-section', VideoSection);

class BundleProduct extends HTMLElement {
  constructor() {
    super();

    this.classes = {
      souldout: 'price--sold-out',
      onsale: 'price--on-sale',
      nocompare: 'price--no-compare'
    };

    this.addToCart = this.querySelector('add-to-cart');
    this.price = this.querySelector('.price');
    this.variants = this.querySelector('select');
    if (this.variants) {
      this.variants.addEventListener('change', this.onSelectChange.bind(this));
    }
  }

  onSelectChange(event) {
    const variants = event.target;
    const variant = variants.options[variants.selectedIndex];
    const compare_at_price = parseInt(variant.dataset.compare_at_price || 0);
    const price = parseInt(variant.dataset.price || 0);
    const available = variant.dataset.available === 'true';
    const price_varies = variants.dataset.price_varies === 'true';
    const compare_at_price_varies = variants.dataset.compare_at_price_varies === 'true';

    // Remove classes
    this.price.classList.remove(this.classes.souldout);
    this.price.classList.remove(this.classes.onsale);
    this.price.classList.remove(this.classes.nocompare);

    // Add classes
    if (available === false) {
      this.price.classList.add(this.classes.souldout);
    }
    else if (compare_at_price > price && available) {
      this.price.classList.add(this.classes.onsale);
    }
    if (price_varies == false && compare_at_price_varies) {
      this.price.classList.add(this.classes.nocompare);
    }

    // Change price
    const price__regular = this.querySelector('.price__regular');
    price__regular.querySelector('.price-item--regular').innerHTML = `<price-money><bdi>${theme.Currency.formatMoney(price, theme.shopSettings.moneyFormat)}</bdi></price-money>`;

    const price__sale = this.querySelector('.price__sale');
    price__sale.querySelector('.price-item--regular').innerHTML = `<price-money><bdi>${theme.Currency.formatMoney(compare_at_price, theme.shopSettings.moneyFormat)}</bdi></price-money>`;
    price__sale.querySelector('.price-item--sale').innerHTML = `<price-money><bdi>${theme.Currency.formatMoney(price, theme.shopSettings.moneyFormat)}</bdi></price-money>`;

    // Change variant id
    if (this.addToCart) {
      this.addToCart.dataset.variantId = variant.value;
    }
  }
}
customElements.define('bundle-product', BundleProduct);

class BundleProducts extends HTMLElement {
  constructor() {
    super();

    this.classes = {
      hover: 'is-hover',
      active: 'is-active',
    };

    this.miniCart = document.querySelector('mini-cart');
    this.button = this.querySelector('button');
    if (this.button) this.button.addEventListener('click', this.onButtonClick.bind(this));

    this.blocks = this.querySelectorAll('[data-block-id]');
    this.blocks.forEach((block) => block.addEventListener('mouseenter', this.onEnterHandler.bind(this)));
    this.blocks.forEach((block) => block.addEventListener('mouseleave', this.onLeaveHandler.bind(this)));
  }

  onEnterHandler(event) {
    this.classList.add(this.classes.hover);

    const target = event.target;
    const blockId = target.dataset.blockId;
    this.blocks.forEach((block) => {
      if (target.nodeName === 'BUNDLE-PRODUCT') {
        if (block.nodeName === 'BUNDLE-PRODUCT') {
          block.classList.add(this.classes.active);
          return;
        }
      }
      if (block.dataset.blockId === blockId) {
        block.classList.add(this.classes.active);
      }
    });
  }

  onLeaveHandler() {
    this.classList.remove(this.classes.hover);
    this.blocks.forEach((block) => block.classList.remove(this.classes.active));
  }

  onButtonClick(event) {
    event.preventDefault();

    const ids = this.querySelectorAll('[name="id"]');
    const items = {
      items: [...ids].map((e => e.value)).map((e => ({
        id: e,
        quantity: 1
      })))
    };

    if (document.body.classList.contains('template-cart') || !theme.shopSettings.cartDrawer) {
      Shopify.postLink2(theme.routes.cart_add_url, {
        parameters: {
          ...items
        }
      });
      return;
    }

    this.handleErrorMessage();

    this.button.setAttribute('disabled', true);
    this.button.classList.add('loading');
    const sections = this.miniCart ? this.miniCart.getSectionsToRender().map((section) => section.id) : [];

    const body = JSON.stringify({
      ...items,
      sections: sections,
      sections_url: window.location.pathname
    });

    fetch(`${theme.routes.cart_add_url}`, { ...fetchConfig('javascript'), body })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.handleErrorMessage(response.description);
          return;
        }

        if (response.status === 422) {
          document.dispatchEvent(new CustomEvent('ajaxProduct:error', {
            detail: {
              errorMessage: response.description
            }
          }));
        }
        else {
          this.miniCart && this.miniCart.renderContents(response);

          document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
            detail: {
              product: response
            }
          }));
        }
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.button.classList.remove('loading');
        this.button.removeAttribute('disabled');
      });
  }

  handleErrorMessage(errorMessage = false) {
    this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
    
    if (this.errorMessageWrapper) {
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');
      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }
    else {
      if (errorMessage) {
        alert(errorMessage);
      }
    }
  }
}
customElements.define('bundle-products', BundleProducts);

class ShopTheLook extends HTMLElement {
  constructor() {
    super();

    if (this.dataset.blockCount == 1) {
      return;
    }

    theme.initWhenVisible({
      element: this,
      callback: this.init.bind(this),
      threshold: 600
    });
  }

  init() {
    this.products = this.querySelector('.image-with-text__products');

    this.blocks = this.querySelectorAll('lookbook-component [data-block-index]');
    this.blocks.forEach((block) => block.addEventListener('mouseenter', this.onEnterHandler.bind(this)));

    this.initSlider();
  }

  onEnterHandler(event) {
    if (this.flickity && typeof this.flickity.select === 'function') {
      const index = parseInt(event.target.dataset.blockIndex);
      this.flickity.select(index);
    }
  }

  onFocusHandler(index) {
    this.blocks.forEach((block) => {
      block.classList.remove('focus');

      if (parseInt(block.dataset.blockIndex) === index) {
        block.classList.add('focus');
      }
    });
  }

  initSlider() {
    this.flickity = new Flickity(this.products, {
      accessibility: false,
      rightToLeft: theme.config.rtl,
      prevNextButtons: false,
      pageDots: true,
      wrapAround: true,
      fade: true,
      setGallerySize: true,
      on: {
        ready: () => {
          this.onFocusHandler(0);
        },
        change: (index) => {
          this.onFocusHandler(index);
        }
      }    
    });
  }
}
customElements.define('shop-the-look', ShopTheLook);

class SelectWrapper extends HTMLElement {
  constructor() {
    super();

    theme.initWhenVisible({
      element: this,
      callback: this.init.bind(this),
      threshold: 200
    });

    this.select = this.querySelector('select');
    if (this.select) {
      this.select.addEventListener('change', this.handleSelectChange.bind(this));
    }
  }

  init() {
    const style = window.getComputedStyle(this.select);
    const value = this.select.options[this.select.selectedIndex].text;

    const text = document.createElement('span');
    text.style.fontFamily = style.fontFamily;
    text.style.fontSize = style.fontSize;
    text.style.fontWeight = style.fontWeight;
    text.style.visibility = 'hidden';
    text.style.position = 'absolute';
    text.innerHTML = value;

    document.body.appendChild(text);
    const width = text.clientWidth;
    
    this.style.setProperty('--width', `${width}px`);
    text.remove();
  }

  handleSelectChange() {
    this.init();
  }
}
customElements.define('select-wrapper', SelectWrapper);

class ImageComparison extends HTMLElement {
  constructor() {
    super();

    this.active = false;
    this.button = this.querySelector('button');
    this.horizontal = this.dataset.layout === 'horizontal';
    this.bodyStyleOverflowY = document.body.style.overflowY;
    this.init();

    theme.initWhenVisible({
      element: this.querySelector('.image-comparison__animate'),
      callback: this.animate.bind(this),
      threshold: 0
    });
  }

  animate() {
    this.setAttribute('animate', '');
    this.classList.add('animating');
    setTimeout(() => {
      this.classList.remove('animating');
    }, 1e3);
  }

  init() {
    this.button.addEventListener('touchstart', this.startHandler.bind(this));
    document.body.addEventListener('touchend', this.endHandler.bind(this));
    document.body.addEventListener('touchmove', this.onHandler.bind(this));
    
    this.button.addEventListener('mousedown', this.startHandler.bind(this));
    document.body.addEventListener('mouseup', this.endHandler.bind(this));
    document.body.addEventListener('mousemove', this.onHandler.bind(this));
  }

  startHandler() {
    if(window.innerWidth <= 750) {
      document.body.style.overflowY = 'hidden';
    }

    this.active = true;
    this.classList.add('scrolling');
  }

  endHandler() {
    this.active = false;
    this.classList.remove('scrolling');
    document.body.style.overflowY = this.bodyStyleOverflowY;
  }

  onHandler(e) {
    if (!this.active) return;
    
    const event = (e.touches && e.touches[0]) || e;
    const x = this.horizontal
                ? event.pageX - this.offsetLeft
                : event.pageY - this.offsetTop;

    this.scrollIt(x);
  }

  scrollIt(x) {
    const distance = this.horizontal ? this.clientWidth : this.clientHeight;
    
    const max = distance - 20;
    const min = 20;
    const mouseX = Math.max(min, (Math.min(x, max)));
    const mousePercent = (mouseX * 100) / distance;
    this.style.setProperty('--percent', mousePercent + '%');
  }
}
customElements.define('image-comparison', ImageComparison);

class ScrollingPromotion extends HTMLElement {
  constructor() {
    super();

    this.config = {
      moveTime: parseFloat(this.dataset.speed), // 100px going to move for
      space: 100, // 100px
    };

    this.promotion = this.querySelector('.promotion');

    theme.initWhenVisible({
      element: this,
      callback: this.init.bind(this),
      threshold: 600
    });
  }

  init() {
    if (this.childElementCount === 1) {
      this.promotion.classList.add('promotion--animated');

      for (let index = 0; index < 10; index++) {
        this.clone = this.promotion.cloneNode(true);
        this.clone.setAttribute('aria-hidden', true);
        this.appendChild(this.clone);

        const imageWrapper = this.clone.querySelector('.media-wrapper');
        if (imageWrapper) {
          imageWrapper.classList.remove('loading');
        }
      }

      const animationTimeFrame = (this.promotion.clientWidth / this.config.space) * this.config.moveTime;
      this.style.setProperty('--duration', `${animationTimeFrame}s`);

      // pause when out of view
      const observer = new IntersectionObserver((entries, _observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.scrollingPlay();
          } else {
            this.scrollingPause();
          }
        });
      }, {rootMargin: '0px 0px 50px 0px'});

      observer.observe(this);
    }
  }

  scrollingPlay() {
    this.classList.remove('scrolling-promotion--paused');
  }

  scrollingPause() {
    this.classList.add('scrolling-promotion--paused');
  }
}
customElements.define('scrolling-promotion', ScrollingPromotion);

class AccordionTab extends HTMLElement {
  constructor() {
    super();

    this.details = this.querySelector('details');
    this.summary = this.querySelector('summary');
    this.content = this.querySelector('.accordion__content');

    this.animation = null;
    this.isClosing = false;
    this.isExpanding = false;

    theme.initWhenVisible({
      element: this,
      callback: this.init.bind(this),
      threshold: 200
    });
  }

  init() {
    this.summary.addEventListener('click', (e) => this.onClick(e));
  }

  onClick(e) {
    e.preventDefault();

    this.details.style.overflow = 'hidden';
    if (this.isClosing || !this.details.open) {
      this.open();
    }
    else if (this.isExpanding || this.details.open) {
      this.shrink();
    }
  }

  shrink() {
    this.isClosing = true;
    
    const summaryStyle = window.getComputedStyle(this.summary);
    const startHeight = `${this.details.offsetHeight}px`;
    const endHeight = `${this.summary.offsetHeight + parseInt(summaryStyle.marginTop)}px`;
    
    if (this.animation) {
      this.animation.cancel();
    }
    
    this.animation = this.details.animate({
      height: [startHeight, endHeight]
    }, {
      duration: 250,
      easing: 'ease'
    });
    
    this.animation.onfinish = () => this.onAnimationFinish(false);
    this.animation.oncancel = () => this.isClosing = false;
  }

  open() {
    this.details.style.height = `${this.details.offsetHeight}px`;
    this.details.open = true;
    window.requestAnimationFrame(() => this.expand());
  }

  expand() {
    this.isExpanding = true;

    const summaryStyle = window.getComputedStyle(this.summary);
    const startHeight = `${this.details.offsetHeight}px`;
    const endHeight = `${this.summary.offsetHeight + parseInt(summaryStyle.marginTop) + this.content.offsetHeight}px`;
    
    if (this.animation) {
      this.animation.cancel();
    }
    
    this.animation = this.details.animate({
      height: [startHeight, endHeight]
    }, {
      duration: 400,
      easing: 'ease-out'
    });
    this.animation.onfinish = () => this.onAnimationFinish(true);
    this.animation.oncancel = () => this.isExpanding = false;
  }

  onAnimationFinish(open) {
    this.details.open = open;
    this.animation = null;
    this.isClosing = false;
    this.isExpanding = false;
    this.details.style.height = this.details.style.overflow = '';
  }
}
customElements.define('accordion-tab', AccordionTab);

class ProductRecommendations extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    new IntersectionObserver(this.handleIntersection.bind(this), {rootMargin: '0px 0px 600px 0px'}).observe(this);
  }

  handleIntersection(entries, observer) {
    if (!entries[0].isIntersecting) return;
    observer.unobserve(this);

    fetch(this.dataset.url)
      .then(response => response.text())
      .then(text => {
        const html = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('.shopify-section');

        if (html === null) return;
        
        const recommendations = html.querySelector('product-recommendations');
        if (recommendations && recommendations.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }
      })
      .catch(e => {
        console.error(e);
      });
  }
}
customElements.define('product-recommendations', ProductRecommendations);

class OptionTable extends HTMLElement {
  constructor() {
    super();

    this.config = {
    };

    this.imageOptionRows = this.querySelectorAll(".ot--images .ot--option-row");
    this.dataOptionRows = this.querySelectorAll(".ot--data .ot--option-row");

    theme.initWhenVisible({
      element: this,
      callback: this.init.bind(this),
      threshold: 600
    });
  }

  init() {
    const optionTable = this;

    this.imageOptionRows.forEach((imageOptionRow) => {
      imageOptionRow.addEventListener("mouseenter", function () {
        optionTable.querySelectorAll(".ot--data .ot--option-row").forEach((el) => {
          el.classList.remove("active");
        });
        optionTable.querySelectorAll(".ot--images .ot--option-row").forEach((el) => {
          el.style.opacity = "0.3";
        });
        optionTable.querySelectorAll(".ot--images .ot--option-row").forEach((el) => {
          el.style.zIndex = "initial";
        });

        this.style.opacity = "1";
        this.style.zIndex = "3";

        var imageRowId = this.id;
        var dataRowId = imageRowId.replace("imageRow", "dataRow");
        var activeDataRow = optionTable.querySelector(`#${dataRowId}`);
        activeDataRow.classList.add("active");

        optionTable.querySelectorAll(`:scope .data-row.data-headers>div:not(.blank)`).forEach(el => {
          el.classList.remove('active');
        });
        var activeDataRowValues = activeDataRow.querySelectorAll(`:scope div:not(.is-header)`);
        activeDataRowValues.forEach((activeDataRowValue, i) => {
          if (activeDataRowValue.querySelectorAll(`:scope svg.checkmark`).length > 0) {
            optionTable.querySelectorAll(`:scope .data-row.data-headers>div:not(.blank)`)[i].classList.add('active');
          }
        });
      });
      imageOptionRow.addEventListener("mouseleave", function () {
        optionTable.querySelectorAll(".ot--data .ot--option-row").forEach((el) => {
          el.classList.remove("active");
        });
        optionTable.querySelectorAll(".ot--images .ot--option-row").forEach((el) => {
          el.style.opacity = "1";
        });
        optionTable.querySelectorAll(`:scope .data-row.data-headers>div:not(.blank)`).forEach(el => {
          el.classList.remove('active');
        });
      });
    });

    this.dataOptionRows.forEach((dataOptionRow) => {
      dataOptionRow.addEventListener("mouseenter", function () {
        optionTable.querySelectorAll(".ot--data .ot--option-row").forEach((el) => {
          el.classList.remove("active");
        });
        this.classList.add("active");
        optionTable.querySelectorAll(".ot--images .ot--option-row").forEach((el) => {
          el.style.opacity = "0.3";
        });
        optionTable.querySelectorAll(".ot--images .ot--option-row").forEach((el) => {
          el.style.zIndex = "initial";
        });

        var dataRowId = this.id;
        var imageRowId = dataRowId.replace("dataRow", "imageRow");
        optionTable.querySelector(`#${imageRowId}`).style.opacity = "1";
        optionTable.querySelector(`#${imageRowId}`).style.zIndex = "3";
      });
      dataOptionRow.addEventListener("mouseleave", function () {
        optionTable.querySelectorAll(".ot--data .ot--option-row").forEach((el) => {
          el.classList.remove("active");
        });
        optionTable.querySelectorAll(".ot--images .ot--option-row").forEach((el) => {
          el.style.opacity = "1";
        });
        optionTable.querySelectorAll(`:scope .data-row.data-headers>div:not(.blank)`).forEach(el => {
          el.classList.remove('active');
        })
      });
    });
  }
}
customElements.define('option-table', OptionTable);

class LocalizationForm extends HTMLElement {
  constructor() {
    super();

    this.elements = {
      input: this.querySelector('input[name="locale_code"], input[name="country_code"]'),
      button: this.querySelector('button'),
      panel: this.querySelector('ul'),
    };
    
    this.elements.button.addEventListener('click', this.openSelector.bind(this));
    this.elements.button.addEventListener('focusout', this.closeSelector.bind(this));
    this.addEventListener('keyup', this.onContainerKeyUp.bind(this));

    this.querySelectorAll('a').forEach(item => item.addEventListener('click', this.onItemClick.bind(this)));
  }

  hidePanel() {
    this.elements.button.setAttribute('aria-expanded', 'false');
    this.elements.panel.setAttribute('hidden', true);

    if (this.classList.contains('on-header')) {
      document.body.classList.remove('localization--open');
    }
  }

  onContainerKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    this.hidePanel();
    // this.elements.button.focus();
  }

  onItemClick(event) {
    event.preventDefault();
    const form = this.querySelector('form');
    this.elements.input.value = event.currentTarget.dataset.value;
    if (form) form.submit();
  }

  openSelector() {
    // this.elements.button.focus();
    this.elements.panel.toggleAttribute('hidden');
    this.elements.button.setAttribute('aria-expanded', (this.elements.button.getAttribute('aria-expanded') === 'false').toString());

    if (this.classList.contains('on-header')) {
      document.body.classList.toggle('localization--open');

      // This list sits at z:2 — above the mega menu's z:-1 backdrop, below its
      // z:101 content — so it cannot layer with an open menu; close the menus.
      if (!this.elements.panel.hasAttribute('hidden')) {
        document.querySelectorAll('.shopify-section-header dropdown-menu, .shopify-section-header mega-menu').forEach((menu) => {
          if (menu.classList.contains('is-active') && typeof menu.close === 'function') menu.close();
        });
      }
    }
  }

  closeSelector(event) {
    const shouldClose = event.relatedTarget && event.relatedTarget.nodeName === 'BUTTON';
    if (event.relatedTarget === null || shouldClose) {
      this.hidePanel();
    }
  }
}
customElements.define('localization-form', LocalizationForm);

class HeaderPopover extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.popoverTarget = this.dataset.target;
    this.popoverEl = document.getElementById(this.popoverTarget);
    this.setupEventListeners();
  }

  setupEventListeners() {
    this.popoverEl.addEventListener('toggle', (e) => {
      if (this.popoverEl.matches(':popover-open')) {
        this.showPopover();
      } else {
        this.hidePopover();
      }
    });
  }

  showPopover() {
    document.body.classList.add('with-popover');
    //this.popoverEl.showPopover();
  }

  hidePopover() {
    document.body.classList.remove('with-popover');
    //this.popoverEl.hidePopover();
  }
}
customElements.define('header-popover', HeaderPopover);

if (!customElements.get('tabbed-collections')) {
  customElements.define('tabbed-collections', class TabbedCollections extends HTMLElement {
    constructor() {
      super();
      this.fakeTabs = null;
      this.realDetails = null;
    }

    connectedCallback() {
      this.fakeTabs = this.querySelectorAll('.fake-tab-item');
      // Direct children only — the panels contain their own <details> (card quick views).
      this.realDetails = this.querySelectorAll('.tabs-grid > details');
      
      if (this.fakeTabs.length === 0 || this.realDetails.length === 0) return;

      this.setupFakeTabs();
      this.setupRealDetails();
    }

    setupFakeTabs() {
      this.fakeTabs.forEach(fakeTab => {
        fakeTab.addEventListener('click', this.handleFakeTabClick.bind(this));
      });
    }

    setupRealDetails() {
      this.realDetails.forEach(details => {
        details.addEventListener('toggle', this.handleDetailsToggle.bind(this));

        // The name attribute guarantees at most one open panel, not always one —
        // activating the open tab's summary would close the whole section.
        const summary = details.querySelector('summary');
        if (summary) summary.addEventListener('click', (event) => {
          if (details.open) event.preventDefault();
        });
      });
    }

    handleFakeTabClick(event) {
      const clickedTab = event.currentTarget;
      const tabIndex = parseInt(clickedTab.dataset.tabIndex);

      this.setActiveTab(tabIndex);
      this.triggerRealTab(tabIndex);
      this.scrollTabIntoView(clickedTab);
    }

    handleDetailsToggle(event) {
      const details = event.currentTarget;
      if (!details.open) return;

      const detailsIndex = parseInt(details.style.getPropertyValue('--tab-index'));
      this.setActiveTab(detailsIndex);
      
      const activeTab = this.querySelector(`.fake-tab-item[data-tab-index="${detailsIndex}"]`);
      if (activeTab) this.scrollTabIntoView(activeTab);
    }

    setActiveTab(index) {
      this.fakeTabs.forEach(tab => {
        const isActive = parseInt(tab.dataset.tabIndex) === index;
        tab.classList.toggle('active', isActive);
        tab.setAttribute('aria-pressed', String(isActive));
      });
    }

    triggerRealTab(index) {
      const targetDetails = Array.from(this.realDetails).find(details => {
        return parseInt(details.style.getPropertyValue('--tab-index')) === index;
      });

      if (targetDetails && !targetDetails.open) {
        const summary = targetDetails.querySelector('summary');
        if (summary) summary.click();
      }
    }

    scrollTabIntoView(tab) {
      const container = this.querySelector('.fake-tabs-mobile');
      if (!container) return;

      const scrollLeft = tab.offsetLeft - (container.offsetWidth / 2) + (tab.offsetWidth / 2);
      container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
    }
  });
}

class DeferredSectionLoader {
  /** @type {Set<string>} Allowed data-callback method names. Extend via DeferredSectionLoader.allowCallback('name'). */
  static allowedCallbacks = new Set(['deferredLoadCallback', 'handleMouseover']);

  static allowCallback(name) {
    if (typeof name === 'string' && name) DeferredSectionLoader.allowedCallbacks.add(name);
  }

  constructor() {
    this.cache = new Map();
    this.pendingFetches = new Map();
    this.initialized = new WeakSet();
    this.init();
  }

  init() {
    document.querySelectorAll('[data-deferred-section]').forEach(section => {
      if (this.initialized.has(section)) return;
      this.initialized.add(section);
      this.setupSection(section);
    });
  }

  setupSection(section) {
    const trigger = section.dataset.trigger || 'visible';
    const threshold = parseInt(section.dataset.threshold) || 200;
    let loaded = false;
    
    const loadContent = () => {
      if (loaded) return;
      loaded = true;
      this.loadSection(section);
    };

    switch (trigger) {
      case 'visible':
        this.observeVisibility(section, loadContent, threshold);
        break;
      case 'hover':
        section.addEventListener('mouseenter', loadContent, { once: true });
        section.addEventListener('focusin', loadContent, { once: true });
        break;
      case 'click':
        section.addEventListener('click', loadContent, { once: true });
        break;
      default:
        this.observeVisibility(section, loadContent, threshold);
    }
  }

  observeVisibility(element, callback, threshold) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            callback();
            observer.disconnect();
          }
        });
      },
      { rootMargin: `${threshold}px 0px ${threshold}px 0px` }
    );
    observer.observe(element);
  }

  async loadSection(section) {
    const sectionId = section.dataset.sectionId;
    if (!sectionId) {
      console.error('DeferredSection: Missing data-section-id');
      return;
    }

    section.classList.add('is-loading');

    try {
      const html = await this.fetchSection(sectionId);
      this.injectContent(section, html);
      section.classList.remove('is-loading');
      section.classList.add('is-loaded');
      this.executeCallback(section);
      
      section.dispatchEvent(new CustomEvent('deferred:loaded', { 
        bubbles: true,
        detail: { sectionId, html, element: section }
      }));
    } catch (error) {
      console.error('DeferredSection: Error loading', error);
      section.classList.remove('is-loading');
      section.classList.add('is-error');
      
      section.dispatchEvent(new CustomEvent('deferred:error', { 
        bubbles: true,
        detail: { sectionId, error }
      }));
    }
  }

  async fetchSection(sectionId) {
    if (this.cache.has(sectionId)) return this.cache.get(sectionId);
    if (this.pendingFetches.has(sectionId)) return this.pendingFetches.get(sectionId);

    const url = `${window.location.pathname}?section_id=${sectionId}`;
    const supportsAbort = typeof AbortController !== 'undefined';
    const controller = supportsAbort ? new AbortController() : null;
    const signal = controller?.signal;
    const timeoutMs = parseInt(document.body?.dataset?.deferredTimeout, 10) || 15000;

    let timeoutId;
    if (controller) {
      timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    }

    const fetchPromise = fetch(url, signal ? { signal } : undefined)
      .then(response => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .then(html => {
        if (timeoutId) clearTimeout(timeoutId);
        this.cache.set(sectionId, html);
        this.pendingFetches.delete(sectionId);
        return html;
      })
      .catch(error => {
        if (timeoutId) clearTimeout(timeoutId);
        this.pendingFetches.delete(sectionId);
        throw error;
      });

    this.pendingFetches.set(sectionId, fetchPromise);
    return fetchPromise;
  }

  injectContent(section, html) {
    const placeholder = section.querySelector('[data-deferred-content]');
    if (!placeholder) return;

    const contentId = placeholder.dataset.contentId;
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    let sourceContent;
    if (contentId) {
      sourceContent = doc.querySelector(`[data-content-id="${contentId}"]:not([data-deferred-content])`);
    }
    if (!sourceContent) {
      sourceContent = doc.querySelector('.shopify-section');
    }

    if (sourceContent) {
      if (placeholder.dataset.replaceParent !== undefined) {
        section.innerHTML = sourceContent.innerHTML;
      } else {
        placeholder.innerHTML = sourceContent.innerHTML || sourceContent.outerHTML;
        placeholder.removeAttribute('data-deferred-content');
      }
      section.removeAttribute('data-deferred-section');
      if (typeof HTMLUpdateUtility !== 'undefined' && typeof HTMLUpdateUtility.setInnerHTML === 'function') {
        HTMLUpdateUtility.setInnerHTML(section, section.innerHTML); // Re-execute scripts
      }
    }
  }

  executeCallback(section) {
    const callbackName = section.dataset.callback;
    if (!callbackName) return;
    if (!DeferredSectionLoader.allowedCallbacks.has(callbackName)) return;

    const delay = parseInt(section.dataset.callbackDelay) || 0;
    const targetSelector = section.dataset.callbackTarget;
    
    const execute = () => {
      let target = section;
      if (targetSelector) {
        target = section.closest(targetSelector) || section.querySelector(targetSelector) || section;
      }
      if (!target) return;

      const isCustomElement = !!(target.tagName && target.tagName.includes('-'));
      const allowAttribute = target.hasAttribute && target.hasAttribute('data-allow-deferred-callback');
      if (!isCustomElement && !allowAttribute) return;

      const fn = target[callbackName];
      if (typeof fn === 'function') {
        fn.call(target);
      }
    };

    delay > 0 ? setTimeout(execute, delay) : requestAnimationFrame(execute);
  }

  static load(sectionElement) {
    window.deferredSectionLoader?.loadSection(sectionElement);
  }

  static clearCache(sectionId) {
    if (!window.deferredSectionLoader) return;
    sectionId ? window.deferredSectionLoader.cache.delete(sectionId) : window.deferredSectionLoader.cache.clear();
  }

  static onLoaded(sectionId, callback) {
    document.addEventListener('deferred:loaded', (e) => {
      if (!sectionId || e.detail.sectionId === sectionId) callback(e.detail);
    });
  }
}

window.DeferredSectionLoader = DeferredSectionLoader;
window.deferredSectionLoader = new DeferredSectionLoader();

document.addEventListener('shopify:section:load', () => {
  window.deferredSectionLoader?.init();
});

/*
  Spec chips glide through their overflow while the card is hovered and
  glide home on leave — the row hides its scrollbar, and horizontal wheel
  scrolling asks for a Shift the visitor doesn't know about. Hover-only:
  touch devices keep their native swipe. Delegated, because cards also
  arrive later via quick view, similar products and deferred sections.
*/
(() => {
  if (!window.matchMedia('(hover: hover)').matches) return;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const SPEED = 0.14; // px per ms
  const isRTL = document.documentElement.dir === 'rtl';

  const maxScroll = (el) => {
    const max = el.scrollWidth - el.clientWidth;
    return isRTL ? -max : max;
  };

  const glide = (el, target) => {
    cancelAnimationFrame(el.hlScrollFrame);
    if (reduced.matches) {
      el.scrollLeft = target;
      return;
    }
    const from = el.scrollLeft;
    const dist = target - from;
    if (!dist) return;
    const duration = Math.min(1800, Math.abs(dist) / SPEED);
    // The strip variant snaps by proximity; left on, every frame's write
    // gets yanked onto an item start. Off for the glide, back for the hand.
    el.style.scrollSnapType = 'none';
    let started;
    const step = (now) => {
      if (started === undefined) started = now;
      const p = Math.min(1, (now - started) / duration);
      const eased = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
      el.scrollLeft = from + dist * eased;
      if (p < 1) {
        el.hlScrollFrame = requestAnimationFrame(step);
      } else {
        el.style.scrollSnapType = '';
      }
    };
    el.hlScrollFrame = requestAnimationFrame(step);
  };

  const stripOf = (card) => card.querySelector('[data-hover-scroll]');

  document.addEventListener('mouseover', (e) => {
    const card = e.target.closest('.card-wrapper, .card-product, .product__highlights-block');
    if (!card || card.contains(e.relatedTarget)) return;
    const strip = stripOf(card);
    if (strip) glide(strip, maxScroll(strip));
  });

  document.addEventListener('mouseout', (e) => {
    const card = e.target.closest('.card-wrapper, .card-product, .product__highlights-block');
    if (!card || card.contains(e.relatedTarget)) return;
    const strip = stripOf(card);
    if (strip) glide(strip, 0);
  });
})();

/* WrenLife community: return to the topic or search you were reading.
 * Per-tab navigation metadata only: no post, reply, profile or photo content.
 */
;(() => {
  const basePath = '/community/my/wrenlife-trying-to-conceive';
  const cleanPath = path => path.replace(/\/+$/, '');
  if (cleanPath(location.pathname) !== basePath && !location.pathname.startsWith(`${basePath}/`)) return;
  const installKey = Symbol.for('wren.community.contextReturn');
  if (window[installKey]) return;
  const api = { restoringList: false };
  window[installKey] = api;

  const rootSelector = 'main.architechpro-mega-community-body';
  const postLinks = 'a.mc-card-post__title-link[href], a.mc-postable__time-link[href], a.wren-search-result[href]';
  const stateKey = 'wrenCommunityBrowseContext';
  const searchKey = 'wrenCommunitySearch';
  const transferKey = 'wren.community.browseTransfer.v1';
  const lifetime = 30 * 60 * 1000;
  const transferLifetime = 60 * 1000;
  const topicLabels = new Map([
    ['general', 'Welcome & Guidelines'], ['cycles-ovulation', 'TTC & Fertility'],
    ['pcos-trying-to-conceive', 'Hormones & PCOS'], ['fertility-testing-treatment', 'Fertility Testing & Treatment'],
    ['emotional-support', 'Emotional Support'], ['research-resources', 'Fertility Resources'],
    ['glp-1-weight-health', 'GLP-1 & Weight Health'], ['nutrition-everyday-wellbeing', 'Nutrition & Everyday Wellbeing'],
    ['faint-lines-test-photos', 'Faint Lines & Test Photos'], ['pregnancy-birth-stories', 'Pregnancy & Birth Stories'],
    ['baby-names', 'Baby Names']
  ]);
  const route = raw => {
    try {
      const url = new URL(raw, location.origin);
      if (url.origin !== location.origin || !/^https?:$/.test(url.protocol)) return null;
      url.pathname = cleanPath(url.pathname);
      url.hash = '';
      if (url.pathname === basePath) return { href: url.href, kind: 'list', topic: '' };
      if (!url.pathname.startsWith(`${basePath}/`)) return null;
      const parts = url.pathname.slice(basePath.length + 1).split('/');
      if (!topicLabels.has(parts[0])) return null;
      if (parts.length === 1) return { href: url.href, kind: 'list', topic: parts[0] };
      if (parts.length === 2 && parts[1] && parts[1] !== 'null') return { href: url.href, kind: 'thread', topic: parts[0] };
    } catch { /* Invalid or unrelated destinations keep native behavior. */ }
    return null;
  };
  const fresh = (at, limit) => Number.isFinite(at) && at <= Date.now() + 1000 && Date.now() - at <= limit;
  const hasSearch = query => Boolean(query.normalize('NFKC').replace(/[\p{P}\p{S}\s]+/gu, ' ').trim());
  const snapshot = value => {
    if (!value || value.version !== 1 || !fresh(value.at, lifetime)) return null;
    const list = route(value.href);
    const thread = route(value.anchor);
    if (list?.kind !== 'list' || thread?.kind !== 'thread') return null;
    if (typeof value.query !== 'string' || value.query.length > 255 || (list.topic && value.query)) return null;
    if (!Number.isFinite(value.y) || value.y < 0 || value.y > 1e7 || !Number.isFinite(value.offset) || Math.abs(value.offset) > 20000) return null;
    return { version: 1, at: value.at, href: list.href, query: value.query, y: value.y, anchor: new URL(thread.href).pathname, offset: value.offset };
  };
  const historyValue = () => history.state && typeof history.state === 'object' ? history.state : {};
  const saveState = (kind, value) => {
    try {
      history.replaceState({ ...historyValue(), [stateKey]: { kind, snapshot: value },
        ...(kind === 'list' ? { [searchKey]: value.query } : {}) }, '', location.href);
    } catch { /* Browsing remains usable when history storage is unavailable. */ }
  };
  const readTransfer = current => {
    let value;
    try {
      value = JSON.parse(sessionStorage.getItem(transferKey) || 'null');
      sessionStorage.removeItem(transferKey);
    } catch { return null; }
    const saved = snapshot(value?.snapshot);
    const from = route(value?.from);
    const target = route(value?.target);
    const referrer = route(activeHref || document.referrer);
    if (!saved || !from || !referrer || !fresh(value.at, transferLifetime) || target?.href !== current.href || from.href !== referrer.href) return null;
    if (current.kind === 'list' && saved.href !== current.href) return null;
    if (current.kind === 'thread' && new URL(current.href).pathname !== saved.anchor) return null;
    return saved;
  };
  const saveTransfer = (from, target, value) => {
    try { sessionStorage.setItem(transferKey, JSON.stringify({ at: Date.now(), from, target, snapshot: value })); }
    catch { /* The real return link remains available without restoration. */ }
  };
  const cardFor = link => link.closest('.wren-search-result, .mc-card') || link;
  let activeHref = '';
  let activeSnapshot = null;
  let restore = null;
  let timer = null;
  let queued = false;
  const clearListSnapshot = () => {
    if (route(location.href)?.kind !== 'list') return;
    const state = historyValue();
    if (state[stateKey]?.kind === 'list') {
      const next = { ...state };
      delete next[stateKey];
      try { history.replaceState(next, '', location.href); } catch {}
    }
    activeSnapshot = null;
  };
  const stopRestore = (consume = false) => {
    if (consume && restore) clearListSnapshot();
    restore = null;
    api.restoringList = false;
    window.clearTimeout(timer);
    timer = null;
  };
  const activate = (restoreHistory = false) => {
    const current = route(location.href);
    if (!current) { stopRestore(); activeHref = ''; activeSnapshot = null; return; }
    if (current.href === activeHref && !restoreHistory) return;
    const transferred = readTransfer(current);
    const savedState = historyValue()[stateKey];
    const saved = savedState?.kind === current.kind ? snapshot(savedState.snapshot) : null;
    const memory = current.href === activeHref ? snapshot(activeSnapshot) : null;
    let candidate = transferred || saved || memory;
    if (!transferred && current.kind === 'list' && !current.topic &&
        typeof historyValue()[searchKey] === 'string' && candidate?.query !== historyValue()[searchKey]) candidate = null;
    const matches = candidate && (current.kind === 'thread' ? candidate.anchor === new URL(current.href).pathname : candidate.href === current.href);
    activeSnapshot = matches ? candidate : null;
    activeHref = current.href;
    stopRestore();
    if (activeSnapshot) saveState(current.kind, activeSnapshot);
    if (current.kind === 'list' && activeSnapshot && (transferred || restoreHistory)) {
      restore = { snapshot: activeSnapshot, until: Date.now() + 8000, lastTarget: null, stable: 0 };
      api.restoringList = true;
    }
  };
  const returnLink = root => root.querySelector('.mc-post-show #wren-community-thread-nav > a:first-child');
  const syncLink = (root, current) => {
    if (current.kind !== 'thread') return;
    const link = returnLink(root);
    if (!link) return;
    const saved = snapshot(activeSnapshot);
    const list = saved && route(saved.href);
    const home = new URL(basePath, location.origin);
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview) home.searchParams.set('preview_theme_id', preview);
    const href = list?.href || home.href;
    const label = saved && hasSearch(saved.query) ? 'Back to search results' : list?.topic ? `Back to ${topicLabels.get(list.topic)}` : 'Back to topics';
    if (link.href !== href) link.href = href;
    if (link.textContent !== label) link.textContent = label;
  };
  const restorePosition = root => {
    const request = restore;
    if (!request) return;
    const expired = Date.now() >= request.until;
    const saved = request.snapshot;
    const list = route(saved.href);
    const input = root.querySelector('#wren-community-search-input');
    const ready = list.topic ? root.querySelector('.mc-community-hashtag') : root.querySelector('.mc-community-show__posts-feed');
    const searchStatus = root.querySelector('#wren-community-search .wren-search-status')?.textContent.trim();
    const searchReady = !hasSearch(saved.query) || (input?.value === saved.query && searchStatus && searchStatus !== 'Loading conversations…');
    if (ready && searchReady) {
      const link = Array.from(root.querySelectorAll(postLinks)).find(item => {
        const target = route(item.href);
        return target?.kind === 'thread' && new URL(target.href).pathname === saved.anchor && item.getClientRects().length;
      });
      const maxY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      const desired = link ? window.scrollY + cardFor(link).getBoundingClientRect().top - saved.offset : saved.y;
      const target = Math.max(0, Math.min(desired, maxY));
      if (link || expired) {
        if (Math.abs(window.scrollY - target) > 2) window.scrollTo({ top: target, behavior: 'instant' });
        const reachable = desired <= maxY + 2;
        request.stable = reachable && request.lastTarget !== null && Math.abs(request.lastTarget - target) <= 2 ? request.stable + 1 : 0;
        request.lastTarget = target;
        if (expired || request.stable >= 2) { stopRestore(true); return; }
      }
    }
    if (expired) { stopRestore(true); return; }
    window.clearTimeout(timer);
    timer = window.setTimeout(schedule, 120);
  };
  const sync = () => {
    activate();
    const current = route(location.href);
    const root = current && document.querySelector(rootSelector);
    if (!root) {
      if (restore && Date.now() > restore.until) stopRestore(true);
      if (restore) { window.clearTimeout(timer); timer = window.setTimeout(schedule, 120); }
      return;
    }
    syncLink(root, current);
    restorePosition(root);
  };
  const schedule = () => {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(() => { queued = false; sync(); });
  };
  document.addEventListener('click', event => {
    if (event.defaultPrevented || (typeof event.button === 'number' && event.button !== 0) || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target?.closest?.('a[href]');
    const root = document.querySelector(rootSelector);
    if (!link || !root?.contains(link) || link.hasAttribute('download')) return;
    const targetName = (link.getAttribute('target') || document.querySelector('base[target]')?.getAttribute('target') || '').toLowerCase();
    if (targetName && targetName !== '_self') return;
    const current = route(location.href);
    const target = route(link.href);
    if (!current || !target) return;
    if (current.kind === 'thread' && link === returnLink(root)) {
      const saved = snapshot(activeSnapshot);
      if (saved && saved.href === target.href) saveTransfer(current.href, target.href, saved);
      return;
    }
    if (current.kind !== 'list' || target.kind !== 'thread' || !link.matches(postLinks)) return;
    const url = new URL(target.href);
    const preview = new URL(current.href).searchParams.get('preview_theme_id');
    if (preview) { url.searchParams.set('preview_theme_id', preview); link.href = url.href; }
    const query = current.topic ? '' : (root.querySelector('#wren-community-search-input')?.value || '').slice(0, 255);
    const value = snapshot({ version: 1, at: Date.now(), href: current.href, query, y: Math.max(0, window.scrollY),
      anchor: url.pathname, offset: cardFor(link).getBoundingClientRect().top });
    if (!value) return;
    activeSnapshot = value;
    saveState('list', value);
    saveTransfer(current.href, url.href, value);
  }, true);
  const userInteracted = () => { stopRestore(true); clearListSnapshot(); };
  window.addEventListener('pointerdown', userInteracted, { capture: true, passive: true });
  window.addEventListener('wheel', userInteracted, { capture: true, passive: true });
  window.addEventListener('touchmove', userInteracted, { capture: true, passive: true });
  window.addEventListener('keydown', event => {
    if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ', 'Spacebar'].includes(event.key)) userInteracted();
  }, true);
  document.addEventListener('input', event => { if (event.target?.id === 'wren-community-search-input') userInteracted(); }, true);
  window.addEventListener('pagehide', () => stopRestore());
  window.addEventListener('popstate', () => { activate(true); schedule(); });
  window.addEventListener('pageshow', event => { if (event.persisted) activate(true); schedule(); });
  new MutationObserver(schedule).observe(document.documentElement, { childList: true, subtree: true, characterData: true });
  const navigation = window.performance?.getEntriesByType?.('navigation')[0];
  activate(navigation?.type === 'back_forward');
  sync();
})();


;(() => {
  const basePath = '/community/my/wrenlife-trying-to-conceive';
  const previewPath = '/community/my/preview/wrenlife-trying-to-conceive';
  const targetId = 'wren-community-posts';
  const linkSelector = '.mc-community-show__topics-nav a.mc-community-show__nav-item-inner';
  const headingSelector = 'h2.mc-community-show__feed-heading';
  const rootSelector = 'main.architechpro-mega-community-body';
  const badgeSelector = `${rootSelector} .mc-card-post__topic-mobile, ${rootSelector} .mc-card-badges__button`;
  const welcomeId = 'wren-community-welcome-link';
  const topics = [
    ['Welcome & Guidelines', 'general'],
    ['TTC & Fertility', 'cycles-ovulation'],
    ['Hormones & PCOS', 'pcos-trying-to-conceive'],
    ['Fertility Testing & Treatment', 'fertility-testing-treatment'],
    ['Emotional Support', 'emotional-support'],
    ['Fertility Resources', 'research-resources'],
    ['GLP-1 & Weight Health', 'glp-1-weight-health'],
    ['Nutrition & Everyday Wellbeing', 'nutrition-everyday-wellbeing'],
    ['Faint Lines & Test Photos', 'faint-lines-test-photos'],
    ['Pregnancy & Birth Stories', 'pregnancy-birth-stories'],
    ['Baby Names', 'baby-names']
  ];
  const normalize = value => value.normalize('NFKC').replace(/\s+/g, ' ').trim().toLowerCase();
  const slugByLabel = new Map(topics.map(([label, slug]) => [normalize(label), slug]));
  slugByLabel.set(normalize('Start Here'), 'general');
  const labelBySlug = new Map(topics.map(([label, slug]) => [slug, normalize(label)]));
  const context = () => {
    const url = new URL(window.location.href);
    const path = url.pathname.replace(/\/+$/, '');
    const preview = path === previewPath;
    if (preview && !url.searchParams.get('code')) return null;
    if (!preview && path !== basePath && !path.startsWith(`${basePath}/`)) return null;
    const topic = preview ? url.searchParams.get('filter') : path.slice(basePath.length + 1);
    return { url, preview, topic: labelBySlug.has(topic) ? topic : null };
  };
  if (!context()) return;
  const installationKey = Symbol.for('wren.community.navigation');
  if (window[installationKey]) return;
  window[installationKey] = true;
  const history = window.history;
  if (history && typeof history.pushState === 'function' && typeof history.replaceState === 'function') {
    const pushState = history.pushState;
    const replaceState = history.replaceState;
    history.pushState = function (...args) {
      if (this === history && args.length >= 2 && context() && (args[2] == null || typeof args[2] === 'string')) {
        let sameUrl = false;
        try {
          const href = args[2] == null ? window.location.href : new URL(args[2], document.baseURI || window.location.href).href;
          sameUrl = href === window.location.href;
        } catch {}
        if (sameUrl) return Reflect.apply(replaceState, this, args);
      }
      return Reflect.apply(pushState, this, args);
    };
  }
  let pendingScroll = null;
  let scrollTimer = null;
  let frameQueued = false;
  let welcomeLink = null;
  let labels = null;
  try {
    labels = JSON.parse(document.getElementById('wren-community-labels')?.textContent || 'null');
  } catch {}
  if (labels?.welcome_topic) slugByLabel.set(normalize(labels.welcome_topic), 'general');
  const renameWelcomeLabel = element => {
    if (!labels?.welcome_topic) return;
    element.childNodes.forEach(node => {
      if (node.nodeType === 3 && normalize(node.nodeValue || '') === normalize('Start Here')) {
        node.nodeValue = node.nodeValue.replace(/start\s+here/i, labels.welcome_topic);
      } else if (node.nodeType === 1) renameWelcomeLabel(node);
    });
  };
  const style = document.createElement('style');
  style.id = 'wren-community-topic-labels-style';
  style.textContent = `
    ${rootSelector} #${welcomeId} { display: flex; align-items: center; width: fit-content; max-width: 100%; min-height: 44px; margin: 0 0 10px; padding: 7px 0; color: #294e41; font: inherit; font-size: 14px; line-height: 1.4; text-decoration: underline; text-underline-offset: 3px; overflow-wrap: anywhere; }
    ${rootSelector} #${welcomeId}:focus-visible { outline: 2px solid #294e41; outline-offset: 3px; border-radius: 3px; }
    ${rootSelector} .mc-card-post__topic-mobile[data-wren-topic-prefix], ${rootSelector} .mc-card-badges__button[data-wren-topic-prefix] { min-height: 44px; max-width: 100%; height: auto; white-space: normal; overflow-wrap: anywhere; text-align: start; }
    ${rootSelector} [data-wren-topic-prefix]::before { content: attr(data-wren-topic-prefix); }
  `;
  document.head.append(style);
  const cancelScroll = () => {
    pendingScroll = null;
    if (scrollTimer !== null) window.clearTimeout(scrollTimer);
    scrollTimer = null;
  };
  const destination = (slug, current) => {
    const url = new URL(current.preview ? previewPath : `${basePath}/${slug}`, current.url.origin);
    if (current.preview) {
      url.searchParams.set('code', current.url.searchParams.get('code'));
      url.searchParams.set('filter', slug);
    }
    if (current.url.searchParams.has('preview_theme_id')) {
      url.searchParams.set('preview_theme_id', current.url.searchParams.get('preview_theme_id'));
    }
    return url;
  };
  const matchingHeading = slug => {
    const heading = document.querySelector(headingSelector);
    if (!heading || !heading.isConnected || slugByLabel.get(normalize(heading.textContent || '')) !== slug) return null;
    const rect = heading.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 ? heading : null;
  };
  const finishScroll = request => {
    if (pendingScroll !== request) return;
    const current = context();
    const heading = current && current.topic === request.slug ? matchingHeading(request.slug) : null;
    if (!heading) {
      request.queued = false;
      return;
    }
    heading.id = targetId;
    heading.style.scrollMarginTop = '96px';
    if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
    cancelScroll();
    try {
      heading.focus({ preventScroll: true });
    } catch {}
    heading.scrollIntoView({ block: 'start', behavior: 'instant' });
  };
  const sync = () => {
    const current = context();
    if (!current) {
      cancelScroll();
      return;
    }
    const root = document.querySelector(rootSelector);
    if (!root) return;
    root.querySelectorAll(`${linkSelector}, .mc-card-post__topic-mobile, .mc-card-badges__button, ${headingSelector}, .mc-page__header-wrapper`).forEach(renameWelcomeLabel);
    root.querySelectorAll(linkSelector).forEach(link => {
      const slug = slugByLabel.get(normalize(link.textContent || ''));
      if (!slug) return;
      const href = destination(slug, current).href;
      if (link.getAttribute('href') !== href) link.setAttribute('href', href);
      if (link.getAttribute('target') !== '_self') link.setAttribute('target', '_self');
    });
    if (labels?.topic_prefix) root.querySelectorAll('.mc-card-post__topic-mobile, .mc-card-badges__button').forEach(button => {
      const label = (button.textContent || '').replace(/\s+/g, ' ').trim();
      const slug = slugByLabel.get(normalize(label));
      if (!slug) return;
      if (button.tagName === 'A') {
        const href = destination(slug, current).href;
        if (button.getAttribute('href') !== href) button.setAttribute('href', href);
        if (button.getAttribute('target') !== '_self') button.setAttribute('target', '_self');
      }
      const prefix = `${labels.topic_prefix} `;
      const accessibleLabel = `${prefix}${label}`;
      if (button.getAttribute('data-wren-topic-prefix') !== prefix) button.setAttribute('data-wren-topic-prefix', prefix);
      if (button.getAttribute('aria-label') !== accessibleLabel) button.setAttribute('aria-label', accessibleLabel);
      if (button.tagName === 'BUTTON' && button.getAttribute('type') !== 'button') button.setAttribute('type', 'button');
    });
    const header = root.querySelector('.mc-page__header-wrapper');
    if (header && labels?.welcome_link) {
      if (!welcomeLink?.isConnected) {
        welcomeLink = document.createElement('a');
        welcomeLink.id = welcomeId;
        welcomeLink.textContent = labels.welcome_link;
        welcomeLink.target = '_self';
        header.after(welcomeLink);
      }
      const href = destination('general', current).href;
      if (welcomeLink.getAttribute('href') !== href) welcomeLink.setAttribute('href', href);
      if (header.nextElementSibling !== welcomeLink) header.after(welcomeLink);
    }
    if (!current.topic) return;
    const request = pendingScroll;
    if (!request || request.slug !== current.topic || request.queued) return;
    const heading = matchingHeading(current.topic);
    if (!heading) return;
    request.queued = true;
    window.requestAnimationFrame(() => window.requestAnimationFrame(() => finishScroll(request)));
  };
  const scheduleSync = () => {
    if (frameQueued) return;
    frameQueued = true;
    window.requestAnimationFrame(() => {
      frameQueued = false;
      sync();
    });
  };
  const requestScroll = slug => {
    cancelScroll();
    pendingScroll = { slug, queued: false };
    scrollTimer = window.setTimeout(cancelScroll, 60000);
    scheduleSync();
  };
  document.addEventListener('click', event => {
    if ((typeof event.button === 'number' && event.button !== 0) || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const target = event.target instanceof Element ? event.target : event.target?.parentElement;
    const link = target?.closest(`${linkSelector}, ${badgeSelector}, #${welcomeId}`);
    if (!link) return;
    const current = context();
    const slug = link.id === welcomeId ? 'general' : slugByLabel.get(normalize(link.textContent || ''));
    if (!current || !slug) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (current.topic === slug) {
      requestScroll(slug);
      return;
    }
    cancelScroll();
    window.location.assign(destination(slug, current).href);
  }, true);
  const observer = new MutationObserver(scheduleSync);
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
    characterData: true
  });
  window.addEventListener('pagehide', cancelScroll);
  window.addEventListener('wheel', cancelScroll, { capture: true, passive: true });
  window.addEventListener('touchmove', cancelScroll, { capture: true, passive: true });
  const scrollKeys = new Set(['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'PageUp', 'PageDown', 'Home', 'End', ' ', 'Spacebar']);
  window.addEventListener('keydown', event => {
    if (scrollKeys.has(event.key)) cancelScroll();
  }, true);
  window.addEventListener('pageshow', event => {
    if (event.persisted) cancelScroll();
    scheduleSync();
  });
  const navigation = window.performance?.getEntriesByType?.('navigation')[0];
  const restoringHistory = navigation?.type === 'back_forward' || window.performance?.navigation?.type === 2;
  const current = context();
  if (current.topic && !restoringHistory && !window[Symbol.for('wren.community.contextReturn')]?.restoringList) requestScroll(current.topic);
  sync();
})();

/* WrenLife community: compact mobile navigation, October 2026. */
;(() => {
  const basePath = '/community/my/wrenlife-trying-to-conceive';
  const previewPath = '/community/my/preview/wrenlife-trying-to-conceive';
  const pageUrl = new URL(window.location.href);
  const path = pageUrl.pathname.replace(/\/+$/, '');
  const isPreview = path === previewPath && Boolean(pageUrl.searchParams.get('code'));
  if (!isPreview && path !== basePath && !path.startsWith(`${basePath}/`)) return;
  const installKey = Symbol.for('wren.community.mobile');
  if (window[installKey]) return;
  window[installKey] = true;

  const mobile = window.matchMedia('(max-width: 749px)');
  const rootSelector = 'main.architechpro-mega-community-body .mc-community-show';
  const topicSelector = '.mc-community-show__topics-nav a.mc-community-show__nav-item-inner';
  const normalized = value => (value || '').normalize('NFKC').replace(/\s+/g, ' ').trim();
  const homeUrl = new URL(isPreview ? previewPath : basePath, pageUrl.origin);
  if (isPreview) homeUrl.searchParams.set('code', pageUrl.searchParams.get('code'));
  if (pageUrl.searchParams.has('preview_theme_id')) homeUrl.searchParams.set('preview_theme_id', pageUrl.searchParams.get('preview_theme_id'));

  const style = document.createElement('style');
  style.id = 'wren-community-mobile-style';
  style.textContent = `
    #wren-community-mobile-top, #wren-community-mobile-dock, .wren-community-mobile-title { display: none; }
    @media (max-width: 749px) {
      html.wren-community-mobile-ready main.architechpro-mega-community-body .mc-community-show > .mc-page { padding-left: 16px; padding-right: 16px; }
      html.wren-community-mobile-ready main.architechpro-mega-community-body .mc-community-show .mc-page__header-wrapper { margin-bottom: 12px; }
      html.wren-community-mobile-ready main.architechpro-mega-community-body .mc-community-show .mc-page__header-wrapper h1 { font-size: 24px; line-height: 1.2; }
      html.wren-community-mobile-ready main.architechpro-mega-community-body .mc-community-show .mc-page__header-wrapper h1.wren-community-mobile-heading nav.mc-breadcrumbs { display: none; }
      html.wren-community-mobile-ready main.architechpro-mega-community-body .mc-community-show .mc-page__header-wrapper h1.wren-community-mobile-heading > .wren-community-mobile-title { display: block; }
      html.wren-community-mobile-ready.wren-community-mobile-has-ask main.architechpro-mega-community-body .mc-community-show .mc-page__header-primary-button { display: none; }
      html.wren-community-mobile-ready main.architechpro-mega-community-body .mc-community-show .mc-layout__section--one-third { display: none; }
      html.wren-community-mobile-ready main.architechpro-mega-community-body .mc-community-show .mc-layout__section--one-third.wren-community-mobile-search-open,
      html.wren-community-mobile-ready main.architechpro-mega-community-body .wren-community-mobile-search-open .wren-community-mobile-search-path { display: block !important; }
      html.wren-community-mobile-ready main.architechpro-mega-community-body .wren-community-mobile-search-open .wren-community-mobile-search-path:not(.wren-community-mobile-search-item) > :not(.wren-community-mobile-search-path),
      html.wren-community-mobile-ready main.architechpro-mega-community-body .wren-community-mobile-search-open > :not(.wren-community-mobile-search-path) { display: none !important; }
      html.wren-community-mobile-ready main.architechpro-mega-community-body .wren-community-mobile-search-open { margin-bottom: 16px; }
      html.wren-community-mobile-ready main.architechpro-mega-community-body .wren-community-mobile-search-item input.mc-community-show__nav-item-textfield { min-height: 44px; font-size: 16px; }
      html.wren-community-mobile-ready main.architechpro-mega-community-body .wren-community-mobile-search-item button.mc-community-show__search-button { min-width: 44px; min-height: 44px; }
      html.wren-community-mobile-ready main.architechpro-mega-community-body .mc-community-show .mc-community-banner-wrapper { max-height: 72px; overflow: hidden; border-radius: 12px; }
      html.wren-community-mobile-ready main.architechpro-mega-community-body .mc-community-show img.mc-community-show__featured-image { display: block; width: 100%; height: 72px; max-height: 72px; object-fit: cover; }
      html.wren-community-mobile-ready main.architechpro-mega-community-body { padding-bottom: calc(88px + env(safe-area-inset-bottom, 0px)); }
      html.wren-community-mobile-ready mobile-dock { display: none !important; }
      html.wren-community-mobile-ready #wren-community-mobile-top { display: block; margin: 0 0 16px; color: #243832; font-family: inherit; }
      #wren-community-mobile-top *, #wren-community-mobile-dock * { box-sizing: border-box; }
      #wren-community-mobile-top .wren-community-mobile-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
      #wren-community-mobile-top .wren-community-mobile-back, #wren-community-mobile-top .wren-community-mobile-search { min-height: 44px; display: inline-flex; align-items: center; gap: 7px; padding: 8px 0; border: 0; border-radius: 6px; background: transparent; color: #243832; font: inherit; font-size: 14px; font-weight: 600; line-height: 1.35; text-decoration: none; cursor: pointer; }
      #wren-community-mobile-top .wren-community-mobile-search { padding-left: 8px; padding-right: 8px; }
      #wren-community-mobile-top svg, #wren-community-mobile-dock svg { width: 20px; height: 20px; flex-shrink: 0; fill: none; stroke: currentColor; stroke-width: 1.8; stroke-linecap: round; stroke-linejoin: round; }
      #wren-community-mobile-topic-picker { border: 1px solid #cbd7cf; border-radius: 12px; background: #f6f8f3; scroll-margin-top: 96px; overflow: hidden; }
      #wren-community-mobile-topic-picker > summary { min-height: 52px; display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 12px 14px; color: #243832; cursor: pointer; list-style: none; font: inherit; font-size: 16px; font-weight: 650; line-height: 1.35; }
      #wren-community-mobile-topic-picker > summary::-webkit-details-marker { display: none; }
      #wren-community-mobile-topic-picker[open] > summary svg { transform: rotate(180deg); }
      #wren-community-mobile-topic-picker nav { display: grid; max-height: min(60vh, 420px); padding: 0 8px 8px; overflow-y: auto; overscroll-behavior: contain; }
      #wren-community-mobile-topic-picker nav a { min-height: 48px; display: flex; align-items: center; padding: 11px 10px; border-radius: 8px; color: #243832; font-size: 15px; line-height: 1.4; text-decoration: none; overflow-wrap: anywhere; }
      #wren-community-mobile-topic-picker nav a:hover, #wren-community-mobile-topic-picker nav a[aria-current="page"] { background: #e4ece2; font-weight: 650; }
      html.wren-community-mobile-ready #wren-community-mobile-dock { display: grid; grid-template-columns: 1fr 1.4fr 1fr; align-items: stretch; gap: 4px; position: fixed; z-index: 30; left: 0; right: 0; bottom: 0; padding: 7px 8px calc(7px + env(safe-area-inset-bottom, 0px)); border-top: 1px solid #d6dfd6; background: #fffdf8; box-shadow: 0 -3px 18px #2438320c; color: #243832; font-family: inherit; }
      #wren-community-mobile-dock > a, #wren-community-mobile-dock > button { min-width: 0; min-height: 54px; display: flex; flex-direction: column; justify-content: center; align-items: center; gap: 3px; padding: 5px 4px; border: 0; border-radius: 10px; background: transparent; color: inherit; font: inherit; font-size: 12px; font-weight: 600; line-height: 1.25; text-align: center; text-decoration: none; cursor: pointer; }
      #wren-community-mobile-dock > .wren-community-mobile-ask { background: #294e41; color: #ffffff; }
      #wren-community-mobile-dock > button:disabled { opacity: .6; cursor: default; }
      #wren-community-mobile-top [hidden] { display: none !important; }
      #wren-community-mobile-top a:focus-visible, #wren-community-mobile-top button:focus-visible, #wren-community-mobile-topic-picker summary:focus-visible, #wren-community-mobile-dock > :focus-visible { outline: 2px solid #294e41; outline-offset: -3px; }
      html.wren-community-mobile-editing #wren-community-mobile-dock { display: none; }
      html.wren-community-mobile-modal #wren-community-mobile-dock { display: none; }
    }
  `;
  (document.head || document.documentElement).append(style);

  const icon = name => {
    const paths = {
      back: '<path d="m12 5-7 7 7 7M5 12h14"/>',
      search: '<circle cx="10.5" cy="10.5" r="6.5"/><path d="m16 16 4 4"/>',
      chevron: '<path d="m6 9 6 6 6-6"/>',
      topics: '<rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/>',
      ask: '<path d="M21 11.5a8.5 8.5 0 0 1-8.5 8.5H3l2.4-4.7A8.5 8.5 0 1 1 21 11.5Z"/><path d="M12 7v8m-4-4h8"/>',
      account: '<circle cx="12" cy="8" r="4"/><path d="M4 21v-2a8 8 0 0 1 16 0v2"/>'
    };
    return `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">${paths[name]}</svg>`;
  };
  let top;
  let dock;
  let picker;
  let topicList;
  let searchButton;
  let askButton;
  let accountLink;
  let topicsButton;
  let topicsSignature = '';
  let queued = false;
  let modalWasOpen = false;
  let lastLauncher = null;
  let searchOpen = false;
  let searchWasShown = false;
  let pendingSearchFocus = false;

  const nativePrimary = () => {
    const button = document.querySelector(`${rootSelector} .mc-page__header-primary-button`);
    return button && !button.disabled && /^(log\s*in|sign\s*in|create\s+(a\s+)?post|new\s+post|add\s+(a\s+)?post|write\s+(a\s+)?post|ask\s+a\s+question)$/i.test(normalized(button.textContent)) ? button : null;
  };
  const nativeComposer = () => {
    const input = document.querySelector(`${rootSelector} .mc-community-show__post-form > .mc-input-form > .mc-input-form__inner > .mc-textarea-wrapper > div:not(.mc-input-form__title) > textarea.mc-input-form__textarea`);
    if (!input || input.disabled || input.readOnly || input.closest('[hidden], [aria-hidden="true"], [inert]')) return null;
    const rect = input.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 ? input : null;
  };
  const nativeSearch = () => document.querySelector(`${rootSelector} #mc-community-search-nav-item button.mc-community-show__nav-item-inner`);
  const nativeSearchInput = () => document.querySelector(`${rootSelector} input.mc-community-show__nav-item-textfield`);
  const usableTopicUrl = raw => {
    try {
      const url = new URL(raw, pageUrl);
      const topicPath = url.pathname.replace(/\/+$/, '');
      if (url.origin !== pageUrl.origin || !['http:', 'https:'].includes(url.protocol)) return null;
      if (isPreview) return topicPath === previewPath && url.searchParams.get('code') === pageUrl.searchParams.get('code') && url.searchParams.has('filter') ? url : null;
      return topicPath.startsWith(`${basePath}/`) ? url : null;
    } catch { return null; }
  };
  const currentTopicKey = url => isPreview ? url.searchParams.get('filter') || '' : url.pathname.replace(/\/+$/, '');
  const getTopics = root => {
    const dedup = new Map();
    root.querySelectorAll(topicSelector).forEach(link => {
      const label = normalized(link.textContent);
      const url = usableTopicUrl(link.getAttribute('href') || '');
      if (label && url && !dedup.has(label.toLowerCase())) dedup.set(label.toLowerCase(), { label, url });
    });
    return Array.from(dedup.values());
  };
  const accountHref = () => {
    const link = Array.from(document.querySelectorAll('mobile-dock a.dock__item')).find(item => /^(my\s+)?account$/i.test(normalized(item.textContent)));
    try {
      const url = new URL(link?.getAttribute('href') || '/pages/member-home', pageUrl.origin);
      return /^https?:$/.test(url.protocol) ? url.href : new URL('/pages/member-home', pageUrl.origin).href;
    } catch { return new URL('/pages/member-home', pageUrl.origin).href; }
  };
  const showTopics = () => {
    if (!picker) return;
    picker.open = true;
    picker.querySelector('summary').focus({ preventScroll: true });
    picker.scrollIntoView({ block: 'start', behavior: 'instant' });
  };
  const createControls = layout => {
    top = document.createElement('div');
    top.id = 'wren-community-mobile-top';
    top.innerHTML = `<div class="wren-community-mobile-row"><a class="wren-community-mobile-back">${icon('back')}<span>Back to community</span></a><button type="button" class="wren-community-mobile-search">${icon('search')}<span>Search</span></button></div><details id="wren-community-mobile-topic-picker"><summary>Choose a topic${icon('chevron')}</summary><nav aria-label="Community topics"></nav></details>`;
    const back = top.querySelector('a');
    back.href = homeUrl.href;
    back.target = '_self';
    picker = top.querySelector('details');
    topicList = picker.querySelector('nav');
    topicList.addEventListener('click', event => {
      if ((typeof event.button === 'number' && event.button !== 0) || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest('a');
      if (!link || !topicList.contains(link)) return;
      const nativeLink = Array.from(document.querySelectorAll(`${rootSelector} ${topicSelector}`)).find(item => item.href === link.href && normalized(item.textContent) === normalized(link.textContent));
      if (!nativeLink) return;
      event.preventDefault();
      picker.open = false;
      nativeLink.click();
    });
    searchButton = top.querySelector('button');
    searchButton.setAttribute('aria-expanded', 'false');
    searchButton.addEventListener('click', () => {
      picker.open = false;
      searchOpen = !searchOpen;
      pendingSearchFocus = searchOpen;
      if (searchOpen && !nativeSearchInput()) {
        searchWasShown = false;
        nativeSearch()?.click();
      }
      if (!searchOpen) searchButton.focus({ preventScroll: true });
      schedule();
    });
    picker.addEventListener('keydown', event => {
      if (event.key === 'Escape' && picker.open) {
        event.preventDefault();
        picker.open = false;
        picker.querySelector('summary').focus();
      }
    });
    layout.before(top);

    dock = document.createElement('nav');
    dock.id = 'wren-community-mobile-dock';
    dock.setAttribute('aria-label', 'Community shortcuts');
    dock.innerHTML = `<button type="button" aria-controls="wren-community-mobile-topic-picker">${icon('topics')}<span>Topics</span></button><button type="button" class="wren-community-mobile-ask">${icon('ask')}<span>Ask a question</span></button><a target="_self">${icon('account')}<span>My account</span></a>`;
    topicsButton = dock.querySelector('button');
    topicsButton.setAttribute('aria-expanded', 'false');
    topicsButton.addEventListener('click', showTopics);
    picker.addEventListener('toggle', () => topicsButton.setAttribute('aria-expanded', String(picker.open)));
    askButton = dock.querySelector('.wren-community-mobile-ask');
    askButton.addEventListener('click', () => {
      picker.open = false;
      const composer = nativeComposer();
      if (composer) {
        lastLauncher = null;
        composer.focus({ preventScroll: true });
        composer.scrollIntoView({ block: 'center', behavior: 'instant' });
      } else {
        lastLauncher = askButton;
        nativePrimary()?.click();
      }
      schedule();
    });
    accountLink = dock.querySelector('a');
    document.body.append(dock);
  };
  const isEditing = () => {
    const active = document.activeElement;
    return Boolean(active && (active.matches('input:not([type="button"]):not([type="submit"]):not([type="checkbox"]):not([type="radio"]):not([type="hidden"]), textarea, select') || active.isContentEditable));
  };
  const updateEditing = () => document.documentElement.classList.toggle('wren-community-mobile-editing', mobile.matches && isEditing());
  const updateSearch = root => {
    const input = nativeSearchInput();
    const item = input?.closest('li#mc-community-search-nav-item');
    const sidebar = item?.closest('.mc-layout__section--one-third');
    if (searchOpen && searchWasShown && !input) searchOpen = false;
    root.querySelectorAll('.wren-community-mobile-search-open, .wren-community-mobile-search-path, .wren-community-mobile-search-item').forEach(node => {
      node.classList.remove('wren-community-mobile-search-open', 'wren-community-mobile-search-path', 'wren-community-mobile-search-item');
    });
    if (searchOpen && sidebar && item) {
      searchWasShown = true;
      if (!input.hasAttribute('aria-label') && !input.hasAttribute('aria-labelledby')) input.setAttribute('aria-label', 'Search community');
      const submit = item.querySelector('button.mc-community-show__search-button');
      if (submit && !submit.hasAttribute('aria-label') && !submit.hasAttribute('aria-labelledby')) submit.setAttribute('aria-label', 'Search community');
      sidebar.classList.add('wren-community-mobile-search-open');
      item.classList.add('wren-community-mobile-search-item');
      for (let node = item; node && node !== sidebar; node = node.parentElement) node.classList.add('wren-community-mobile-search-path');
      if (pendingSearchFocus) {
        pendingSearchFocus = false;
        window.requestAnimationFrame(() => { if (searchOpen && mobile.matches && input.isConnected) input.focus({ preventScroll: true }); });
      }
    }
    const label = searchOpen ? 'Close search' : 'Search';
    const text = searchButton.querySelector('span');
    if (text.textContent !== label) text.textContent = label;
    searchButton.setAttribute('aria-expanded', String(searchOpen));
  };
  const updateModal = () => {
    const modal = Array.from(document.querySelectorAll('[role="dialog"][aria-modal="true"], dialog[open]')).some(item => {
      const rect = item.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    });
    document.documentElement.classList.toggle('wren-community-mobile-modal', modal);
    if (modalWasOpen && !modal) {
      const launcher = lastLauncher;
      lastLauncher = null;
      if (launcher?.isConnected && mobile.matches) window.requestAnimationFrame(() => {
        const active = document.activeElement;
        const rect = active?.getBoundingClientRect();
        if (!active || active === document.body || !rect?.width || !rect?.height) launcher.focus({ preventScroll: true });
      });
    }
    modalWasOpen = modal;
  };
  const sync = () => {
    const root = document.querySelector(rootSelector);
    const layout = root?.querySelector('.mc-layout');
    const topics = root ? getTopics(root) : [];
    if (!layout || !topics.length) {
      document.documentElement.classList.remove('wren-community-mobile-ready');
      document.documentElement.classList.remove('wren-community-mobile-has-ask');
      return;
    }
    if (!top?.isConnected || !dock?.isConnected) {
      top?.remove();
      dock?.remove();
      topicsSignature = '';
      createControls(layout);
    }
    const title = root.querySelector('.mc-page__header-wrapper h1');
    if (title?.querySelector('nav.mc-breadcrumbs')) {
      if (!title.querySelector(':scope > .wren-community-mobile-title')) {
        const label = document.createElement('span');
        label.className = 'wren-community-mobile-title';
        label.textContent = 'WrenLife Community';
        title.append(label);
      }
      title.classList.add('wren-community-mobile-heading');
    }
    const signature = topics.map(({ label, url }) => `${label}\n${url.href}`).join('\n');
    if (signature !== topicsSignature) {
      topicsSignature = signature;
      const fragment = document.createDocumentFragment();
      topics.forEach(({ label, url }) => {
        const link = document.createElement('a');
        link.textContent = label;
        link.href = url.href;
        link.target = '_self';
        if (currentTopicKey(url) === currentTopicKey(pageUrl)) link.setAttribute('aria-current', 'page');
        fragment.append(link);
      });
      topicList.replaceChildren(fragment);
    }
    const hideSearch = !nativeSearch() && !nativeSearchInput();
    if (searchButton.hidden !== hideSearch) searchButton.hidden = hideSearch;
    updateSearch(root);
    const hasAsk = Boolean(nativeComposer() || nativePrimary());
    if (askButton.disabled !== !hasAsk) askButton.disabled = !hasAsk;
    document.documentElement.classList.toggle('wren-community-mobile-has-ask', hasAsk);
    const href = accountHref();
    if (accountLink.href !== href) accountLink.href = href;
    document.documentElement.classList.add('wren-community-mobile-ready');
    updateEditing();
    updateModal();
  };
  const schedule = () => {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(() => { queued = false; sync(); });
  };
  const observer = new MutationObserver(schedule);
  observer.observe(document.documentElement, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['open', 'hidden', 'disabled', 'readonly', 'inert', 'aria-hidden', 'aria-modal'] });
  document.addEventListener('focusin', updateEditing, true);
  document.addEventListener('focusout', () => window.requestAnimationFrame(updateEditing), true);
  document.addEventListener('keydown', event => {
    if (event.key === 'Escape' && mobile.matches && searchOpen && event.target.closest?.('.wren-community-mobile-search-open')) {
      event.preventDefault();
      searchOpen = false;
      pendingSearchFocus = false;
      searchButton.focus({ preventScroll: true });
      schedule();
    }
  });
  window.addEventListener('pageshow', schedule);
  mobile.addEventListener('change', () => { if (!mobile.matches && picker) picker.open = false; updateEditing(); });
  sync();
})();

/* WrenLife community: readable mobile posts, October 2026. */
;(() => {
  const basePath = '/community/my/wrenlife-trying-to-conceive';
  const previewPath = '/community/my/preview/wrenlife-trying-to-conceive';
  const pageUrl = new URL(window.location.href);
  const path = pageUrl.pathname.replace(/\/+$/, '');
  const isPreview = path === previewPath && Boolean(pageUrl.searchParams.get('code'));
  if (!isPreview && path !== basePath && !path.startsWith(`${basePath}/`)) return;

  const installKey = Symbol.for('wren.community.readability');
  if (window[installKey]) return;
  window[installKey] = true;

  // CSS applies to later app renders without changing React content or handlers.
  const scope = 'html.wren-community-reading main.architechpro-mega-community-body :is(.mc-community-show, .mc-community-hashtag, .mc-post-show)';
  const card = `${scope} .mc-card:has(.mc-card-post__body)`;
  const style = document.createElement('style');
  style.id = 'wren-community-reading-style';
  style.textContent = `
    @media (max-width: 749px) {
      ${card} {
        padding: 16px;
        border: 1px solid #c5d0c8;
        border-radius: 14px;
        background: #ffffff;
        box-shadow: 0 2px 7px #24383208;
      }

      ${card} .mc-post-card__top {
        gap: 10px;
      }
      ${card} .mc-post-card__title-wrapper {
        min-width: 0;
      }
      ${card} .mc-postable-card__name-link {
        color: #293c33;
        font-size: 14px;
        font-weight: 600;
        line-height: 1.4;
        overflow-wrap: anywhere;
      }
      ${card} .mc-postable__time-link {
        color: #52615a;
        font-size: 13px;
        line-height: 1.4;
      }

      ${card} .mc-card-post__title-link {
        display: block;
        min-height: 44px;
        padding: 4px 0;
        color: #243832;
        text-decoration: none;
      }
      ${card} .mc-card-post__title {
        margin: 0;
        color: #243832;
        font-size: 19px;
        font-weight: 650;
        line-height: 1.3;
        overflow-wrap: anywhere;
      }
      ${card} .mc-cart-post__text-content {
        color: #293c33 !important;
        font-size: 16px;
        line-height: 1.55;
        overflow-wrap: anywhere;
      }
      ${card} .mc-cart-post__text-content :is(p, li, span, a) {
        font-size: inherit;
        line-height: inherit;
      }
      ${card} .mc-cart-post__text-content a {
        color: #294e41 !important;
        text-decoration: underline;
        text-decoration-thickness: 1px;
        text-underline-offset: 3px;
      }

      ${card} :is(.mc-card-post__view-more, .mc-card-post__view-more--expanded) {
        display: flex;
        align-items: center;
        justify-content: center;
        width: max-content;
        max-width: 100%;
        min-width: 44px;
        min-height: 44px;
        margin-top: 8px;
        padding: 10px 12px;
        border-radius: 8px;
        color: #294e41;
        font-size: 14px;
        font-weight: 600;
        line-height: 1.4;
        text-transform: capitalize;
        text-decoration: underline;
        text-underline-offset: 3px;
      }

      ${card} .mc-card-post__bottom {
        flex-wrap: wrap;
        gap: 8px;
        min-height: 53px;
        padding-top: 8px;
        border-top: 1px solid #e2e8e2;
      }
      ${card} :is(.mc-like-button, .mc-comment-button) {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 7px;
        min-width: 44px;
        min-height: 44px;
        padding: 10px 12px;
        border-radius: 8px;
        font-size: 14px;
        line-height: 1.4;
      }
      ${card} :is(.mc-like-button__text, .mc-comment-button__text) {
        font-size: 14px;
        line-height: 1.4;
      }

      ${card} .mc-card-post__topic-mobile {
        min-height: 44px;
        max-width: 100%;
        padding: 10px 12px;
        border: 1px solid #cbd6c9;
        border-radius: 24px;
        background: #edf2e9;
        color: #334d3f;
        font-size: 13px;
        line-height: 1.4;
        text-align: left;
        white-space: normal;
        overflow-wrap: anywhere;
      }
      ${card} :is(.mc-card-post__title-link, .mc-card-post__view-more, .mc-card-post__view-more--expanded, .mc-like-button, .mc-comment-button, .mc-card-post__topic-mobile):focus-visible {
        outline: 2px solid #294e41;
        outline-offset: 3px;
      }

      ${card} :is(.mc-card-post__text-content-wrapper, .mc-comment-card__title-wrapper, .mc-postable-card__left) {
        min-width: 0;
        overflow-wrap: anywhere;
      }
      ${card} .mc-postable-card__name-link > span {
        font-size: inherit;
        line-height: inherit;
        color: inherit;
      }
      ${card} .mc-comment__list > .mc-comment-card {
        min-width: 0;
      }
      ${card} .mc-comment-card > .mc-comment__body {
        margin-left: 0;
        margin-inline-start: 0;
      }
      ${card} .mc-comment-card > .mc-comment__body .mc-comment {
        margin-inline-start: 12px;
        border-inline-start: 2px solid #d8e2d8;
        padding-inline-start: 10px;
      }
      ${card} .mc-comment__body > .mc-block-stack > .mc-inline-stack {
        flex-wrap: wrap;
        gap: 8px;
      }
      ${card} :is(.mc-comment-card__date, .mc-comment-card__date .mc-postable__time-text) {
        color: #52615a;
        font-size: 13px;
        line-height: 1.4;
      }
      ${card} .mc-comment__list > .mc-comment-card + .mc-comment-card {
        margin-block-start: 16px;
        padding-block-start: 16px;
        border-block-start: 1px solid #e2e8e2;
      }
    }
  `;
  (document.head || document.documentElement).append(style);
  document.documentElement.classList.add('wren-community-reading');
})();


;(() => {
  const basePath = '/community/my/wrenlife-trying-to-conceive';
  const previewPath = '/community/my/preview/wrenlife-trying-to-conceive';
  const rootSelector = 'main.architechpro-mega-community-body .mc-community-show';
  const topicSelector = '.mc-community-show__topics-nav a.mc-community-show__nav-item-inner';
  const homeClass = 'wren-community-directory-home';
  const directoryId = 'wren-community-topic-directory';
  const backId = 'wren-community-directory-back';
  const topicBackId = 'wren-community-topic-back';
  const topicRootSelector = 'main.architechpro-mega-community-body .mc-community-hashtag';
  const normalized = value => (value || '').normalize('NFKC').replace(/\s+/g, ' ').trim();
  const key = value => normalized(value).toLowerCase();
  const context = () => {
    const url = new URL(window.location.href);
    const path = url.pathname.replace(/\/+$/, '');
    const preview = path === previewPath && Boolean(url.searchParams.get('code'));
    if (!preview && path !== basePath && !path.startsWith(`${basePath}/`)) return null;
    const homeUrl = new URL(preview ? previewPath : basePath, url.origin);
    if (preview) homeUrl.searchParams.set('code', url.searchParams.get('code'));
    if (url.searchParams.has('preview_theme_id')) homeUrl.searchParams.set('preview_theme_id', url.searchParams.get('preview_theme_id'));
    return { url, preview, homeUrl, homePath: preview ? !url.searchParams.get('filter') : path === basePath };
  };
  if (!context()) return;
  const installationKey = Symbol.for('wren.community.directory');
  if (window[installationKey]) return;
  window[installationKey] = true;

  const descriptions = new Map([
    ['ttc & fertility', 'Cycle tracking, ovulation questions, and the two-week wait.'],
    ['faint lines & test photos', 'Share your test photo and talk through the uncertainty together.'],
    ['hormones & pcos', 'Talk about cycles, symptoms, and everyday life with PCOS.'],
    ['fertility testing & treatment', 'Questions and shared experiences with testing and treatment.'],
    ['emotional support', 'A place to vent, find encouragement, or simply feel heard.'],
    ['pregnancy & birth stories', 'Pregnancy conversations and birth stories, in your own words.'],
    ['baby names', 'Share your shortlist, swap ideas, and find a name you love.'],
    ['fertility resources', 'Share helpful reading and ask about information you have found.'],
    ['glp-1 & weight health', 'Everyday experiences with weight health and GLP-1 support.'],
    ['nutrition & everyday wellbeing', 'Food, sleep, movement, and the routines that work for you.'],
    ['welcome & guidelines', 'Meet the community and learn how to make yourself at home.'],
    ['start here', 'Meet the community and learn how to make yourself at home.']
  ]);
  const order = new Map(Array.from(descriptions.keys()).map((label, index) => [label, index]));
  order.set('welcome & guidelines', -2);
  order.set('start here', -1);
  const style = document.createElement('style');
  style.id = 'wren-community-directory-style';
  style.textContent = `
    #${directoryId}, #${backId} { display: none; }
    html.${homeClass} ${rootSelector} .mc-layout,
    html.${homeClass} ${rootSelector} #wren-community-mobile-top,
    html.${homeClass} ${rootSelector} #wren-community-welcome-link,
    html.${homeClass} ${rootSelector} .mc-page__header-primary-button,
    html.${homeClass} #wren-community-mobile-dock { display: none !important; }
    html.${homeClass} ${rootSelector} #${directoryId} { display: block; }
    #${directoryId} { margin: 4px 0 32px; padding: clamp(20px, 4vw, 38px); border: 1px solid #d9e1d7; border-radius: 20px; background: #f6f7f0; color: #243832; }
    #${directoryId} * { box-sizing: border-box; }
    #${directoryId} .wren-community-directory-heading { margin: 0 0 9px; color: #243832; font-family: inherit; font-size: clamp(25px, 3vw, 35px); font-weight: 650; line-height: 1.2; letter-spacing: -.02em; }
    #${directoryId} .wren-community-directory-intro { max-width: 680px; margin: 0 0 25px; color: #52635a; font-family: inherit; font-size: 16px; line-height: 1.6; }
    #${directoryId} .wren-community-directory-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
    #${directoryId} .wren-community-directory-card { display: flex; align-items: center; gap: 16px; width: 100%; min-width: 0; min-height: 112px; padding: 19px 20px; border: 1px solid #d2ddd2; border-radius: 13px; background: #fffefa; color: #243832; font: inherit; text-align: left; text-decoration: none; cursor: pointer; transition: border-color .15s ease, background-color .15s ease; }
    #${directoryId} .wren-community-directory-card:hover { border-color: #829a87; background: #edf2e9; }
    #${directoryId} .wren-community-directory-copy { flex: 1; min-width: 0; }
    #${directoryId} .wren-community-directory-title { display: block; margin: 0 0 5px; font-size: 18px; font-weight: 650; line-height: 1.35; overflow-wrap: anywhere; }
    #${directoryId} .wren-community-directory-description { display: block; color: #52635a; font-size: 14px; font-weight: 400; line-height: 1.5; overflow-wrap: anywhere; }
    #${directoryId} .wren-community-directory-arrow { display: flex; flex-shrink: 0; align-items: center; justify-content: center; width: 31px; height: 31px; border: 1px solid #c3d1c2; border-radius: 50%; color: #294e41; font-size: 20px; font-weight: 400; }
    #${directoryId} .wren-community-directory-card:focus-visible, #${backId}:focus-visible { outline: 3px solid #294e41; outline-offset: 3px; }
    ${topicRootSelector} #${topicBackId} { display: inline-flex; align-items: center; min-height: 44px; width: fit-content; margin: 0 0 14px; padding: 7px 0; color: #294e41; font: inherit; font-size: 15px; font-weight: 600; line-height: 1.4; text-decoration: underline; text-underline-offset: 3px; }
    ${topicRootSelector} #${topicBackId}:focus-visible { outline: 3px solid #294e41; outline-offset: 3px; }
    ${rootSelector} #${backId}[data-wren-visible] { display: inline-flex; align-items: center; min-height: 44px; width: fit-content; margin: 0 0 10px; color: #294e41; font: inherit; font-size: 14px; font-weight: 600; text-decoration: underline; text-underline-offset: 3px; }
    @media (max-width: 749px) {
      html.${homeClass} main.architechpro-mega-community-body { padding-bottom: 24px; }
      #${directoryId} { margin-top: 6px; margin-bottom: 24px; padding: 16px 12px; border-radius: 15px; }
      #${directoryId} .wren-community-directory-grid { grid-template-columns: 1fr; gap: 8px; }
      #${directoryId} .wren-community-directory-card { min-height: 80px; padding: 12px; gap: 10px; }
      #${directoryId} .wren-community-directory-title { margin-bottom: 4px; font-size: 17px; line-height: 1.35; }
      #${directoryId} .wren-community-directory-description { font-size: 14px; line-height: 1.45; }
      #${directoryId} .wren-community-directory-arrow { width: 24px; height: 24px; }
      #${directoryId} .wren-community-directory-intro { margin-bottom: 14px; }
      html.wren-community-mobile-ready ${rootSelector} #${backId}[data-wren-visible] { display: none; }
    }
  `;
  (document.head || document.documentElement).append(style);

  let directory;
  let grid;
  let back;
  let topicBack;
  let signature = '';
  let queued = false;
  const validTopicUrl = (native, current) => {
    const raw = native.getAttribute('href');
    if (!raw) return null;
    try {
      const url = new URL(raw, current.url);
      const path = url.pathname.replace(/\/+$/, '');
      if (url.origin !== current.url.origin) return null;
      if (current.preview) {
        if (path !== previewPath || url.searchParams.get('code') !== current.url.searchParams.get('code') || !url.searchParams.get('filter')) return null;
      } else if (!path.startsWith(`${basePath}/`)) return null;
      if (current.url.searchParams.has('preview_theme_id')) url.searchParams.set('preview_theme_id', current.url.searchParams.get('preview_theme_id'));
      return url;
    } catch { return null; }
  };
  const topicsIn = (root, current) => {
    const seen = new Set();
    return Array.from(root.querySelectorAll(topicSelector)).flatMap(native => {
      const label = normalized(native.textContent);
      const labelKey = key(label);
      if (!label || seen.has(labelKey)) return [];
      seen.add(labelKey);
      return [{ label, labelKey, url: validTopicUrl(native, current) }];
    }).sort((a, b) => (order.get(a.labelKey) ?? 100) - (order.get(b.labelKey) ?? 100));
  };
  const createDirectory = layout => {
    directory = document.createElement('section');
    directory.id = directoryId;
    directory.setAttribute('aria-labelledby', 'wren-community-directory-heading');
    const heading = document.createElement('h2');
    heading.id = 'wren-community-directory-heading';
    heading.className = 'wren-community-directory-heading';
    heading.textContent = 'Choose a topic';
    const intro = document.createElement('p');
    intro.className = 'wren-community-directory-intro';
    intro.textContent = 'Choose a topic to read, ask a question, or share your story.';
    grid = document.createElement('nav');
    grid.className = 'wren-community-directory-grid';
    grid.setAttribute('aria-label', 'Community topics');
    grid.addEventListener('click', event => {
      const button = event.target.closest('button[data-wren-topic-label]');
      if (!button || !grid.contains(button)) return;
      const root = document.querySelector(rootSelector);
      const native = Array.from(root?.querySelectorAll(topicSelector) || []).find(item => key(item.textContent) === button.dataset.wrenTopicLabel);
      native?.click();
      schedule();
    });
    directory.append(heading, intro, grid);
    layout.before(directory);
    signature = '';
  };
  const renderTopics = topics => {
    const nextSignature = JSON.stringify(topics.map(topic => [topic.label, topic.url?.href || '']));
    if (signature === nextSignature) return;
    signature = nextSignature;
    const fragment = document.createDocumentFragment();
    topics.forEach(topic => {
      const card = document.createElement(topic.url ? 'a' : 'button');
      card.className = 'wren-community-directory-card';
      if (topic.url) {
        card.href = topic.url.href;
        card.target = '_self';
      } else {
        card.type = 'button';
        card.dataset.wrenTopicLabel = topic.labelKey;
      }
      const copy = document.createElement('span');
      copy.className = 'wren-community-directory-copy';
      const title = document.createElement('span');
      title.className = 'wren-community-directory-title';
      title.textContent = topic.label;
      const description = document.createElement('span');
      description.className = 'wren-community-directory-description';
      description.textContent = descriptions.get(topic.labelKey) || 'Start a conversation, ask a question, or share your experience.';
      const arrow = document.createElement('span');
      arrow.className = 'wren-community-directory-arrow';
      arrow.textContent = '→';
      arrow.setAttribute('aria-hidden', 'true');
      copy.append(title, description);
      card.append(copy, arrow);
      fragment.append(card);
    });
    grid.replaceChildren(fragment);
  };
  const sync = () => {
    const current = context();
    const root = current && document.querySelector(rootSelector);
    const layout = root?.querySelector('.mc-layout');
    const heading = root?.querySelector('h2.mc-community-show__feed-heading');
    const topicRoot = current && !current.homePath ? document.querySelector(topicRootSelector) : null;
    const topicHeader = topicRoot?.querySelector('.mc-page__header-wrapper');
    if (topicHeader) {
      if (!topicBack?.isConnected || !topicRoot.contains(topicBack)) {
        topicBack = document.createElement('a');
        topicBack.id = topicBackId;
        topicBack.textContent = '← Back to topics';
        topicBack.target = '_self';
        topicHeader.before(topicBack);
      }
      if (topicBack.getAttribute('href') !== current.homeUrl.href) topicBack.setAttribute('href', current.homeUrl.href);
      if (topicHeader.previousElementSibling !== topicBack) topicHeader.before(topicBack);
    } else if (topicBack?.isConnected) {
      topicBack.remove();
    }
    if (current) document.querySelectorAll('mobile-dock a.dock__item').forEach(link => {
      if (key(link.textContent) !== 'home') return;
      if (link.getAttribute('href') !== current.homeUrl.href) link.setAttribute('href', current.homeUrl.href);
      if (link.getAttribute('target') !== '_self') link.setAttribute('target', '_self');
    });
    if (!current || !root || !layout || !heading) {
      document.documentElement.classList.remove(homeClass);
      if (back?.hasAttribute('data-wren-visible')) back.removeAttribute('data-wren-visible');
      return;
    }
    root.querySelectorAll('a.mc-community-show__nav-item-inner').forEach(link => {
      if (key(link.textContent) !== 'home') return;
      if (link.getAttribute('href') !== current.homeUrl.href) link.setAttribute('href', current.homeUrl.href);
      if (link.getAttribute('target') !== '_self') link.setAttribute('target', '_self');
    });
    const home = current.homePath && key(heading.textContent) === 'home';
    const topics = topicsIn(root, current);
    const ready = home && topics.length > 0;
    if (ready) {
      if (!directory?.isConnected || !root.contains(directory)) createDirectory(layout);
      renderTopics(topics);
    }
    document.documentElement.classList.toggle(homeClass, ready);
    if (!back?.isConnected || !root.contains(back)) {
      back = document.createElement('a');
      back.id = backId;
      back.textContent = '← Back to topics';
      back.target = '_self';
      layout.before(back);
    }
    if (back.getAttribute('href') !== current.homeUrl.href) back.setAttribute('href', current.homeUrl.href);
    if (!home && !back.hasAttribute('data-wren-visible')) back.setAttribute('data-wren-visible', '');
    if (home && back.hasAttribute('data-wren-visible')) back.removeAttribute('data-wren-visible');
  };
  const schedule = () => {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(() => { queued = false; sync(); });
  };
  document.addEventListener('click', event => {
    if ((typeof event.button === 'number' && event.button !== 0) || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest?.(`${rootSelector} a.mc-community-show__nav-item-inner`);
    if (!link || key(link.textContent) !== 'home') return;
    const current = context();
    if (!current) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    window.location.assign(current.homeUrl.href);
  }, true);
  const observer = new MutationObserver(schedule);
  observer.observe(document.documentElement, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['href'] });
  window.addEventListener('popstate', schedule);
  window.addEventListener('pageshow', schedule);
  sync();
})();



/* WrenLife community: first-visit entry and profile navigation, October 17, 2026. */
;(() => {
  const communityPath = '/community/my/wrenlife-trying-to-conceive';
  const path = window.location.pathname.replace(/\/+$/, '');
  const memberMatch = path.match(/^\/community\/my\/members\/(\d+)$/);
  const isHome = path === communityPath;
  if (!isHome && !memberMatch) return;
  const installKey = Symbol.for('wren.community.polish.20261017');
  if (window[installKey]) return;
  window[installKey] = true;

  const style = document.createElement('style');
  style.id = 'wren-community-polish-style';
  style.textContent = `
    #wren-community-first-hello { margin: 0 0 28px; padding: 0 0 26px; border-bottom: 1px solid #ccd8cb; }
    #wren-community-first-hello h2 { margin: 0 0 9px; color: #243832; font: inherit; font-size: clamp(23px, 3vw, 30px); font-weight: 650; line-height: 1.25; letter-spacing: -.02em; }
    #wren-community-first-hello p { max-width: 620px; margin: 0 0 16px; color: #52635a; font-size: 16px; line-height: 1.5; }
    #wren-community-first-hello a { display: inline-flex; align-items: center; justify-content: center; min-height: 48px; max-width: 100%; padding: 12px 22px; border: 1px solid #294e41; border-radius: 10px; background: #294e41; color: #fff; font: inherit; font-size: 16px; font-weight: 600; line-height: 1.4; text-align: center; text-decoration: none; box-sizing: border-box; }
    #wren-community-first-hello a:hover { background: #203e34; }
    #wren-community-first-hello a:focus-visible { outline: 3px solid #294e41; outline-offset: 4px; }
    .mc-postable__time-link[data-wren-unavailable-link] { cursor: default; text-decoration: none; }
    @media (max-width: 749px) {
      #wren-community-first-hello { margin-bottom: 16px; padding-bottom: 16px; }
      #wren-community-first-hello h2 { font-size: 22px; }
      #wren-community-first-hello p { margin-bottom: 12px; }
      #wren-community-first-hello a { width: 100%; min-height: 44px; padding: 10px 14px; }
    }
  `;
  (document.head || document.documentElement).append(style);

  const addFirstStep = () => {
    const directory = document.getElementById('wren-community-topic-directory');
    if (!directory || directory.querySelector('#wren-community-first-hello')) return;
    const section = document.createElement('section');
    section.id = 'wren-community-first-hello';
    section.setAttribute('aria-labelledby', 'wren-community-first-hello-heading');
    const heading = document.createElement('h2');
    heading.id = 'wren-community-first-hello-heading';
    heading.textContent = 'New here? Say hello.';
    const copy = document.createElement('p');
    copy.textContent = 'Introduce yourself with a name or nickname—or start by reading.';
    const link = document.createElement('a');
    const destination = new URL(`${communityPath}/general/introduce-yourself-start-with-a-hello`, window.location.origin);
    const previewTheme = new URL(window.location.href).searchParams.get('preview_theme_id');
    if (previewTheme) destination.searchParams.set('preview_theme_id', previewTheme);
    link.href = destination.href;
    link.textContent = 'Introduce yourself';
    section.append(heading, copy, link);
    directory.prepend(section);
  };

  const polishProfile = () => {
    const root = document.querySelector('main.architechpro-mega-community-body');
    if (!root) return;
    root.querySelectorAll('.mc-postable-card__title-wrapper').forEach(wrapper => {
      const timestamp = wrapper.querySelector('a.mc-postable__time-link[href]');
      if (!timestamp) return;
      let invalidUrl;
      try { invalidUrl = new URL(timestamp.getAttribute('href'), window.location.href); } catch { return; }
      if (invalidUrl.origin !== window.location.origin || !invalidUrl.pathname.startsWith(`${communityPath}/`) || !/\/null\/?$/.test(invalidUrl.pathname)) return;
      const topicPath = invalidUrl.pathname.replace(/\/null\/?$/, '');
      wrapper.querySelectorAll('a.mc-postable-card__title-link[href]').forEach(link => {
        let url;
        try { url = new URL(link.getAttribute('href'), window.location.href); } catch { return; }
        if (url.origin === window.location.origin && url.pathname.replace(/\/+$/, '') === topicPath && link.textContent.trim() === 'post') {
          const lead = link.previousSibling;
          const separator = link.nextSibling;
          if (lead?.nodeType === 3 && /commented\s*$/.test(lead.textContent)) lead.textContent = lead.textContent.replace(/commented\s*$/, 'commented in ');
          if (separator?.nodeType === 3 && /^\s*in\s*$/.test(separator.textContent)) separator.textContent = ' · ';
          link.textContent = 'this topic';
          link.setAttribute('aria-label', 'Open the topic containing this comment');
          link.setAttribute('target', '_self');
        }
      });
      // Keep the date readable without inventing a missing parent-post URL.
      timestamp.removeAttribute('href');
      timestamp.removeAttribute('target');
      timestamp.setAttribute('data-wren-unavailable-link', '');
    });
  };

  let queued = false;
  const sync = () => {
    if (isHome) addFirstStep();
    if (memberMatch) polishProfile();
  };
  const schedule = () => {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(() => { queued = false; sync(); });
  };
  new MutationObserver(schedule).observe(document.documentElement, {
    childList: true, subtree: true, characterData: true,
    attributes: true, attributeFilter: ['href']
  });
  window.addEventListener('pageshow', schedule);
  sync();
})();

/* WrenLife community: clear replies and predictable navigation, October 17, 2026. */
;(() => {
  const communityPath = '/community/my/wrenlife-trying-to-conceive';
  const introductionPath = `${communityPath}/general/introduce-yourself-start-with-a-hello`;
  if (!/^\/community\/my(?:\/|$)/.test(window.location.pathname)) return;
  const installKey = Symbol.for('wren.community.replies.navigation');
  if (window[installKey]) return;
  window[installKey] = true;

  const rootSelector = 'main.architechpro-mega-community-body';
  const cleanPath = () => window.location.pathname.replace(/\/+$/, '');
  const isIntroduction = () => cleanPath() === introductionPath;
  const visible = element => !!element && element.getClientRects().length > 0 && !element.closest('[hidden], [inert], [aria-hidden="true"]');
  const commentArea = native => native.closest('.mc-card')?.querySelector('.mc-comment');
  const composer = native => commentArea(native)?.querySelector('form.mc-postable-form textarea.mc-input-form__textarea');
  let queued = false;
  let pendingFocus = null;
  let focusTimer;
  const openedIntroductions = new Set();

  const style = document.createElement('style');
  style.id = 'wren-community-replies-navigation-style';
  style.textContent = `
    ${rootSelector} .wren-community-reply-action { display: inline-flex; align-items: center; justify-content: center; min-height: 44px; padding: 10px 20px; border: 1px solid #294e41; border-radius: 10px; background: #294e41; color: #fff; font: inherit; font-size: 16px; font-weight: 600; line-height: 1.4; cursor: pointer; }
    ${rootSelector} .wren-community-reply-action:hover { background: #203e34; }
    ${rootSelector} .wren-community-reply-action:focus-visible, #wren-community-thread-nav a:focus-visible { outline: 3px solid #294e41; outline-offset: 3px; }
    ${rootSelector} .mc-card-post__bottom { flex-wrap: wrap; gap: 12px; }
    #wren-community-thread-nav { display: flex; margin: 0 0 14px; }
    #wren-community-thread-nav a { display: inline-flex; align-items: center; min-height: 44px; padding: 7px 0; color: #294e41; font: inherit; font-size: 15px; font-weight: 600; line-height: 1.4; text-decoration: underline; text-underline-offset: 3px; }
    ${rootSelector} .mc-post-show #wren-community-welcome-link { display: none; }
    ${rootSelector} .mc-comment > form.mc-postable-form textarea.mc-input-form__textarea { min-height: 88px; font-size: 16px; line-height: 1.5; scroll-margin-top: 110px; }
    @media (max-width: 749px) {
      ${rootSelector} .wren-community-reply-action { width: 100%; min-height: 48px; }
      ${rootSelector} .mc-card-post__bottom > .mc-comment-button, ${rootSelector} .mc-card-post__bottom > .mc-like-button { min-height: 44px; }
      ${rootSelector} .mc-post-show > .mc-page { padding-left: 16px; padding-right: 16px; }
    }
  `;
  (document.head || document.documentElement).append(style);

  const focusReply = native => {
    const input = composer(native);
    if (!visible(input) || input.disabled || input.readOnly) return false;
    input.focus({ preventScroll: true });
    input.scrollIntoView({ block: 'center', behavior: 'instant' });
    return true;
  };

  const addReplyActions = root => {
    root.querySelectorAll('.mc-card-post__bottom > button.mc-comment-button:not(.mc-reply-button)').forEach(native => {
      if (!native.closest('.mc-post-show, .mc-community-hashtag, .mc-community-show') || !native.closest('.mc-card')) return;
      const row = native.parentElement;
      let action = row.querySelector('.wren-community-reply-action');
      if (!action) {
        action = document.createElement('button');
        action.type = 'button';
        action.className = 'wren-community-reply-action';
        action.addEventListener('click', () => {
          const current = action.parentElement?.querySelector('button.mc-comment-button:not(.mc-reply-button)');
          if (!current || current.disabled || current.getAttribute('aria-disabled') === 'true') return;
          if (focusReply(current)) return;
          if (!commentArea(current)) current.click();
          pendingFocus = current;
          window.clearTimeout(focusTimer);
          focusTimer = window.setTimeout(() => { pendingFocus = null; }, 2000);
          schedule();
        });
        row.append(action);
      }
      const hello = isIntroduction() && !!native.closest('.mc-post-show');
      const label = hello ? 'Say hello' : 'Reply';
      if (action.textContent !== label) action.textContent = label;
      const input = composer(native);
      const disabled = native.disabled || native.getAttribute('aria-disabled') === 'true' ||
        (!!commentArea(native) && (!input || input.disabled || input.readOnly));
      if (action.disabled !== disabled) action.disabled = disabled;
      if (input && !input.hasAttribute('aria-label') && !input.hasAttribute('aria-labelledby')) {
        input.setAttribute('aria-label', hello ? 'Write your introduction' : 'Write your reply');
      }
      // Open this first conversation once, without focusing or opening the keyboard.
      // Logged-out members still initiate the app's native access flow themselves.
      if (hello && root.querySelector('.mc-profile__button') && visible(native) && !openedIntroductions.has(cleanPath())) {
        openedIntroductions.add(cleanPath());
        if (!commentArea(native)) native.click();
      }
    });
  };

  const normalizeNavigation = root => {
    root.querySelectorAll('a[href][target="_blank"]').forEach(link => {
      if (link.hasAttribute('download')) return;
      try {
        const url = new URL(link.getAttribute('href'), window.location.href);
        if (url.origin === window.location.origin && /^\/community\/my(?:\/|$)/.test(url.pathname)) {
          link.setAttribute('target', '_self');
        }
      } catch { /* Leave links the app cannot resolve untouched. */ }
    });
    const post = root.querySelector('.mc-post-show');
    const header = post?.querySelector('.mc-page__header-wrapper');
    if (header && !post.querySelector('#wren-community-thread-nav')) {
      const nav = document.createElement('nav');
      nav.id = 'wren-community-thread-nav';
      nav.setAttribute('aria-label', 'Community navigation');
      const back = document.createElement('a');
      const url = new URL(communityPath, window.location.origin);
      const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      back.href = url.href;
      back.target = '_self';
      back.textContent = 'Back to topics';
      nav.append(back);
      header.after(nav);
    }
  };

  const sync = () => {
    const root = document.querySelector(rootSelector);
    if (!root) return;
    normalizeNavigation(root);
    addReplyActions(root);
    if (pendingFocus && focusReply(pendingFocus)) {
      pendingFocus = null;
      window.clearTimeout(focusTimer);
    }
  };
  const schedule = () => {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(() => { queued = false; sync(); });
  };
  new MutationObserver(schedule).observe(document.documentElement, {
    childList: true, subtree: true, attributes: true, attributeFilter: ['href', 'target', 'disabled', 'readonly', 'aria-disabled']
  });
  window.addEventListener('pageshow', schedule);
  window.addEventListener('popstate', schedule);
  sync();
})();

/* WrenLife community search, October 2026.
 * Search the member-visible feed; Mega's native /search page currently fails to render.
 * No separate copy of member content is stored or requested.
 */
;(() => {
  const basePath = '/community/my/wrenlife-trying-to-conceive';
  const path = location.pathname.replace(/\/+$/, '');
  if (path !== basePath && !path.startsWith(`${basePath}/`)) return;
  const installationKey = Symbol.for('wren.community.search');
  if (window[installationKey]) return;
  window[installationKey] = true;
  const rootSelector = 'main.architechpro-mega-community-body';
  const stateKey = 'wrenCommunitySearch';
  const normalize = text => (text || '').normalize('NFKC').toLocaleLowerCase().replace(/[\p{P}\p{S}\s]+/gu, ' ').trim();
  const plain = text => (text || '').replace(/\s+/g, ' ').trim();
  const homeUrl = new URL(basePath, location.origin);
  const previewId = new URL(location.href).searchParams.get('preview_theme_id');
  if (previewId) homeUrl.searchParams.set('preview_theme_id', previewId);
  const searchUrl = new URL(homeUrl);
  searchUrl.hash = 'wren-community-search-input';
  const getSavedQuery = () => typeof history.state?.[stateKey] === 'string' ? history.state[stateKey].slice(0, 255) : '';
  let query = path === basePath ? getSavedQuery() : '';
  let panel, input, clear, status, results, help;
  let signature = '';
  let queued = false;
  let timer;
  let focusedFromLink = false;

  const style = document.createElement('style');
  style.id = 'wren-community-search-style';
  style.textContent = `
    #wren-community-search { margin:4px 0 22px; padding:24px; border:1px solid #ccd8cb; border-radius:16px; background:#fffefa; color:#243832; }
    #wren-community-search * { box-sizing:border-box; }
    #wren-community-search label { display:block; margin-bottom:10px; font:inherit; font-size:21px; font-weight:650; line-height:1.3; }
    #wren-community-search .wren-search-row { display:flex; flex-wrap:wrap; gap:10px; align-items:center; }
    #wren-community-search input { flex:1 1 220px; min-width:0; width:100%; min-height:48px; padding:12px 14px; border:1px solid #829a87; border-radius:9px; background:#fff; color:#243832; font:inherit; font-size:16px; line-height:1.4; scroll-margin-top:120px; }
    #wren-community-search button { min-height:48px; padding:11px 20px; border:1px solid #294e41; border-radius:9px; background:#294e41; color:white; font:inherit; font-size:16px; font-weight:600; line-height:1.4; cursor:pointer; }
    #wren-community-search .wren-search-clear { background:transparent; color:#294e41; border-color:transparent; text-decoration:underline; text-underline-offset:3px; }
    #wren-community-search .wren-search-clear:disabled { opacity:.45; cursor:default; }
    #wren-community-search .wren-search-help { margin:10px 0 0; color:#52635a; font-size:14px; line-height:1.5; }
    #wren-community-search .wren-search-status { margin:20px 0 12px; font-size:16px; font-weight:600; line-height:1.5; }
    #wren-community-search .wren-search-status:empty { display:none; }
    #wren-community-search ol { display:grid; gap:10px; margin:0; padding:0; list-style:none; }
    #wren-community-search .wren-search-result { display:block; padding:18px; border:1px solid #d2ddd2; border-radius:12px; color:#243832; background:#f6f7f0; text-decoration:none; overflow-wrap:anywhere; }
    #wren-community-search .wren-search-result:hover { background:#edf2e9; border-color:#829a87; }
    #wren-community-search .wren-search-result h3 { margin:5px 0 8px; color:inherit; font:inherit; font-size:19px; font-weight:650; line-height:1.35; }
    #wren-community-search .wren-search-result p { margin:0; font-size:15px; line-height:1.5; color:#52635a; }
    #wren-community-search .wren-search-topic { font-size:13px; font-weight:600; color:#294e41; }
    #wren-community-search .wren-search-empty { margin:0; font-size:15px; line-height:1.5; }
    #wren-community-search :is(input,button,a):focus-visible, .wren-community-search-link:focus-visible { outline:3px solid #294e41; outline-offset:3px; }
    #wren-community-search [hidden] { display:none !important; }
    html.wren-community-search-active #wren-community-topic-directory { display:none !important; }
    .wren-community-search-link { display:inline-flex; align-items:center; min-height:44px; margin:0 0 14px 20px; color:#294e41; font:inherit; font-size:15px; font-weight:600; line-height:1.4; text-decoration:underline; text-underline-offset:3px; }
    #wren-community-thread-nav { flex-wrap:wrap; gap:0 20px; }
    #wren-community-thread-nav .wren-community-search-link { margin:0; }
    @media(max-width:749px) {
      #wren-community-search { margin-bottom:16px; padding:16px 12px; border-radius:14px; }
      #wren-community-search label { margin-bottom:8px; font-size:18px; }
      #wren-community-search .wren-search-row { display:grid; grid-template-columns:minmax(0,1fr) auto; gap:8px; }
      #wren-community-search button[type=submit] { padding:11px 14px; }
      #wren-community-search .wren-search-clear { grid-column:1 / -1; justify-self:start; }
      #wren-community-search .wren-search-clear:disabled { display:none; }
      #wren-community-search .wren-search-result { padding:16px; }
    }
  `;
  document.head.append(style);

  const setText = (element, text) => { if (element.textContent !== text) element.textContent = text; };
  const saveQuery = () => {
    try { history.replaceState({ ...history.state, [stateKey]: query }, '', location.href); } catch { /* Search still works if history state is unavailable. */ }
  };
  const readPosts = root => {
    const posts = new Map();
    root.querySelectorAll('.mc-community-show__posts-feed .mc-card-post__title-link').forEach(link => {
      const card = link.closest('.mc-card');
      const title = plain(link.textContent);
      if (!card || !title) return;
      let url;
      try { url = new URL(link.getAttribute('href'), location.href); } catch { return; }
      if (url.origin !== location.origin || !url.pathname.startsWith(`${basePath}/`) || url.pathname.endsWith('/null')) return;
      if (previewId) url.searchParams.set('preview_theme_id', previewId);
      if (posts.has(url.href)) return;
      const topic = plain(card.querySelector('.mc-card-post__topic-mobile')?.textContent || card.querySelector('a.mc-card-post__topic')?.textContent);
      const excerpt = plain(card.querySelector('.mc-cart-post__text-content')?.textContent);
      posts.set(url.href, { title, topic, excerpt, url: url.href, titleKey: normalize(title), searchable: normalize(`${title} ${topic} ${excerpt}`) });
    });
    return Array.from(posts.values());
  };
  const render = root => {
    if (!root) return;
    const posts = readPosts(root);
    const terms = normalize(query).split(' ').filter(Boolean);
    const active = terms.length > 0;
    document.documentElement.classList.toggle('wren-community-search-active', active);
    if (clear.disabled === Boolean(query)) clear.disabled = !query;
    const ready = Boolean(root.querySelector('.mc-community-show__posts-feed'));
    const nextSignature = JSON.stringify([query, ready, posts.map(p => [p.url,p.title,p.topic,p.excerpt])]);
    if (signature === nextSignature) return;
    signature = nextSignature;
    setText(help, posts.length ? `Search titles, topics, and previews from ${posts.length} conversations.` : 'Search conversation titles, topics, and previews.');
    if (!active) {
      setText(status, '');
      results.replaceChildren();
      return;
    }
    if (!ready) {
      setText(status, 'Loading conversations…');
      results.replaceChildren();
      return;
    }
    const matches = posts.filter(p => terms.every(t => p.searchable.includes(t)));
    matches.sort((a,b) => Number(b.titleKey.includes(normalize(query))) - Number(a.titleKey.includes(normalize(query))));
    setText(status, !posts.length ? 'No conversations are available to search yet.' : matches.length ? `${matches.length} conversation${matches.length === 1 ? '' : 's'} found` : 'No conversations found');
    const fragment = document.createDocumentFragment();
    if (!matches.length) {
      const message = document.createElement('p');
      message.className = 'wren-search-empty';
      message.textContent = 'Try a shorter phrase or clear your search to browse the topics.';
      fragment.append(message);
    } else {
      const list = document.createElement('ol');
      matches.forEach(post => {
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.className = 'wren-search-result';
        link.href = post.url;
        link.target = '_self';
        if (post.topic) {
          const topic = document.createElement('span');
          topic.className = 'wren-search-topic';
          topic.textContent = post.topic;
          link.append(topic);
        }
        const title = document.createElement('h3');
        title.textContent = post.title;
        const excerpt = document.createElement('p');
        excerpt.textContent = post.excerpt.length > 220 ? `${post.excerpt.slice(0,217).trimEnd()}…` : post.excerpt;
        link.append(title,excerpt);
        item.append(link);
        list.append(item);
      });
      fragment.append(list);
    }
    results.replaceChildren(fragment);
  };
  const search = () => {
    query = input.value.slice(0,255);
    saveQuery();
    render(document.querySelector(rootSelector));
  };
  const mount = directory => {
    panel = document.createElement('section');
    panel.id = 'wren-community-search';
    panel.setAttribute('aria-label','Find a conversation');
    panel.innerHTML = '<form role="search" aria-label="Search conversations"><label for="wren-community-search-input">Search conversations</label><div class="wren-search-row"><input id="wren-community-search-input" type="search" maxlength="255" placeholder="Try two-week wait or PCOS" aria-describedby="wren-community-search-help" autocomplete="off" enterkeyhint="search"><button type="submit">Search</button><button type="button" class="wren-search-clear" disabled>Clear search</button></div><p id="wren-community-search-help" class="wren-search-help"></p></form><p class="wren-search-status" role="status" aria-live="polite" aria-atomic="true"></p><div class="wren-search-results"></div>';
    input = panel.querySelector('input');
    input.value = query;
    clear = panel.querySelector('.wren-search-clear');
    status = panel.querySelector('.wren-search-status');
    results = panel.querySelector('.wren-search-results');
    help = panel.querySelector('.wren-search-help');
    input.addEventListener('input', () => { query = input.value.slice(0,255); saveQuery(); window.clearTimeout(timer); timer = window.setTimeout(search,180); });
    panel.querySelector('form').addEventListener('submit',event=> { event.preventDefault(); window.clearTimeout(timer); search(); input.blur(); });
    clear.addEventListener('click', () => { window.clearTimeout(timer); input.value=''; search(); input.focus({preventScroll:true}); });
    directory.before(panel);
    signature = '';
  };
  const sync = () => {
    const root = document.querySelector(rootSelector);
    if (!root) return;
    const back = root.querySelector('#wren-community-thread-nav, #wren-community-topic-back');
    if (path !== basePath && back && !root.querySelector('.wren-community-search-link')) {
      const link = document.createElement('a');
      link.className = 'wren-community-search-link';
      link.href = searchUrl.href;
      link.target = '_self';
      link.textContent = 'Search conversations';
      if (back.tagName === 'NAV') back.append(link); else back.after(link);
    }
    if (path !== basePath) return;
    const directory = root.querySelector('#wren-community-topic-directory');
    if (!directory) return;
    if (!panel?.isConnected) mount(directory);
    render(root);
    if (!focusedFromLink && location.hash === searchUrl.hash) {
      focusedFromLink = true;
      input.focus({preventScroll:true});
      input.scrollIntoView({block:'center'});
    }
  };
  const schedule = () => { if (queued) return; queued=true; requestAnimationFrame(()=>{queued=false;sync();}); };
  const observer = new MutationObserver(schedule);
  observer.observe(document.documentElement,{childList:true,subtree:true,characterData:true});
  window.addEventListener('pageshow',schedule);
  window.addEventListener('popstate',()=> { query=getSavedQuery(); if(input) input.value=query; schedule(); });
  window.addEventListener('hashchange',()=> { focusedFromLink=false; schedule(); });
  sync();
})();

/* WrenLife community member names, October 2026.
 * Mega Community is configured to show chosen nicknames. Older author labels
 * may still contain a customer ID. Reuse only an unambiguous nickname already
 * rendered for that member on this page; otherwise use a neutral label.
 * No account data, profile headings, requests, or persistent name cache.
 */
;(() => {
  if (!/^\/community\/my(?:\/|$)/.test(location.pathname)) return;
  const installationKey = Symbol.for('wren.community.memberNames');
  if (window[installationKey]) return;
  window[installationKey] = true;
  const rootSelector = 'main.architechpro-mega-community-body';
  const replacements = new WeakMap();
  const plain = value => (value || '').replace(/\s+/g, ' ').trim();
  let queued = false;

  const memberId = href => {
    if (!href) return null;
    try {
      const url = new URL(href, location.href);
      return url.origin === location.origin
        ? url.pathname.match(/^\/community\/my\/members\/(\d+)\/?$/)?.[1] || null
        : null;
    } catch { return null; }
  };

  const sync = () => {
    const root = document.querySelector(rootSelector);
    if (!root) return;
    const entries = [];
    root.querySelectorAll('.mc-postable-card__name-link').forEach(link => {
      const id = memberId(link.getAttribute('href'));
      const label = link.querySelector('span.mc-font-bold.mc-bodyMd');
      if (id && label && !label.children.length) entries.push({ id, label });
    });
    const profileId = location.pathname.match(/^\/community\/my\/members\/(\d+)\/?$/)?.[1];
    if (profileId) {
      root.querySelectorAll('.mc-postable-card__title-wrapper > span.mc-font-bold.mc-bodyMd').forEach(label => {
        if (!label.children.length) entries.push({ id: profileId, label });
      });
    }

    const names = new Map();
    entries.forEach(entry => {
      const text = plain(entry.label.textContent);
      const previous = replacements.get(entry.label);
      if (previous && text === previous.text) {
        entry.needsFallback = true;
        return;
      }
      replacements.delete(entry.label);
      entry.needsFallback = text === entry.id;
      if (!entry.needsFallback && text && text !== 'Community member') {
        if (!names.has(entry.id)) names.set(entry.id, new Set());
        names.get(entry.id).add(text);
      }
    });

    entries.forEach(({ id, label, needsFallback }) => {
      if (!needsFallback) return;
      const candidates = names.get(id);
      const text = candidates?.size === 1 ? candidates.values().next().value : 'Community member';
      replacements.set(label, { id, text });
      if (label.textContent !== text) label.textContent = text;
    });
  };

  const style = document.createElement('style');
  style.id = 'wren-community-member-names-style';
  style.textContent = `${rootSelector} .mc-postable-card__name-link { min-width:0; overflow-wrap:anywhere; }
    ${rootSelector} .mc-postable-card__name-link > span { white-space:normal; }`;
  document.head.append(style);
  const schedule = () => {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(() => { queued = false; sync(); });
  };
  new MutationObserver(schedule).observe(document.documentElement, {
    childList: true, subtree: true, characterData: true,
    attributes: true, attributeFilter: ['href']
  });
  window.addEventListener('pageshow', schedule);
  window.addEventListener('popstate', schedule);
  sync();
})();

/* WrenLife community: warn before leaving unfinished writing, October 2026.
 * Draft content stays only in Mega Community's own inputs. Nothing is saved,
 * restored, submitted, or interpreted as a successful post by this module.
 */
;(() => {
  if (!/^\/community\/my(?:\/|$)/.test(window.location.pathname)) return;
  const installationKey = Symbol.for('wren.community.postingGuard');
  if (window[installationKey]) return;
  window[installationKey] = true;

  const rootSelector = 'main.architechpro-mega-community-body';
  const formSelector = 'form.mc-postable-form';
  const fieldSelector = 'textarea.mc-input-form__textarea';
  const attachmentSelector = '.mc-attachments > ul.mc-attachments__list > li.mc-attachments__list-item';
  const baselines = new WeakMap();
  const attachmentBaselines = new WeakMap();
  const helpers = new WeakMap();
  let nextHelper = 0;
  let queued = false;
  let unloadListening = false;
  let replayControl = null;
  let allowedUnload = false;
  let unloadTimer;
  let dialog;
  let keepButton;
  let pendingNavigation = null;

  const root = () => document.querySelector(rootSelector);
  const forms = () => Array.from(root()?.querySelectorAll(formSelector) || []);
  const fields = form => Array.from(form.querySelectorAll(fieldSelector));
  const remember = field => {
    if (!baselines.has(field)) baselines.set(field, field.value);
  };
  const isDirty = field => {
    remember(field);
    const baseline = baselines.get(field);
    const current = field.value;
    return current !== baseline && Boolean(current.trim() || baseline.trim());
  };
  const attachmentSources = form => Array.from(form.querySelectorAll(attachmentSelector))
    .map(item => item.querySelector('img.mc-attachments__image'))
    .filter(Boolean)
    .map(image => image.getAttribute('src') || '');
  const localPhoto = source => source.startsWith('blob:');
  const attachmentsDirty = form => {
    const current = attachmentSources(form);
    let state = attachmentBaselines.get(form);
    if (!state) {
      // A blob preview is newly selected, even if it arrived before the form
      // was first observed. Persisted edit-form images start as its baseline.
      state = { baseline: current.filter(source => !localPhoto(source)), edited: current.some(localPhoto) };
      attachmentBaselines.set(form, state);
    }
    if (current.some(localPhoto)) state.edited = true;
    // Existing images may render after their edit form mounts. Let those
    // settle until an attachment interaction or a new local preview occurs.
    if (!state.edited) state.baseline = current;
    return current.length !== state.baseline.length || current.some((source, index) => source !== state.baseline[index]);
  };
  const formDirty = form => {
    const photosChanged = attachmentsDirty(form);
    return fields(form).some(isDirty) || photosChanged;
  };
  const dirtyForms = () => forms().filter(formDirty);
  const hasWriting = () => dirtyForms().length > 0;
  const fieldFromEvent = event => {
    const field = event.target;
    if (!field?.matches?.(fieldSelector)) return null;
    const form = field.closest(formSelector);
    return form && root()?.contains(form) ? field : null;
  };
  const rememberAttachmentInteraction = event => {
    const control = event.target?.closest?.('input[type="file"], .mc-attachments__remove-button button.mc-attachments__button-icon');
    const form = control?.closest(formSelector);
    if (!form || !root()?.contains(form)) return false;
    // Capture the visible preview list before the native change/remove
    // handler runs. FileList is not draft state: it can outlive removal.
    attachmentsDirty(form);
    attachmentBaselines.get(form).edited = true;
    return true;
  };

  const clearUnloadAllowance = () => {
    allowedUnload = false;
    window.clearTimeout(unloadTimer);
  };
  const allowConfirmedUnload = () => {
    clearUnloadAllowance();
    allowedUnload = true;
    // Avoid a second browser prompt for the navigation just confirmed. The
    // allowance is single-use and expires if the app does not navigate.
    unloadTimer = window.setTimeout(clearUnloadAllowance, 1000);
  };
  const beforeUnload = event => {
    if (allowedUnload) {
      clearUnloadAllowance();
      return;
    }
    if (!hasWriting()) return;
    event.preventDefault();
    event.returnValue = '';
  };

  const sync = () => {
    let dirty = false;
    forms().forEach(form => {
      const inputs = fields(form);
      inputs.forEach(remember);
      const changed = formDirty(form);
      dirty = dirty || changed;
      let helper = helpers.get(form);
      if (changed && !helper?.isConnected) {
        helper = document.createElement('p');
        helper.id = `wren-community-posting-status-${++nextHelper}`;
        helper.className = 'wren-community-posting-status';
        helper.setAttribute('role', 'status');
        helper.setAttribute('aria-live', 'polite');
        helper.textContent = 'Not posted yet.';
        form.append(helper);
        helpers.set(form, helper);
      }
      if (helper) {
        if (helper.hidden !== !changed) helper.hidden = !changed;
        inputs.forEach(input => {
          const described = (input.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
          if (!described.includes(helper.id)) input.setAttribute('aria-describedby', [...described, helper.id].join(' '));
        });
      }
    });
    if (dirty && !unloadListening) {
      window.addEventListener('beforeunload', beforeUnload);
      unloadListening = true;
    } else if (!dirty && unloadListening) {
      window.removeEventListener('beforeunload', beforeUnload);
      unloadListening = false;
    }
  };
  const schedule = () => {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(() => { queued = false; sync(); });
  };

  const navigationUrl = link => {
    if (!link || link.hasAttribute('download')) return null;
    const target = (link.getAttribute('target') || document.querySelector('base[target]')?.getAttribute('target') || '').toLowerCase();
    if (target && target !== '_self') return null;
    const href = link.getAttribute('href');
    if (!href) return null;
    try {
      const url = new URL(href, window.location.href);
      if (!/^https?:$/.test(url.protocol)) return null;
      const current = new URL(window.location.href);
      if (url.origin === current.origin && url.pathname === current.pathname && url.search === current.search && (href.startsWith('#') || url.hash || current.hash)) return null;
      return url.href;
    } catch { return null; }
  };

  const usableProfileButton = button => Boolean(button?.isConnected &&
    button.matches('button.mc-profile__button') && root()?.contains(button) &&
    !button.disabled && button.getAttribute('aria-disabled') !== 'true');
  const restoreFocus = navigation => {
    const original = navigation.button || navigation.link;
    const focus = navigation.focus?.isConnected ? navigation.focus : original;
    if (focus?.isConnected && !focus.disabled) focus.focus({ preventScroll: true });
  };
  const continueNavigation = navigation => {
    const { button, link, href } = navigation;
    if (button) {
      // Replay only this original native profile control. Its destination and
      // current-member identity stay entirely under Mega Community's control.
      if (!usableProfileButton(button)) {
        clearUnloadAllowance();
        restoreFocus(navigation);
        return;
      }
      allowConfirmedUnload();
      replayControl = button;
      try { button.click(); }
      finally { replayControl = null; }
      schedule();
      return;
    }
    allowConfirmedUnload();
    if (link.isConnected && navigationUrl(link) === href) {
      replayControl = link;
      try { link.click(); }
      finally { replayControl = null; }
    } else {
      window.location.assign(href);
    }
    schedule();
  };
  const closePrompt = leave => {
    const navigation = pendingNavigation;
    if (!navigation) return;
    pendingNavigation = null;
    if (dialog?.open) dialog.close();
    if (dialog) dialog.hidden = true;
    if (leave) {
      // Re-read inputs on this action. We never clear text or mark it posted.
      sync();
      continueNavigation(navigation);
    } else {
      restoreFocus(navigation);
      sync();
    }
  };
  const createDialog = () => {
    if (dialog) return dialog;
    dialog = document.createElement('dialog');
    dialog.id = 'wren-community-leave-dialog';
    dialog.hidden = true;
    dialog.setAttribute('aria-labelledby', 'wren-community-leave-title');
    dialog.setAttribute('aria-describedby', 'wren-community-leave-description');
    const title = document.createElement('h2');
    title.id = 'wren-community-leave-title';
    title.textContent = 'Leave without posting?';
    const description = document.createElement('p');
    description.id = 'wren-community-leave-description';
    description.textContent = 'Your draft has not been posted. If you leave this page, it may be lost.';
    const actions = document.createElement('div');
    actions.className = 'wren-community-leave-actions';
    keepButton = document.createElement('button');
    keepButton.type = 'button';
    keepButton.className = 'wren-community-keep-writing';
    keepButton.textContent = 'Keep writing';
    keepButton.addEventListener('click', () => closePrompt(false));
    const leaveButton = document.createElement('button');
    leaveButton.type = 'button';
    leaveButton.className = 'wren-community-leave-page';
    leaveButton.textContent = 'Leave page';
    leaveButton.addEventListener('click', () => closePrompt(true));
    actions.append(keepButton, leaveButton);
    dialog.append(title, description, actions);
    dialog.addEventListener('cancel', event => { event.preventDefault(); closePrompt(false); });
    dialog.addEventListener('close', () => { if (pendingNavigation) closePrompt(false); });
    document.body.append(dialog);
    return dialog;
  };
  const confirmNavigation = navigation => {
    if (!hasWriting()) { continueNavigation(navigation); return; }
    pendingNavigation = navigation;
    const prompt = createDialog();
    if (typeof prompt.showModal === 'function') {
      prompt.hidden = false;
      prompt.showModal();
      keepButton.focus({ preventScroll: true });
    } else {
      closePrompt(window.confirm('Your draft has not been posted. Leave this page and risk losing it?'));
    }
  };

  document.addEventListener('beforeinput', event => {
    const field = fieldFromEvent(event);
    if (field) remember(field);
  }, true);
  document.addEventListener('focusin', event => {
    const field = fieldFromEvent(event);
    if (field) remember(field);
  }, true);
  document.addEventListener('input', event => {
    if (!fieldFromEvent(event)) return;
    clearUnloadAllowance();
    sync();
  }, true);
  document.addEventListener('change', event => {
    if (rememberAttachmentInteraction(event)) { clearUnloadAllowance(); schedule(); }
    if (fieldFromEvent(event)) sync();
  }, true);
  document.addEventListener('click', event => {
    if (rememberAttachmentInteraction(event)) { clearUnloadAllowance(); schedule(); }
    if (event.defaultPrevented || (typeof event.button === 'number' && event.button !== 0)) return;
    const candidate = event.target?.closest?.('button.mc-profile__button');
    const button = usableProfileButton(candidate) ? candidate : null;
    const link = event.target?.closest?.('a[href]');
    if ((!button && !link) || (button || link) === replayControl) return;
    if (!button && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) return;
    const href = button ? null : navigationUrl(link);
    if ((!button && !href) || !hasWriting()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (pendingNavigation) return;
    const active = document.activeElement;
    confirmNavigation({ button, link, href, focus: active && active !== document.body ? active : button || link });
  }, true);
  document.addEventListener('pointerdown', clearUnloadAllowance, true);
  document.addEventListener('keydown', clearUnloadAllowance, true);
  new MutationObserver(schedule).observe(document.documentElement, {
    childList: true, subtree: true, attributes: true, attributeFilter: ['src']
  });
  window.addEventListener('pageshow', () => { clearUnloadAllowance(); sync(); });
  window.addEventListener('popstate', schedule);
  sync();
})();

/* WrenLife community: readable, touch-friendly native posting controls. */
;(() => {
  if (!/^\/community\/my(?:\/|$)/.test(window.location.pathname)) return;
  const installationKey = Symbol.for('wren.community.postingControls');
  if (window[installationKey]) return;
  window[installationKey] = true;

  const rootSelector = 'main.architechpro-mega-community-body';
  const formSelector = `${rootSelector} form.mc-postable-form`;
  const initialized = new WeakMap();
  let queued = false;
  const style = document.createElement('style');
  style.id = 'wren-community-posting-style';
  style.textContent = `
    ${formSelector} textarea.mc-input-form__textarea { font-size: 16px; line-height: 1.5; scroll-margin-block: 110px 90px; }
    ${formSelector} .mc-textarea-wrapper > div:not(.mc-input-form__title) > textarea.mc-input-form__textarea { min-height: 88px; max-height: 240px; max-height: min(40dvh, 280px); overflow-y: auto; }
    ${formSelector} .mc-input-from__actions { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
    ${formSelector} .mc-input-form__left { display: flex; flex-wrap: wrap; min-width: 0; }
    ${formSelector} .mc-input-form__right { margin-left: auto; flex: 0 0 auto; }
    ${formSelector} .mc-card-form__action-button { min-height: 44px; min-width: 44px; }
    ${formSelector} button.mc-input-form__submit.wren-community-post-button { display: inline-flex; align-items: center; justify-content: center; gap: 8px; width: auto; min-width: 96px; min-height: 44px; padding: 10px 14px; border-radius: 9px; background: #294e41; color: #fff; font: inherit; font-size: 15px; font-weight: 650; line-height: 1.35; white-space: nowrap; }
    ${formSelector} button.mc-input-form__submit.wren-community-post-button svg { flex: 0 0 20px; width: 20px; height: 20px; }
    ${formSelector} button.mc-input-form__submit.wren-community-post-button:focus-visible { outline: 2px solid #294e41; outline-offset: 3px; }
    ${formSelector} .wren-community-posting-status { margin: 8px 0 0; color: #53635c; font: inherit; font-size: 13px; line-height: 1.4; }
    ${formSelector} .wren-community-posting-status[hidden] { display: none !important; }
    #wren-community-leave-dialog { box-sizing: border-box; width: min(420px, calc(100% - 32px)); max-height: calc(100vh - 32px); max-height: calc(100dvh - 32px); margin: auto; padding: 24px; overflow-y: auto; border: 1px solid #d6dfd6; border-radius: 16px; background: #fffdf8; color: #243832; box-shadow: 0 12px 50px #24383233; font-family: inherit; }
    #wren-community-leave-dialog::backdrop { background: #182c25a6; }
    #wren-community-leave-dialog h2 { margin: 0 0 12px; color: inherit; font-size: 23px; line-height: 1.25; }
    #wren-community-leave-dialog p { margin: 0 0 20px; color: inherit; font-size: 16px; line-height: 1.5; }
    #wren-community-leave-dialog .wren-community-leave-actions { display: flex; flex-wrap: wrap; gap: 10px; }
    #wren-community-leave-dialog button { flex: 1 1 130px; min-height: 44px; padding: 11px 14px; border: 1px solid #294e41; border-radius: 9px; background: #fffdf8; color: #294e41; font: inherit; font-size: 16px; font-weight: 650; line-height: 1.35; cursor: pointer; }
    #wren-community-leave-dialog button.wren-community-keep-writing { background: #294e41; color: #fff; }
    #wren-community-leave-dialog button:focus-visible { outline: 2px solid #294e41; outline-offset: 3px; }
  `;
  (document.head || document.documentElement).append(style);

  const sync = () => {
    document.querySelectorAll(formSelector).forEach(form => {
      if (!initialized.has(form)) {
        // Leave existing edit forms' native action labels alone.
        initialized.set(form, !Array.from(form.querySelectorAll('textarea.mc-input-form__textarea')).some(input => input.value.trim()));
      }
      form.querySelectorAll('textarea.mc-input-form__textarea').forEach(input => {
        if (input.getAttribute('aria-label') || input.getAttribute('aria-labelledby') || input.labels?.length) return;
        const label = input.closest('.mc-input-form__title') ? 'Post title (optional)' : form.querySelector('.mc-community-show__post-form') ? 'Write your post' : 'Write your reply';
        input.setAttribute('aria-label', label);
      });
      if (!initialized.get(form)) return;
      form.querySelectorAll('button.mc-input-form__submit[type="button"]').forEach(button => {
        let label = button.querySelector('.wren-community-post-button-label');
        const nativeText = Array.from(button.childNodes).filter(node => node !== label).map(node => node.textContent || '').join('').trim();
        if (nativeText || button.getAttribute('aria-label') || button.getAttribute('aria-labelledby') || button.getAttribute('title')) {
          label?.remove();
          button.classList.remove('wren-community-post-button');
          return;
        }
        const text = form.querySelector('.mc-community-show__post-form') ? 'Post' : 'Post reply';
        if (!label) {
          label = document.createElement('span');
          label.className = 'wren-community-post-button-label';
          button.append(label);
        }
        if (label.textContent !== text) label.textContent = text;
        button.classList.add('wren-community-post-button');
      });
    });
  };
  const schedule = () => {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(() => { queued = false; sync(); });
  };
  new MutationObserver(schedule).observe(document.documentElement, { childList: true, subtree: true });
  sync();
})();

/* WrenLife community: visible native shortcuts and a way back from activity. */
;(() => {
  if (!/^\/community\/my(?:\/|$)/.test(window.location.pathname)) return;
  const installationKey = Symbol.for('wren.community.returnNavigation');
  if (window[installationKey]) return;
  window[installationKey] = true;

  const rootSelector = 'main.architechpro-mega-community-body';
  const homePath = '/community/my/wrenlife-trying-to-conceive';
  let queued = false;
  const homeHref = () => {
    const href = new URL(homePath, window.location.origin);
    const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
    if (preview) href.searchParams.set('preview_theme_id', preview);
    return href.href;
  };
  const style = document.createElement('style');
  style.id = 'wren-community-return-navigation-style';
  style.textContent = `
    ${rootSelector} .mc-page__header.wren-community-return-header { flex-wrap: wrap; gap: 12px; }
    ${rootSelector} .wren-community-return-header .mc-page__header-buttons { gap: 8px; flex-wrap: wrap; }
    ${rootSelector} .wren-community-return-header .mc-notifications__button-wrapper { width: auto; }
    ${rootSelector} button.mc-notifications__button.wren-community-return-control,
    ${rootSelector} button.mc-profile__button.wren-community-return-control { box-sizing: border-box; display: inline-flex; align-items: center; justify-content: center; gap: 8px; width: auto; height: auto; min-width: 44px; min-height: 44px; padding: 7px 12px; border: 1px solid #cbd7cf; border-radius: 10px; background: #f6f8f3; color: #243832; font: inherit; font-size: 14px; font-weight: 600; line-height: 1.35; white-space: nowrap; cursor: pointer; }
    ${rootSelector} .wren-community-return-control .mc-page__header-profile-avatar { width: 28px; height: 28px; border-radius: 50%; object-fit: cover; }
    ${rootSelector} .wren-community-return-control > svg { width: 20px; height: 20px; flex: 0 0 20px; }
    ${rootSelector} .wren-community-return-control:focus-visible,
    #wren-community-return-nav a:focus-visible,
    ${rootSelector} .wren-community-notification-help a:focus-visible { outline: 2px solid #294e41; outline-offset: 3px; }
    #wren-community-return-nav { display: flex; flex-wrap: wrap; gap: 8px 16px; margin: 0 0 16px; }
    #wren-community-return-nav a { display: inline-flex; align-items: center; min-height: 44px; padding: 8px 0; color: #294e41; font: inherit; font-size: 15px; font-weight: 600; line-height: 1.4; text-decoration: underline; text-underline-offset: 3px; }
    ${rootSelector} .mc-notification-page__empty-state { color: #53635c !important; font-size: 16px; line-height: 1.5; }
    ${rootSelector} .wren-community-notification-help { margin: 16px auto 0; max-width: 380px; text-align: center; color: #53635c; font: inherit; font-size: 16px; line-height: 1.5; }
    ${rootSelector} .wren-community-notification-help p { margin: 0 0 16px; }
    ${rootSelector} .wren-community-notification-help a { box-sizing: border-box; display: inline-flex; align-items: center; justify-content: center; min-height: 44px; padding: 10px 20px; border: 1px solid #294e41; border-radius: 10px; background: #294e41; color: #fff; font: inherit; font-size: 16px; font-weight: 600; line-height: 1.4; text-decoration: none; }
    @media (max-width: 749px) {
      ${rootSelector} .mc-page__header.wren-community-return-header { grid-template-columns: minmax(0, 1fr); }
      ${rootSelector} .wren-community-return-header .mc-page__header-left,
      ${rootSelector} .wren-community-return-header .mc-page__actions-wrapper { grid-column: 1 / -1; grid-row: auto; min-width: 0; width: 100%; max-width: 100%; }
      ${rootSelector} .wren-community-return-header .mc-page__title-wrapper { min-width: 0; max-width: 100%; }
      ${rootSelector} .wren-community-return-header .mc-page__actions-wrapper { margin-left: 0; }
      ${rootSelector} .wren-community-return-header .mc-page__actions,
      ${rootSelector} .wren-community-return-header .mc-page__header-buttons { width: 100%; justify-content: flex-start; }
      ${rootSelector} .mc-notification-page,
      ${rootSelector} .mc-community-customers-show > .mc-page { padding-left: 16px; padding-right: 16px; }
      ${rootSelector} .wren-community-return-header .mc-notifications__dropdown { box-sizing: border-box; left: 0; right: auto; width: min(340px, calc(100vw - 40px)); max-width: calc(100vw - 40px); }
    }
  `;
  (document.head || document.documentElement).append(style);

  const labelControl = (button, text) => {
    let label = button.querySelector('.wren-community-return-label');
    if (!label) {
      label = document.createElement('span');
      label.className = 'wren-community-return-label';
      button.append(label);
    }
    if (label.textContent !== text) label.textContent = text;
    button.classList.add('wren-community-return-control');
    button.closest('.mc-page__header')?.classList.add('wren-community-return-header');
    // The visible text supplies the name; preserve any app-owned unread badge.
    button.querySelectorAll('svg').forEach(icon => {
      if (icon.getAttribute('aria-hidden') !== 'true') icon.setAttribute('aria-hidden', 'true');
    });
    button.querySelectorAll('img:not([alt])').forEach(avatar => avatar.setAttribute('alt', ''));
  };

  const sync = () => {
    const root = document.querySelector(rootSelector);
    if (!root) return;
    root.querySelectorAll('button.mc-notifications__button').forEach(button => labelControl(button, 'Notifications'));
    root.querySelectorAll('button.mc-profile__button').forEach(button => labelControl(button, 'My activity'));

    const path = window.location.pathname.replace(/\/+$/, '');
    const isNotifications = path === '/community/my/notifications';
    const isProfile = /^\/community\/my\/members\/[^/]+$/.test(path);
    const page = isNotifications ? root.querySelector('.mc-notification-page') : isProfile ? root.querySelector('.mc-community-customers-show .mc-page') : null;
    const header = page?.querySelector('.mc-page__header-wrapper');
    let nav = root.querySelector('#wren-community-return-nav');
    if (header) {
      if (!nav) {
        nav = document.createElement('nav');
        nav.id = 'wren-community-return-nav';
        nav.setAttribute('aria-label', 'Community navigation');
        const back = document.createElement('a');
        back.textContent = 'Back to topics';
        nav.append(back);
      }
      if (header.nextElementSibling !== nav) header.after(nav);
      const back = nav.querySelector('a');
      if (back.href !== homeHref()) back.href = homeHref();
    } else nav?.remove();

    root.querySelectorAll('.mc-notifications__empty-message.mc-notification-page__empty-state').forEach(empty => {
      const nativeText = Array.from(empty.childNodes).filter(node => node.nodeType === 3).map(node => node.textContent).join(' ').replace(/\s+/g, ' ').trim();
      let help = empty.querySelector('.wren-community-notification-help');
      // Only enhance the app's explicit empty state, never a loading/error page.
      if (!isNotifications || nativeText !== 'No new notifications.') { help?.remove(); return; }
      if (!help) {
        help = document.createElement('div');
        help.className = 'wren-community-notification-help';
        const description = document.createElement('p');
        description.textContent = 'Check back here for community updates. In the meantime, explore a topic or join a conversation.';
        const browse = document.createElement('a');
        browse.textContent = 'Browse topics';
        help.append(description, browse);
        empty.append(help);
      }
      const browse = help.querySelector('a');
      if (browse.href !== homeHref()) browse.href = homeHref();
    });
  };
  const schedule = () => {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(() => { queued = false; sync(); });
  };
  new MutationObserver(schedule).observe(document.documentElement, { childList: true, subtree: true, characterData: true });
  window.addEventListener('pageshow', schedule);
  window.addEventListener('popstate', schedule);
  sync();
})();


/* WrenLife community: clearer native photo selection and previews.
 * The app still owns file selection, validation, removal and posting.
 */
;(() => {
  if (!/^\/community\/my(?:\/|$)/.test(window.location.pathname)) return;
  const installationKey = Symbol.for('wren.community.photoControls');
  if (window[installationKey]) return;
  window[installationKey] = true;

  const rootSelector = 'main.architechpro-mega-community-body';
  const formSelector = 'form.mc-postable-form';
  const initialized = new WeakMap();
  const helpers = new WeakMap();
  let nextHelper = 0;
  let queued = false;

  const knownPhotoInput = input => input.matches('input[type="file"]') &&
    (input.getAttribute('accept') || '').split(',').map(type => type.trim().toLowerCase()).sort().join(',') === '.jpeg,.jpg,.png,.webp';
  const inputFor = label => {
    const form = label.closest(formSelector);
    const controls = label.closest('.mc-input-form__left');
    const id = label.getAttribute('for');
    if (!form || !controls || !id) return null;
    // Resolve within this form, since the app can repeat image-upload IDs.
    const matches = Array.from(controls.querySelectorAll('input[type="file"]'))
      .filter(input => input.closest(formSelector) === form && input.id === id && knownPhotoInput(input));
    return matches.length === 1 ? matches[0] : null;
  };
  const choosePhoto = label => {
    const input = inputFor(label);
    if (input && !input.matches(':disabled')) input.click();
  };

  const style = document.createElement('style');
  style.id = 'wren-community-photo-controls-style';
  style.textContent = `
    ${rootSelector} .wren-community-photo-picker { box-sizing: border-box; display: inline-flex; align-items: center; justify-content: center; gap: 7px; width: auto; height: auto; min-width: 44px; min-height: 44px; padding: 9px 12px; border: 1px solid #cbd7cf; border-radius: 9px; color: #294e41; background: #f6f8f3; font: inherit; font-size: 14px; line-height: 1.4; cursor: pointer; }
    ${rootSelector} .wren-community-photo-picker > svg { width: 20px; height: 20px; flex: 0 0 20px; }
    ${rootSelector} .wren-community-photo-picker[aria-disabled="true"] { opacity: .6; cursor: default; }
    ${rootSelector} .wren-community-photo-picker:focus-visible,
    ${rootSelector} .wren-community-photo-item button.mc-attachments__button-icon:focus-visible { outline: 2px solid #294e41; outline-offset: 3px; }
    ${rootSelector} .wren-community-photo-help { margin: 8px 0 0; color: #53635c; font: inherit; font-size: 13px; line-height: 1.5; }
    ${rootSelector} .mc-attachments__list-item.wren-community-photo-item { box-sizing: border-box; display: flex; flex-direction: column; gap: 8px; flex: 0 1 240px; width: 240px; max-width: 100%; min-width: 0; height: auto; padding: 10px; border: 1px solid #d6dfd6; border-radius: 10px; background: #f6f8f3; }
    ${rootSelector} .wren-community-photo-item > .mc-attachments__image-wrapper { order: 1; display: block; width: 100%; max-width: 100%; height: 170px; overflow: hidden; }
    ${rootSelector} .wren-community-photo-item .mc-attachments__image { display: block; width: 100%; height: 100%; max-width: 100%; max-height: 100%; object-fit: contain; }
    ${rootSelector} .wren-community-photo-item > .mc-attachments__remove-button { order: 2; position: static; inset: auto; display: flex; justify-content: flex-end; width: 100%; height: auto; min-height: 44px; visibility: visible !important; opacity: 1 !important; pointer-events: auto; }
    ${rootSelector} .wren-community-photo-item button.mc-attachments__button-icon { box-sizing: border-box; display: inline-flex; align-items: center; justify-content: center; gap: 6px; width: auto; height: auto; min-width: 44px; min-height: 44px; padding: 9px 12px; border: 1px solid #cbd7cf; border-radius: 8px; background: #fff; color: #294e41; font: inherit; font-size: 14px; line-height: 1.4; visibility: visible !important; opacity: 1 !important; pointer-events: auto; }
    ${rootSelector} .wren-community-photo-item button.mc-attachments__button-icon > svg { width: 18px; height: 18px; flex: 0 0 18px; }
  `;
  (document.head || document.documentElement).append(style);

  const labelPicker = label => {
    const input = inputFor(label);
    if (!input) {
      const original = initialized.get(label);
      if (original) {
        label.classList.remove('wren-community-photo-picker');
        label.querySelector('.wren-community-photo-picker-label')?.remove();
        ['role', 'tabindex', 'aria-disabled'].forEach(name => {
          const ours = name === 'role' ? 'button' : name === 'tabindex' ? '0' : original.lastDisabled;
          if (label.getAttribute(name) !== ours) return;
          if (original[name] === null) label.removeAttribute(name); else label.setAttribute(name, original[name]);
        });
        const described = (label.getAttribute('aria-describedby') || '').split(/\s+/).filter(id => id && !id.startsWith('wren-community-photo-help-'));
        if (described.length) label.setAttribute('aria-describedby', described.join(' ')); else label.removeAttribute('aria-describedby');
      }
      return;
    }
    if (!initialized.has(label)) {
      initialized.set(label, Object.fromEntries(['role', 'tabindex', 'aria-disabled'].map(name => [name, label.getAttribute(name)])));
      // Cancel the label's document-wide ID lookup and invoke this form's input.
      label.addEventListener('click', event => {
        if (event.defaultPrevented || !inputFor(label) || (typeof event.button === 'number' && event.button !== 0)) return;
        event.preventDefault();
        choosePhoto(label);
      });
      label.addEventListener('keydown', event => {
        if (event.defaultPrevented || !inputFor(label) || (event.key !== 'Enter' && event.key !== ' ')) return;
        event.preventDefault();
        if (!event.repeat) choosePhoto(label);
      });
    }
    label.classList.add('wren-community-photo-picker');
    label.setAttribute('role', 'button');
    label.tabIndex = 0;
    label.setAttribute('aria-disabled', String(input.matches(':disabled')));
    initialized.get(label).lastDisabled = label.getAttribute('aria-disabled');
    let text = label.querySelector('.wren-community-photo-picker-label');
    if (!text) {
      text = document.createElement('span');
      text.className = 'wren-community-photo-picker-label';
      text.textContent = 'Add photo';
      label.append(text);
    }
    label.querySelectorAll('svg').forEach(icon => icon.setAttribute('aria-hidden', 'true'));
    const actions = label.closest('.mc-input-from__actions');
    if (!actions) return;
    let helper = helpers.get(actions);
    if (!helper?.isConnected) {
      helper = document.createElement('p');
      helper.id = `wren-community-photo-help-${++nextHelper}`;
      helper.className = 'wren-community-photo-help';
      helper.textContent = 'JPG, PNG or WebP. Preview before posting.';
      actions.after(helper);
      helpers.set(actions, helper);
    }
    const described = (label.getAttribute('aria-describedby') || '').split(/\s+/).filter(id => id && !id.startsWith('wren-community-photo-help-'));
    const description = [...described, helper.id].join(' ');
    if (label.getAttribute('aria-describedby') !== description) label.setAttribute('aria-describedby', description);
  };

  const sync = () => {
    const root = document.querySelector(rootSelector);
    if (!root) return;
    root.querySelectorAll(`${formSelector} label.mc-card-form__action-button[for]`).forEach(labelPicker);
    root.querySelectorAll('.wren-community-photo-help').forEach(helper => {
      const actions = helper.previousElementSibling;
      if (!actions?.matches('.mc-input-from__actions') || !Array.from(actions.querySelectorAll('label.mc-card-form__action-button[for]')).some(inputFor)) helper.remove();
    });
    root.querySelectorAll(`${formSelector} .mc-attachments__list-item`).forEach(item => {
      const image = item.querySelector(':scope > .mc-attachments__image-wrapper > img.mc-attachments__image');
      const remove = item.querySelector(':scope > .mc-attachments__remove-button > button.mc-attachments__button-icon');
      const isPhoto = Boolean(image && remove && !item.querySelector('video, iframe, object'));
      item.classList.toggle('wren-community-photo-item', isPhoto);
      if (!isPhoto) {
        item.querySelector('.wren-community-remove-photo-label')?.remove();
        return;
      }
      let text = remove.querySelector('.wren-community-remove-photo-label');
      if (!text) {
        text = document.createElement('span');
        text.className = 'wren-community-remove-photo-label';
        text.textContent = 'Remove photo';
        remove.append(text);
      }
      remove.querySelectorAll('svg').forEach(icon => icon.setAttribute('aria-hidden', 'true'));
    });
  };
  const schedule = () => {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(() => { queued = false; sync(); });
  };
  new MutationObserver(schedule).observe(document.documentElement, {
    childList: true, subtree: true, attributes: true, attributeFilter: ['accept', 'for', 'id', 'disabled']
  });
  window.addEventListener('pageshow', schedule);
  sync();
})();

;(() => {
  const basePath = '/community/my/wrenlife-trying-to-conceive';
  if (window.location.pathname.replace(/\/+$/, "") !== basePath && !window.location.pathname.startsWith(`${basePath}/`)) return;
  const installationKey = Symbol.for('wren.community.topicCompose');
  if (window[installationKey]) return;
  window[installationKey] = true;

  let labels;
  try { labels = JSON.parse(document.getElementById('wren-community-labels')?.textContent || '{}'); } catch { return; }
  if (!labels.compose_start || !labels.compose_hint) return;
  const rootSelector = 'main.architechpro-mega-community-body';
  let panel;
  let queued = false;
  const currentComposer = () => {
    const path = window.location.pathname.replace(/\/+$/, '');
    const topic = path.slice(basePath.length + 1);
    if (!path.startsWith(`${basePath}/`) || !topic || topic.includes('/')) return null;
    const root = document.querySelector(rootSelector);
    if (!root || root.querySelector('.mc-post-show')) return null;
    const area = root.querySelector('.mc-community-show__post-form');
    const form = area?.closest('form.mc-postable-form');
    const input = Array.from(area?.querySelectorAll('textarea') || []).find(item => !item.closest('.mc-input-form__title') && !item.disabled && !item.readOnly);
    return form && input ? { form, input } : null;
  };

  const style = document.createElement('style');
  style.id = 'wren-community-topic-compose-style';
  style.textContent = `
    #wren-community-topic-compose { display: grid; grid-template-columns: minmax(0, 1fr) auto; align-items: center; gap: 16px; margin: 0 0 14px; padding: 18px; border: 1px solid #ccd8cb; border-radius: 12px; background: #f6f8f3; color: #243832; }
    #wren-community-topic-compose, #wren-community-topic-compose * { box-sizing: border-box; }
    #wren-community-topic-compose p { flex: 1 1 260px; max-width: 430px; margin: 0; color: #52635a; font: inherit; font-size: 15px; line-height: 1.5; }
    #wren-community-topic-compose .wren-community-compose-start { display: inline-flex; flex: 0 0 auto; align-items: center; justify-content: center; min-height: 48px; max-width: 100%; padding: 12px 18px; border: 1px solid #294e41; border-radius: 10px; background: #294e41; color: #fff; font: inherit; font-size: 16px; font-weight: 600; line-height: 1.4; text-align: center; cursor: pointer; }
    #wren-community-topic-compose .wren-community-compose-start:hover { background: #203e34; }
    #wren-community-topic-compose button:focus-visible { outline: 3px solid #294e41; outline-offset: 4px; }
    #wren-community-topic-compose .wren-community-compose-privacy { grid-column: 1 / -1; padding-top: 12px; border-top: 1px solid #dce3d8; }
    #wren-community-topic-compose .wren-community-display-name { min-height: 44px; padding: 5px 0; border: 0; background: transparent; color: #294e41; font: inherit; font-size: 14px; font-weight: 600; line-height: 1.4; text-align: left; text-decoration: underline; text-underline-offset: 3px; cursor: pointer; }
    #wren-community-topic-compose .wren-community-compose-privacy p { max-width: none; font-size: 13px; }
    ${rootSelector} .mc-community-show__post-form { scroll-margin-block: 100px; }
    @media (max-width: 749px) {
      #wren-community-topic-compose { grid-template-columns: minmax(0, 1fr); align-items: stretch; gap: 10px; padding: 14px; }
      #wren-community-topic-compose > p { grid-row: 2; max-width: none; font-size: 14px; }
      #wren-community-topic-compose .wren-community-compose-start { grid-row: 1; width: 100%; }
      ${rootSelector} form.mc-postable-form .mc-input-from__actions { flex-wrap: wrap; gap: 10px; }
      ${rootSelector} form.mc-postable-form .mc-input-form__left { flex-wrap: wrap; gap: 6px; }
      ${rootSelector} form.mc-postable-form .wren-community-photo-picker,
      ${rootSelector} form.mc-postable-form .mc-input-form__hyperlink-button,
      ${rootSelector} form.mc-postable-form #video-upload-trigger-button,
      ${rootSelector} form.mc-postable-form .mc-input-form__submit.wren-community-post-button { min-height: 48px; min-width: 48px; }
    }
  `;
  (document.head || document.documentElement).append(style);

  const sync = () => {
    const composer = currentComposer();
    if (!composer) {
      panel?.remove();
      return;
    }
    if (!panel?.isConnected) {
      panel = document.createElement('div');
      panel.id = 'wren-community-topic-compose';
      const hint = document.createElement('p');
      hint.textContent = labels.compose_hint;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'wren-community-compose-start';
      button.textContent = labels.compose_start;
      button.addEventListener('click', () => {
        const current = currentComposer();
        if (!current) return;
        current.input.focus({ preventScroll: true });
        current.input.click();
        current.input.scrollIntoView({ block: 'center', behavior: 'instant' });
      });
      panel.append(hint, button);
    }
    if (composer.form.previousElementSibling !== panel) composer.form.before(panel);
    const profile = document.querySelector(`${rootSelector} button.mc-profile__button`);
    let privacy = panel.querySelector('.wren-community-compose-privacy');
    if (profile && !profile.disabled && labels.profile_name && labels.profile_hint) {
      if (!privacy) {
        privacy = document.createElement('div');
        privacy.className = 'wren-community-compose-privacy';
        const chooseName = document.createElement('button');
        chooseName.type = 'button';
        chooseName.className = 'wren-community-display-name';
        chooseName.textContent = labels.profile_name;
        chooseName.setAttribute('aria-describedby', 'wren-community-display-name-help');
        chooseName.addEventListener('click', () => {
          const nativeProfile = document.querySelector(`${rootSelector} button.mc-profile__button`);
          if (nativeProfile && !nativeProfile.disabled && nativeProfile.getAttribute('aria-disabled') !== 'true') nativeProfile.click();
        });
        const help = document.createElement('p');
        help.id = 'wren-community-display-name-help';
        help.textContent = labels.profile_hint;
        privacy.append(chooseName, help);
        panel.append(privacy);
      }
    } else {
      privacy?.remove();
    }
    document.querySelectorAll(`${rootSelector} form.mc-postable-form #video-upload-trigger-button`).forEach(button => {
      if (!button.hasAttribute('aria-label') && !button.hasAttribute('aria-labelledby') && labels.add_video) button.setAttribute('aria-label', labels.add_video);
    });
    document.querySelectorAll(`${rootSelector} form.mc-postable-form .mc-input-form__hyperlink-button`).forEach(button => {
      if (!button.hasAttribute('aria-label') && !button.hasAttribute('aria-labelledby') && labels.add_link) button.setAttribute('aria-label', labels.add_link);
    });
  };
  const schedule = () => {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(() => { queued = false; sync(); });
  };
  new MutationObserver(schedule).observe(document.documentElement, { childList: true, subtree: true });
  window.addEventListener('pageshow', schedule);
  window.addEventListener('popstate', schedule);
  sync();
})();

;(() => {
  if (!/^\/community\/my(?:\/|$)/.test(window.location.pathname)) return;
  const installationKey = Symbol.for('wren.community.notificationCopy');
  if (window[installationKey]) return;
  window[installationKey] = true;
  let labels;
  try { labels = JSON.parse(document.getElementById('wren-community-labels')?.textContent || '{}'); } catch { return; }
  if (!labels.alerts_title || !labels.alerts_description || !labels.alerts_enable) return;
  let queued = false;
  const sync = () => {
    document.querySelectorAll('main.architechpro-mega-community-body .mc-banner.mc-community-page-banner').forEach(banner => {
      const title = banner.querySelector('.mc-banner__content > .mc-banner__title');
      const originalTitle = title?.textContent.trim();
      if (originalTitle !== 'Enable web push notifications' && originalTitle !== labels.alerts_title) return;
      const description = banner.querySelector('.mc-banner__content > .mc-banner__description');
      const enable = banner.querySelector('button.mc-banner__action.mc-banner__action--button');
      const dismiss = banner.querySelector('button.mc-banner__dismiss');
      if (title.textContent !== labels.alerts_title) title.textContent = labels.alerts_title;
      if (description && description.textContent !== labels.alerts_description) description.textContent = labels.alerts_description;
      if (enable && enable.textContent !== labels.alerts_enable) enable.textContent = labels.alerts_enable;
      if (dismiss && labels.alerts_dismiss && dismiss.getAttribute('aria-label') !== labels.alerts_dismiss) dismiss.setAttribute('aria-label', labels.alerts_dismiss);
    });
  };
  const schedule = () => {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(() => { queued = false; sync(); });
  };
  new MutationObserver(schedule).observe(document.documentElement, { childList: true, subtree: true, characterData: true });
  window.addEventListener('pageshow', schedule);
  sync();
})();

;(() => {
  const basePath = '/community/my/wrenlife-trying-to-conceive/';
  if (!window.location.pathname.startsWith(basePath)) return;
  const installKey = Symbol.for('wren.community.replyReturn');
  if (window[installKey]) return;
  window[installKey] = true;
  let currentKey = '';
  let expanded = false;
  let scrolled = false;
  let queued = false;
  const sync = () => {
    const url = new URL(window.location.href);
    const commentId = url.searchParams.get('commentId');
    if (!url.pathname.startsWith(basePath) || !commentId || !/^[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}$/i.test(commentId)) return;
    const key = `${url.pathname}?commentId=${commentId}`;
    if (key !== currentKey) {
      currentKey = key;
      expanded = false;
      scrolled = false;
    }
    const post = document.querySelector('main.architechpro-mega-community-body .mc-post-show');
    if (!post) return;
    const comments = post.querySelector('.mc-comment');
    if (!comments && !expanded) {
      const button = post.querySelector('.mc-card-post__bottom > button.mc-comment-button');
      if (button && !button.disabled) {
        expanded = true;
        button.click();
      }
      return;
    }
    if (comments && !scrolled && comments.getClientRects().length) {
      scrolled = true;
      comments.scrollIntoView({ block: 'center', behavior: 'instant' });
    }
  };
  const schedule = () => {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(() => { queued = false; sync(); });
  };
  new MutationObserver(schedule).observe(document.documentElement, { childList: true, subtree: true });
  window.addEventListener('pageshow', schedule);
  window.addEventListener('popstate', schedule);
  sync();
})();
